import type {
  KapasiteettiPaiva, KapasiteettiViikko, KapasiteettiRekisteri, Tyosuhde,
  Vaje, Varamiehet,
  KohdePaallekkaisyys, ApiVirhe,
} from "./shared/types";

/**
 * API-asiakas.
 *
 * Istunto kulkee evästeessä, joten `credentials: "same-origin"` on
 * pakollinen jokaisessa kutsussa. Ilman sitä selain jättää evästeen
 * lähettämättä ja jokainen pyyntö palaa 401:llä ilman selitystä.
 */

/** T440 (206): yksi tarkistuspäätös. `ehdotus` = tarkistettu teksti sellaisena kuin se oli. */
export interface KaannosTarkistus {
  id: number; avain: string; kieli: string; ehdotus: string; tulos: "oikein" | "korjattu"; korjaus: string | null;
  kommentti: string | null; tarkistettu: string; hyvaksynta: "odottaa" | "hyvaksytty" | "hylatty" | "ei_tarvita";
  hyvaksytty: string | null; hyvaksynta_peruste: string | null; tarkistaja: string; hyvaksyja: string | null; oma: boolean;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly virhe: string,
    viesti: string,
    readonly kentta?: string,
    /** Vastauksen muut kentät (esim. 404 `linja_ei_nakyvissa` → `kohde`). */
    readonly lisa?: Record<string, unknown>
  ) {
    super(viesti);
  }
}

async function pyynto<T>(polku: string, asetukset: RequestInit = {}): Promise<T> {
  const vastaus = await fetch(polku, {
    credentials: "same-origin",
    headers: asetukset.body ? { "Content-Type": "application/json" } : {},
    ...asetukset,
  });

  /*
   * ⛔ VANHENTUNUT ISTUNTO EI SAA NÄYTTÄÄ TYHJÄLTÄ DATALTA (19.9.2026).
   *
   * Istunto tarkistettiin vain käynnistyksessä. Kun se vanheni kesken käytön,
   * jokainen näkymä sai 401:n omaan catch-lohkoonsa — niitä on 244 — ja jätti
   * listansa tyhjäksi. Ruudulla se näytti siltä että kanta on tyhjentynyt;
   * mitattuna kannassa oli 731 linjaa ja 9 942 hitsiä.
   *
   * Sama vikaperhe kuin kaikki muutkin tänään: «ei oikeutta» ja «ei dataa»
   * näyttivät samalta. Korjaus kuuluu yhteen paikkaan eikä 244:ään.
   */
  if (vastaus.status === 401) {
    window.dispatchEvent(new CustomEvent("sampo:istunto-vanhentui"));
  }
  if (!vastaus.ok) {
    let v: ApiVirhe = { virhe: "verkkovirhe", viesti: "Yhteys palvelimeen ei onnistunut." };
    try { v = await vastaus.json(); } catch { /* vastaus ei ollut JSONia */ }
    throw new ApiError(vastaus.status, v.virhe, v.viesti, v.kentta, v as unknown as Record<string, unknown>);
  }

  return vastaus.status === 204 ? (undefined as T) : vastaus.json();
}

/**
 * Määrittelemättömät pois hakuparametreista.
 *
 * `?kohde_id=undefined` on eri asia kuin parametrin puuttuminen:
 * palvelin hylkäisi sen skeemavirheenä.
 */
const vainAnnetut = (
  o: Record<string, string | number | undefined>
): Record<string, string | number> =>
  Object.fromEntries(
    Object.entries(o).filter((p): p is [string, string | number] => p[1] !== undefined)
  );

const haku = (p: Record<string, string | number>) =>
  new URLSearchParams(Object.entries(p).map(([k, v]) => [k, String(v)])).toString();

/* ── kirjautuminen ──────────────────────────────────────── */

export interface Kayttaja {
  id: number;
  /** Käännöstyö (tarkistus ja korjaus) on päällä tässä instanssissa — vain demo (KAANNOSTYO=1, Toni 10.10.2026). */
  kaannostyo?: boolean;
  /** Toni 15.10.2026: Puuttuvat hitsit vain master-adapterin instanssissa (pekilo). */
  master_adapteri?: boolean;
  /** Linjatarkastus kirjataan SharePointiin (LINJATARKASTUS_ISANTA=sharepoint, jsg-pekilo, Toni 12.10.2026): kuittaus 409, nippu toimii. */
  linjatarkastus_sharepointissa?: boolean;
  /** Tässä instanssissa valittavat kielet (palvelimen KIELET; liettua 11.10.2026). */
  kielet?: string[];
  /** Demotestauksen ohje päällä (api/.env TESTAUS=1, Toni 10.10.2026). */
  testaus?: boolean;
  /** Kenttätili (koodi + PIN). Palvelin lähettää sen nimellä `kenttatili` (auth.ts `k.kenttatunnus AS kenttatili`); vanha nimi
   *  `kenttatunnus` poistettiin tyypistä 18.10.2026, koska palvelin ei lähetä sitä ja jokainen sitä lukenut ehto oli aina epätosi
   *  (T577: Palaute-nappi näkyi kenttätilille). Käyttäjälistan riveillä (KayttajaRivi) kenttä on yhä `kenttatunnus`. */
  kenttatili?: boolean;
  nimi: string;
  /** 205: käyttöliittymän kieli; null = instanssin kieli (T439). */
  kieli?: string | null;
  /** 125: kenttätilillä null — «ei sähköpostia» on tila, ei puute. */
  sahkoposti: string | null;
  roolit: string[];
  /** 106: tunnuksen laji. `ulkopuolinen` = tilaajan tunnus (H1, 19.9.2026):
   *  urakoitsijan henkilöstö-, tunti- ja varastotieto ei kuulu sille. */
  laji: string;
  /** Kohderajaus. Tyhjä = näkee kaiken. */
  kohteet: number[];
  /** Kytketty henkilö (resurssi). null = ei kytketty. */
  resurssi_id: number | null;
  /** Kytketyn henkilön lyhenne = kenttätekijän koodi (hitsauslokissa hitsaajan leima). */
  kenttakoodi: string | null;
  /** Henkilöllä on hitsaustyön työlaji (127) — kenttätekijän hitsausloki nojaa tähän, ei rooliin. */
  hitsaustyo: boolean;
  /** Työlaji avaa laiteasennuksen kenttätekijälle (134). */
  laiteasennustyo: boolean;
  /** 191 (päätös 52): IV-asennuksen työlaji. */
  iv_asennustyo: boolean;
  /** 263 (T636): laippatyön työlaji. */
  laippatyo: boolean;
  /** Oikeudet jotka eivät ole rooleja (migraatio 067). Vain koko yrityksen (163). */
  oikeudet: string[];
  /** 163: kohteeseen rajatut oikeudet — oikeus → kohde_id:t. */
  kohdeOikeudet?: Record<string, number[]>;
  /** 168: kenttätilin PIN on pääkäyttäjän asettama — vaihdettava ennen muuta. */
  pinVaihdettava?: boolean;
  /** Kenttätili ilman hitsaajan työlajia: voi olla hitsin pari, ei hitsaaja (Toni 11.10.2026). */
  vainPari?: boolean;
}


export interface Laippaliitos {
  id: number;
  linja_id: number | null;
  laite_id: number | null;
  tunnus: string;
  kuvaus: string | null;
  koko_dn: number | null;
  paineluokka: string | null;
  tiiviste: string | null;
  vaadittu_nm: number | null;
  toteutunut_nm: number | null;
  kuitattu: string | null;
  paiva: string | null;
  kuittaaja: string | null;
  huomiot: string | null;
  linja_tunnus: string | null;
  laite_tunnus: string | null;
  /** T636: liitoksen työmaa (linjan tai laitteen kohde) — kenttätilin listan ryhmittelyyn. */
  kohde_tunnus?: string | null;
  /** 264 (T636): kiristysten määrä ja viimeisimmän tila ja päivä. */
  kiristyksia?: number;
  viimeisin_tila?: LaippakiristysTila | null;
  viimeisin_paiva?: string | null;
  /** T677 E: viimeisimmän kiristyksen arvo ja kirjaushetken varoitukset liitosrivillä. */
  viimeisin_menetelma?: LaippakiristysMenetelma | null;
  viimeisin_momentti_nm?: number | null;
  viimeisin_paine_bar?: number | null;
  viimeisin_pulttivoima_kn?: number | null;
  viimeisin_varoitukset?: string[];
}

/** 264 (T636): laippakiristys tapahtumana. */
export type LaippakiristysTila = "kirjattu" | "hyvaksytty" | "palautettu" | "tuotu";
export type LaippakiristysMenetelma = "kasi" | "momenttiavain" | "hydraulinen_momenttiavain" | "kiristin";
export interface Laippakiristys {
  id: number; menetelma: LaippakiristysMenetelma; momentti_nm: number | null; paine_bar: number | null; pulttivoima_kn: number | null;
  tiiviste: string | null; pultit_maara: number | null; pultit_koko: string | null; kalusto_id: number | null; tyokalu_teksti: string | null;
  tyokalu_kalibroitu: string | null; kalibrointi_voimassa_asti: string | null; kalibrointi_voimassa: boolean | null; paiva: string;
  kiristaja_id: number | null; kiristaja: string | null; kirjattu: string; varoitukset: string[]; tila: LaippakiristysTila;
  kasitelty: string | null; palautuksen_peruste: string | null; huomiot: string | null; tuotu: boolean;
}
export interface LaippakiristysSyote {
  menetelma: LaippakiristysMenetelma; momentti_nm?: number | null; paine_bar?: number | null; pulttivoima_kn?: number | null;
  tiiviste?: string | null; pultit_maara?: number | null; pultit_koko?: string | null; kalusto_id?: number | null;
  tyokalu_teksti?: string | null; tyokalu_kalibroitu?: string | null; paiva?: string; huomiot?: string | null;
  /** Työnjohto kirjaa toisen puolesta (Toni 25.10.2026 klo 20.00); pois = kirjaaja itse. */
  kiristaja_id?: number | null;
}

/** Ohjevideo (T481); `vaihe` on opetussarjan ryhmä (T507). */
export interface OhjeVideo {
  tunnus: string; versio: string; otsikko: string; kesto: string | null; pysty: boolean; tekstitys: string[];
  tekstitys_poltettu?: boolean; oletusraita?: string | null; merkinta?: string | null; vaihe?: string | null;
}

export const api = {
  kirjaudu: (sahkoposti: string, salasana: string) =>
    pyynto<{ kayttaja: Kayttaja }>("/api/kirjaudu", {
      method: "POST",
      body: JSON.stringify({ sahkoposti, salasana }),
    }),

  kirjauduKoodilla: (koodi: string, pin: string) =>
    pyynto<{ kayttaja: Kayttaja }>("/api/kirjaudu-koodi", {
      method: "POST", body: JSON.stringify({ koodi, pin }),
    }),

  kirjauduUlos: () => pyynto<{ ok: true }>("/api/kirjaudu-ulos", { method: "POST" }),
  /** 168: kenttätilin oma PIN-vaihto. */
  omaPin: (nykyinen: string, uusi: string) =>
    pyynto<{ ok: true; pin_vaihdettava: boolean | null }>("/api/kenttatilit/oma-pin", {
      method: "POST", body: JSON.stringify({ nykyinen, uusi }) }),

  kenttatilitEhdokkaat: () =>
    pyynto<{ id: number; koodi: string; nimi: string }[]>("/api/kenttatilit/ehdokkaat"),
  /** T288 (13.9.2026): tilit luodaan vain valituille henkilöille; työmaa valinnainen, ja jokainen luotu tili saa samat. */
  luoKenttatilit: (henkilo_idt: number[], kohde_idt: number[], kieli: string | null = null) =>
    pyynto<{ luotu: number; ohitetut: string[]; tilit: { koodi: string; nimi: string; pin: string }[];
             kohteita: number; valittu: number; ei_ehdokkaita: number }>(
      "/api/kenttatilit/luo", { method: "POST", body: JSON.stringify({ henkilo_idt, kohde_idt, kieli }) }),
  /** Norjan erä 8.10.2026: työnjohto asettaa usean tunnuksen kielen kerralla (null = organisaation oletus). */
  asetaTunnustenKieli: (kayttaja_idt: number[], kieli: string | null) =>
    pyynto<{ kieli: string | null; valittu: number; muutettu: number }>(
      "/api/kayttajat/kieli", { method: "POST", body: JSON.stringify({ kayttaja_idt, kieli }) }),
  /** Olemassa olevat kenttätilit. Vastinpari `kenttatilitEhdokkaat`ille. */
  /* Tyyppi tulee `Kenttatili`-rajapinnasta eikä inline-muotona: sama
     muoto oli kirjoitettu kahteen paikkaan, ja `paatti`-kentän lisäys
     päivitti vain toisen. Yksi käsite, yksi paikka. */
  kenttatilit: () => pyynto<Kenttatili[]>("/api/kenttatilit"),
  /** Rivi 91: kenttätilin työmaa johdettuna allokaatiosta — oletus, ei kielto. */
  minunTyomaa: () => pyynto<{ kohde_id: number | null; tunnus?: string; nimi?: string; pvm?: string; peruste: string;
                              /** Työmaat joille on allokoitu tai sidottu mutta jotka eivät näy kentällä (ei aktiivisia). */
                              piilossa: { tunnus: string; nimi: string; tila: string }[] }>("/api/minun/tyomaa"),
  /** Uusi PIN olemassa olevalle kenttätilille. Katkaisee istunnot.
   *  PIN palautetaan KERRAN eikä sitä tallenneta mihinkään. */
  /** 217: Academy kenttätilille (pääkäyttäjä tai kohteen työnjohtaja omille kohteilleen). */
  asetaAcademy: (henkiloId: number, kaytossa: boolean) =>
    pyynto<{ ok: true; kaytossa: boolean }>(`/api/kenttatilit/${henkiloId}/academy`, { method: "PUT", body: JSON.stringify({ kaytossa }) }),

  /** Demotestauksen ohje (Toni 10.10.2026): oma rooli, oma kieli; vain TESTAUS=1-instanssissa (muualla 404). */
  testausohje: (kieli: string, rooli?: string) =>
    pyynto<{ rooli: string; oma_rooli: string; roolit?: string[]; kieli: string; otsikko: string | null; nakyy: string[]; ei_nay: string[]; sisalto: string }>(
      `/api/testaus?kieli=${encodeURIComponent(kieli)}${rooli ? `&rooli=${encodeURIComponent(rooli)}` : ""}`),

  /** T487 (Toni 11.10.2026): testaajan palaute, vain demossa (TESTAUS=1). Rivit ovat (tunnus, nimikirjaimet) -kohtaisia. */
  testauspalauteOma: (rooli: string, nimikirjaimet: string) =>
    pyynto<{ tunnuskoodi: string; rivit: { laji: string; tehtava_nro: number | null; tulos: string | null; kysymys: string | null;
                                           arvo: number | null; teksti: string | null; muokattu: string }[] }>(
      `/api/testaus/palaute?${new URLSearchParams({ rooli, ...(nimikirjaimet ? { nimikirjaimet } : {}) }).toString()}`),
  tallennaTehtava: (syote: { rooli: string; nro: number; tehtava: string; kieli: string; nimikirjaimet: string;
                             tulos?: string | null; kommentti?: string | null }) =>
    pyynto<{ tallennettu: string }>("/api/testaus/palaute/tehtava", { method: "PUT", body: JSON.stringify(syote) }),
  tallennaLoppu: (syote: { rooli: string; kysymys: string; kieli: string; nimikirjaimet: string; teksti?: string | null; arvo?: number | null }) =>
    pyynto<{ tallennettu: string }>("/api/testaus/palaute/loppu", { method: "PUT", body: JSON.stringify(syote) }),
  lahetaSivupalaute: (syote: { tyyppi: string; teksti: string; osoite: string; laite: string; kieli: string; nimikirjaimet: string }) =>
    pyynto<{ id: number }>("/api/testaus/palaute/sivu", { method: "POST", body: JSON.stringify(syote) }),
  /** T487b: «Uutta testattavaa» 14 päivän ajalta lukijan kielellä; katsottu (tunnus + nimikirjaimet). */
  testattavaaUutta: (nimikirjaimet: string) =>
    pyynto<{ rivit: { avain: string; lahetetty: string; linkki: string; rooli: string | null; teksti: string; katsottu: boolean }[] }>(
      "/api/testaus/uutta" + (nimikirjaimet ? `?nimikirjaimet=${encodeURIComponent(nimikirjaimet)}` : "")),
  merkitseKatsotuksi: (avain: string, kieli: string, nimikirjaimet: string) =>
    pyynto<{ katsottu: true }>("/api/testaus/uutta/katsottu", { method: "PUT", body: JSON.stringify({ avain, kieli, nimikirjaimet }) }),
  testausIlmoitukset: () =>
    pyynto<{ id: number; sha_alku: string; sha_loppu: string; tila: "luonnos" | "lahetetty" | "peruttu"; luotu: string;
             lahetetty: string | null; peruttu: string | null; peruja: string | null; riveja: number;
             rivit: { avain: string; fi: string; en: string; linkki: string; rooli: string | null }[] }[]>("/api/testaus/ilmoitukset"),
  peruTestausIlmoitus: (id: number) =>
    pyynto<{ peruttu: true }>(`/api/testaus/ilmoitukset/${id}/peruutus`, { method: "POST" }),
  testauspalautteet: (suodatin: { rooli?: string; laji?: string; tyyppi?: string }) =>
    pyynto<TestauspalauteRivi[]>("/api/testaus/palautteet?" + new URLSearchParams(
      Object.entries(suodatin).filter(([, v]) => v) as [string, string][]).toString()),

  /** T481 (Toni 11.10.2026): pysyvä roolikohtainen ohje kaikissa instansseissa. Pääkäyttäjä voi valita roolin. */
  ohjeet: (kieli: string, rooli?: string) =>
    pyynto<{ rooli: string; oma_rooli: string; roolit?: string[]; kirjoitetut?: string[]; testaus: boolean; kieli: string; vanhentuneet?: string[];
             otsikko: string | null; nakyy: string[]; ei_nay: string[];
             videot: OhjeVideo[]; sarja?: OhjeVideo[]; sisalto: string | null }>(
      `/api/ohjeet?kieli=${encodeURIComponent(kieli)}${rooli ? `&rooli=${encodeURIComponent(rooli)}` : ""}`),

  uusiPin: (henkiloId: number) =>
    pyynto<{ koodi: string; nimi: string; pin: string }>(
      `/api/kenttatilit/${henkiloId}/pin`, { method: "POST" }),

  mina: () => pyynto<{ kayttaja: Kayttaja }>("/api/mina"),
  /** T440: käännösten tarkistukset yhdelle kielelle (tarkistaja: vain oma kieli). */
  kaannosTarkistukset: (kieli?: string) =>
    pyynto<{ kieli: string; saa_hyvaksya: boolean; rivit: KaannosTarkistus[] }>(`/api/kaannokset/tarkistukset${kieli ? `?kieli=${kieli}` : ""}`),
  tarkistaKaannos: (r: { avain: string; kieli: string; ehdotus: string; tulos: "oikein" | "korjattu"; korjaus?: string | null; kommentti?: string | null }) =>
    pyynto<{ id: number; hyvaksynta: string }>("/api/kaannokset/tarkistukset", { method: "POST", body: JSON.stringify(r) }),
  hyvaksyKaannos: (id: number, hyvaksy: boolean, peruste?: string | null) =>
    pyynto<{ id: number; hyvaksynta: string }>(`/api/kaannokset/tarkistukset/${id}/hyvaksynta`,
      { method: "POST", body: JSON.stringify({ hyvaksy, peruste: peruste ?? null }) }),
  /** T439: oma käyttöliittymän kieli; null = instanssin kieli. */
  asetaKieli: (kieli: string | null) =>
    pyynto<{ kieli: string | null }>("/api/mina/kieli", { method: "PUT", body: JSON.stringify({ kieli }) }),

  vaihdaSalasana: (nykyinen: string, uusi: string) =>
    pyynto<{ ok: true }>("/api/salasana", {
      method: "POST", body: JSON.stringify({ nykyinen, uusi }),
    }),

  /* ── moduulit ─────────────────────────────────────────── */

  moduulit: () => pyynto<Moduuli[]>("/api/moduulit"),

  vaihdaModuuli: (tunnus: string, kaytossa: boolean) =>
    pyynto<{ ok: true }>(`/api/moduulit/${tunnus}`, {
      method: "PATCH", body: JSON.stringify({ kaytossa }),
    }),

  /* ── liitteet ─────────────────────────────────────────── */

  liitteet: (kohde_taulu: string, kohde_id: number) =>
    pyynto<Liite[]>(`/api/liitteet?${haku({ kohde_taulu, kohde_id })}`),

  /** sisalto on base64. data:-etuliite sallitaan ja poistetaan palvelimella. */
  /** 233 (T562): NDT/VT-pöytäkirja numerona kohteen sisällä (hitsi antaa kohteen). */
  haePoytakirja: (hitsi_id: number, numero: string) =>
    pyynto<{ id: number | null; numero: string; liitteita: number }>(`/api/poytakirjat?hitsi_id=${hitsi_id}&numero=${encodeURIComponent(numero)}`),
  luoPoytakirja: (hitsi_id: number, numero: string) =>
    pyynto<{ id: number; numero: string; liitteita: number }>("/api/poytakirjat", { method: "POST", body: JSON.stringify({ hitsi_id, numero }) }),
  merkitseLiite: (id: number, arkaluonteinen: boolean) =>
    pyynto<{ id: number; arkaluonteinen: boolean; muuttui: boolean }>(`/api/liitteet/${id}/arkaluonteinen`,
      { method: "PATCH", body: JSON.stringify({ arkaluonteinen }) }),
  lisaaLiite: (syote: {
    kohde_taulu: string; kohde_id: number; nimi: string; sisalto: string;
    kuvaus?: string; arkaluonteinen?: boolean; laji?: "allekirjoitettu_poytakirja" | null;
  }) => pyynto<Liite>("/api/liitteet", { method: "POST", body: JSON.stringify(syote) }),

  poistaLiite: (id: number) =>
    pyynto<{ poistettu: true }>(`/api/liitteet/${id}`, { method: "DELETE" }),

  liiteOsoite: (id: number) => `/api/liitteet/${id}/tiedosto`,

  /* ── ainestodistukset (EN 10204 3.1) → sulatus ── */
  ainestodistukset: (kohde?: number) =>
    pyynto<Ainestodistus[]>(`/api/ainestodistukset?${haku(kohde !== undefined ? { kohde } : {})}`),
  ainestodistusKattavuus: (kohde?: number) =>
    pyynto<{ sulatus: string; todistus: boolean }[]>(`/api/ainestodistukset/kattavuus?${haku(kohde !== undefined ? { kohde } : {})}`),
  luoAinestodistus: (t: { kohde_id?: number | null; toimittaja?: string | null; materiaali?: string | null; huomiot?: string | null;
                          liite?: { nimi: string; sisalto: string } }) =>
    pyynto<{ id: number }>("/api/ainestodistukset", { method: "POST", body: JSON.stringify(t) }),
  ocrAinestodistus: (id: number) =>
    pyynto<{ ehdotukset: string[]; merkkeja: number }>(`/api/ainestodistukset/${id}/ocr`, { method: "POST" }),
  asetaAinestodistusSulatukset: (id: number, sulatukset: string[]) =>
    pyynto<{ ok: boolean; maara: number }>(`/api/ainestodistukset/${id}/sulatukset`, { method: "PUT", body: JSON.stringify({ sulatukset }) }),
  poistaAinestodistus: (id: number) =>
    pyynto<{ poistettu: number }>(`/api/ainestodistukset/${id}`, { method: "DELETE" }),

  /* ── käyttäjät ────────────────────────────────────────── */

  roolit: () => pyynto<Rooli[]>("/api/roolit"),

  /* ── osaamisradat ─────────────────────────────────────── */
  osaamisradat: () => pyynto<OsaamisRata[]>("/api/osaaminen/radat"),
  osaamiskortti: (henkilo_id: number) =>
    pyynto<{ henkilo_id: number; tasot: OsaamisTaso[]; nimitykset: OsaamisNimitys[] }>(
      `/api/osaaminen/kortti/${henkilo_id}`),
  osaamiskortit: () => pyynto<(OsaamisTaso & { nimi: string; lyhenne: string | null })[]>(
    "/api/osaaminen/kortit"),
  osaamissuoritukset: (henkilo_id?: number) =>
    pyynto<OsaamisSuoritus[]>("/api/osaaminen/suoritukset"
      + (henkilo_id ? `?henkilo_id=${henkilo_id}` : "")),
  kirjaaVaihe: (b: {
    vaihe_id: number; henkilo_id?: number | null; yksikko?: string | null;
    paiva?: string; vastuuhenkilona?: boolean; hyvitys?: boolean; peruste?: string | null;
  }) => pyynto<{ id: number }>("/api/osaaminen/suoritukset",
    { method: "POST", body: JSON.stringify(b) }),
  nimitaRadalle: (b: { henkilo_id: number; rata_id: number; peruste?: string | null }) =>
    pyynto<{ id: number }>("/api/osaaminen/nimitykset",
      { method: "POST", body: JSON.stringify(b) }),
  /* ── Academy ──────────────────────────────────────────── */
  academyOmat: (henkilo_id?: number) =>
    pyynto<AcademyOmaKurssi[]>("/api/academy/omat"
      + (henkilo_id ? `?henkilo_id=${henkilo_id}` : "")),
  academyKurssit: () => pyynto<AcademyKurssi[]>("/api/academy/kurssit"),
  academyKurssi: (id: number) =>
    pyynto<{ kurssi: AcademyKurssi; osiot: AcademyOsio[] }>(`/api/academy/kurssit/${id}`),
  academyKoe: (id: number) =>
    pyynto<{ kurssi: string; lapaisyraja: number; kysymykset: KoeKysymys[] }>(
      `/api/academy/kurssit/${id}/koe`),
  academyPalautaKoe: (id: number, vastaukset: { kysymys_id: number; vaihtoehdot: number[] }[]) =>
    pyynto<KoeTulos>(`/api/academy/kurssit/${id}/koe`,
      { method: "POST", body: JSON.stringify({ vastaukset }) }),
  academyMallisisalto: () =>
    pyynto<{ kursseja: number; osioita: number; kysymyksia: number }>(
      "/api/academy/mallisisalto", { method: "POST" }),
  academyJulkaise: (id: number, julkaistu: boolean) =>
    pyynto<{ id: number | null }>(`/api/academy/kurssit/${id}`,
      { method: "PATCH", body: JSON.stringify({ julkaistu }) }),
  academyTeoria: (henkilo_id: number) =>
    pyynto<TeoriaTila[]>(`/api/academy/teoria/${henkilo_id}`),

  osaamisEhdot: (henkilo_id: number) =>
    pyynto<SertiEhdot[]>(`/api/osaaminen/ehdot/${henkilo_id}`),
  osaamisarvioinnit: (henkilo_id: number) =>
    pyynto<Arviointi[]>(`/api/osaaminen/arvioinnit/${henkilo_id}`),
  kirjaaArviointi: (b: {
    henkilo_id: number; rata_id: number; hyvaksytty: boolean;
    kohde?: string | null; huomiot?: string | null;
  }) => pyynto<{ id: number }>("/api/osaaminen/arvioinnit",
    { method: "POST", body: JSON.stringify(b) }),
  osaamissertifikaatit: (henkilo_id?: number) =>
    pyynto<Sertifikaatti[]>("/api/osaaminen/sertifikaatit"
      + (henkilo_id ? `?henkilo_id=${henkilo_id}` : "")),
  myonnaSertifikaatti: (henkilo_id: number, rata_id: number) =>
    pyynto<{ id: number; numero: string; voimassa_asti: string | null }>(
      "/api/osaaminen/sertifikaatit",
      { method: "POST", body: JSON.stringify({ henkilo_id, rata_id }) }),
  osaamissertifikaatti: (id: number) =>
    pyynto<SertifikaattiTiedot>(`/api/osaaminen/sertifikaatit/${id}`),
  peruSertifikaatti: (id: number, syy?: string) =>
    pyynto<{ id: number | null }>(`/api/osaaminen/sertifikaatit/${id}/peru`,
      { method: "POST", body: JSON.stringify({ syy: syy ?? null }) }),

  luoOsaamismalli: () =>
    pyynto<{ radat: number; vaiheet: number; tasot: number }>(
      "/api/osaaminen/mallipohja", { method: "POST" }),
  luoRata: (b: { tunnus: string; nimi: string; kuvaus?: string | null }) =>
    pyynto<{ id: number }>("/api/osaaminen/radat",
      { method: "POST", body: JSON.stringify(b) }),
  muokkaaRataa: (id: number, b: Record<string, unknown>) =>
    pyynto<{ id: number | null }>(`/api/osaaminen/radat/${id}`,
      { method: "PATCH", body: JSON.stringify(b) }),
  poistaRata: (id: number) =>
    pyynto<{ poistettu: true }>(`/api/osaaminen/radat/${id}`, { method: "DELETE" }),
  luoVaihe: (b: { rata_id: number; tunnus: string; nimi: string; jarjestys?: number }) =>
    pyynto<{ id: number }>("/api/osaaminen/vaiheet",
      { method: "POST", body: JSON.stringify(b) }),
  muokkaaVaihetta: (id: number, b: Record<string, unknown>) =>
    pyynto<{ id: number | null }>(`/api/osaaminen/vaiheet/${id}`,
      { method: "PATCH", body: JSON.stringify(b) }),
  poistaVaihe: (id: number) =>
    pyynto<{ poistettu: true }>(`/api/osaaminen/vaiheet/${id}`, { method: "DELETE" }),
  muokkaaTasoa: (id: number, b: Record<string, unknown>) =>
    pyynto<{ id: number | null }>(`/api/osaaminen/tasot/${id}`,
      { method: "PATCH", body: JSON.stringify(b) }),
  poistaTaso: (id: number) =>
    pyynto<{ poistettu: true }>(`/api/osaaminen/tasot/${id}`, { method: "DELETE" }),

  peruNimitys: (id: number, syy?: string) =>
    pyynto<{ id: number | null }>(`/api/osaaminen/nimitykset/${id}/peru`,
      { method: "POST", body: JSON.stringify({ syy: syy ?? null }) }),

  /** 163: kohde_id tyhjä = koko yritys; kohde vain kohderajattavalle oikeudelle. */
  myonnaOikeus: (kayttaja_id: number, oikeus: string, peruste?: string, kohde_id?: number | null) =>
    pyynto<{ ok: true }>("/api/oikeudet", {
      method: "POST", body: JSON.stringify({ kayttaja_id, oikeus, peruste: peruste ?? null, kohde_id: kohde_id ?? null }),
    }),
  poistaOikeus: (kayttaja_id: number, oikeus: string, kohde_id?: number | null) =>
    pyynto<{ ok: true }>("/api/oikeudet", {
      method: "DELETE", body: JSON.stringify({ kayttaja_id, oikeus, kohde_id: kohde_id ?? null }),
    }),
  /* ── T530 oikeuspaketit (232) ── */
  oikeuspaketit: () => pyynto<OikeuspakettiLuettelo>("/api/oikeuspaketit"),
  oikeustila: (kayttaja_id: number) => pyynto<Oikeustila>(`/api/oikeudet/tunnus/${kayttaja_id}`),
  /** kohde_idt null = koko yritys. lisaa_rooli: paketin vaatima rooli lisätään samalla (näkyvä valinta, ei hiljaa). */
  myonnaPaketti: (b: { kayttaja_id: number; paketti: string; kohde_idt: number[] | null; lisaa_rooli?: boolean; koko_yritys_ok?: boolean; peruste?: string | null }) =>
    pyynto<{ ok: true; id: number; lisatty: number; poistettu: number; rooli_lisatty: string | null }>("/api/oikeuspaketit/myonna",
      { method: "POST", body: JSON.stringify(b) }),
  puraPaketti: (id: number) =>
    pyynto<{ ok: true; poistettu: number; jai: { oikeus: string; kohde_id: number | null; lahteet: string[] }[] }>(
      `/api/oikeuspaketit/${id}/poisto`, { method: "POST", body: "{}" }),
  joukkoPaketti: (b: { kayttaja_idt: number[]; paketti: string; kohde_idt: number[] | null; lisaa_rooli?: boolean; koko_yritys_ok?: boolean }) =>
    pyynto<{ valittu: number; myonnetty: number; hylatty: { kayttaja_id: number; virhe?: string; viesti?: string }[] }>(
      "/api/oikeuspaketit/joukko", { method: "POST", body: JSON.stringify(b) }),
  /** Yksittäisen oikeuden laajuus: null = koko yritys, [id…] = valitut kohteet, [] = pois. Koskee vain yksittäin-lähdettä. */
  oikeudenLaajuus: (kayttaja_id: number, oikeus: string, kohde_idt: number[] | null, peruste?: string | null) =>
    pyynto<{ ok: true; lisatty: number; poistettu: number; jai_paketista: string[] }>("/api/oikeudet/laajuus",
      { method: "POST", body: JSON.stringify({ kayttaja_id, oikeus, kohde_idt, peruste: peruste ?? null }) }),
  kopioiOikeudet: (lahde_id: number, kayttaja_id: number, esikatselu: boolean) =>
    pyynto<{ ok?: true; paketit?: { paketti: string; kohde_idt: number[] | null }[];
             yksittaiset?: { oikeus: string; kohde_idt: number[] | null }[]; ei_kopioida?: string[];
             tulos?: { mita: string; ok: boolean; viesti?: string }[] }>("/api/oikeudet/kopio",
      { method: "POST", body: JSON.stringify({ lahde_id, kayttaja_id, esikatselu }) }),

  /** Myönnettävät oikeudet (067, 152, 162, 163). */
  oikeusluettelo: () =>
    pyynto<{ tunnus: string; nimi: string; kuvaus: string | null; kohderajattava: boolean;
             moduuli?: string | null; moduuli_kaytossa?: boolean }[]>("/api/oikeudet"),

  kayttajat: () => pyynto<KayttajaRivi[]>("/api/kayttajat"),

  /** 106: käyttäjätili IHMISELLE — henkilö on polussa, ei rungossa. Palauttaa kutsulinkin. */
  luoKayttajatili: (henkilo_id: number, syote: { sahkoposti: string; roolit: string[] }) =>
    pyynto<{ id: number; kutsu: string }>(`/api/henkilot/${henkilo_id}/kayttaja`,
      { method: "POST", body: JSON.stringify(syote) }),
  /** 106: kenttätunnus ihmiselle. PIN palautetaan kerran. Työmaa pakollinen (päätös 13.9.2026). */
  luoKenttatunnus: (henkilo_id: number, kohde_idt: number[], kieli: string | null = null) =>
    pyynto<{ id: number; koodi: string; nimi: string; pin: string; kohteita: number }>(`/api/henkilot/${henkilo_id}/kenttatunnus`,
      { method: "POST", body: JSON.stringify({ kohde_idt, kieli }) }),
  /** 106: ulkopuolinen (tilaaja tms.), aina lukija. Palauttaa kutsulinkin. T644: kohde pakollinen. */
  luoUlkopuolinen: (syote: { sahkoposti: string; nimi: string; kohde_idt: number[] }) =>
    pyynto<{ id: number; kutsu: string }>(`/api/kayttajat/ulkopuolinen`,
      { method: "POST", body: JSON.stringify(syote) }),

  /** Uusi kutsulinkki. Mitätöi aiemmat avoimet linkit. */
  luoKutsu: (id: number, laheta = false) =>
    pyynto<Kutsutulos>(`/api/kayttajat/${id}/kutsu`, { method: "POST", body: JSON.stringify({ laheta }) }),
  /** B2: kutsu henkilökortilta — tunnus ratkaistaan palvelimella. */
  luoKutsuHenkilolle: (henkiloId: number, laheta = false) =>
    pyynto<Kutsutulos>(`/api/henkilot/${henkiloId}/kutsu`, { method: "POST", body: JSON.stringify({ laheta }) }),

  /* ── kutsun lunastus (ei vaadi kirjautumista) ─────────── */

  kutsunTiedot: (token: string) =>
    pyynto<{ nimi: string; sahkoposti: string; olemassa: boolean }>(`/api/kutsu/${encodeURIComponent(token)}`),

  asetaSalasanaKutsulla: (token: string, salasana: string) =>
    pyynto<{ ok: true; olemassa: boolean; ilmoitus: { lahetetty: boolean; syy: string | null } | null }>(`/api/kutsu/${encodeURIComponent(token)}`, {
      method: "POST", body: JSON.stringify({ salasana }),
    }),

  /** 122: avoimen kutsun peruutus — peruste pakollinen, kirjautuu (kuka, milloin, miksi). */
  peruKutsu: (id: number, peruste: string) =>
    pyynto<{ perutut: number }>(`/api/kayttajat/${id}/kutsu/peruutus`, {
      method: "POST", body: JSON.stringify({ peruste }),
    }),

  muokkaaKayttajaa: (id: number, muutokset: {
    nimi?: string; sahkoposti?: string; aktiivinen?: boolean; roolit?: string[]; henkilo_id?: number | null;
  }) => pyynto<{ ok: true }>(`/api/kayttajat/${id}`, {
    method: "PATCH", body: JSON.stringify(muutokset),
  }),

  nollaaSalasana: (id: number) =>
    pyynto<{ salasana: string }>(`/api/kayttajat/${id}/salasana`, { method: "POST" }),

  /** Tyhjä lista poistaa rajauksen: käyttäjä näkee taas kaiken.
   *  Paitsi kenttätilillä: sille tyhjä on kielto, ja palvelin vastaa 400 (päätös 13.9.2026). */
  asetaKayttajanKohteet: (id: number, kohde_idt: number[]) =>
    pyynto<{ kohteita: number }>(`/api/kayttajat/${id}/kohteet`, {
      method: "PUT", body: JSON.stringify({ kohde_idt }),
    }),

  /** Poisto, ei passivointi. Lokirivit säilyvät ilman tekijää. */
  poistaKayttaja: (id: number) =>
    pyynto<{ poistettu: true }>(`/api/kayttajat/${id}`, { method: "DELETE" }),

  /* ── rekisterit ───────────────────────────────────────── */

  /* ── viestit (162) ── */
  viestit: () => pyynto<SaapunutViesti[]>("/api/viestit"),
  viestejaLukematta: () => pyynto<{ lukematta: number }>("/api/viestit/lukematta"),
  merkitseLuetuksi: (id: number) =>
    pyynto<{ luettu: string | null; jo_luettu: boolean }>(`/api/viestit/${id}/luettu`, { method: "POST" }),
  lahetetytViestit: () => pyynto<LahetettyViesti[]>("/api/viestit/lahetetyt"),
  viestinKayttajat: () => pyynto<{ id: number; nimi: string; laji: string; roolit: string | null }[]>("/api/viestit/kayttajat"),
  kohteenTyonjohtajat: (kohdeId: number) =>
    pyynto<{ id: number; nimi: string }[]>(`/api/viestit/tyonjohtajat?kohde_id=${kohdeId}`),
  lahetaViesti: (syote: { otsikko: string; teksti: string;
      kohdistus: "kaikki" | "kayttajat" | "kohteen_tyonjohto";
      kayttaja_idt?: number[]; kaikki_tyonjohtajat?: boolean; kohde_id?: number; voimassa_asti?: string }) =>
    pyynto<{ id: number; vastaanottajia: number }>("/api/viestit", { method: "POST", body: JSON.stringify(syote) }),
  peruViesti: (id: number, peruste: string) =>
    pyynto<{ peruttu: true }>(`/api/viestit/${id}/peruutus`, { method: "POST", body: JSON.stringify({ peruste }) }),

  /** «Unohtuiko salasana» (10.10.2026): vastaus on aina sama, oli osoite olemassa tai ei. */
  unohtunutSalasana: (sahkoposti: string, kieli: "fi" | "en") =>
    pyynto<{ ok: boolean; viesti: string }>("/api/salasana/unohtunut", { method: "POST", body: JSON.stringify({ sahkoposti, kieli }) }),
  kohteet: () =>
    pyynto<Kohde[]>("/api/kohteet"),
  /** 278 (Toni 3.11.2026): pääkäyttäjä arkistoi (true) tai palauttaa (false) kohteen. */
  arkistoiKohde: (id: number, arkistoitu: boolean) =>
    pyynto<{ id: number; arkistoitu: string | null }>(`/api/kohteet/${id}/arkistointi`, { method: "POST", body: JSON.stringify({ arkistoitu }) }),
  /** T426: yläpalkin kohdevalinta (204). valittu null = Kaikki kohteet; lukittu = kenttätili jolla yksi työmaa. */
  kohdevalinta: () =>
    pyynto<{ valittu: number | null; lukittu: boolean; kohteet: { id: number; tunnus: string; nimi: string; tila: string }[] }>("/api/kohdevalinta"),
  asetaKohdevalinta: (kohde_id: number | null) =>
    pyynto<{ valittu: number | null }>("/api/kohdevalinta", { method: "PUT", body: JSON.stringify({ kohde_id }) }),

  hub: (kohdeId: number) => pyynto<ProjektiHub>(`/api/hub/${kohdeId}`),
  lahdeaukot: (kohde?: number) => pyynto<LahdeAukot>(`/api/lahdeaukot?${haku(kohde !== undefined ? { kohde } : {})}`),
  lahdeaukotVientiOsoite: (kohde?: number, kieli?: string) =>
    `/api/lahdeaukot/vienti?${haku({ ...(kohde !== undefined ? { kohde } : {}), ...(kieli ? { kieli } : {}) })}`,
  dokumentointivelka: (kohde?: number) => pyynto<Dokumentointivelka>(`/api/dokumentointivelka?${haku(kohde !== undefined ? { kohde } : {})}`),
  ennuste: (kohdeId: number) => pyynto<Ennuste>(`/api/ennuste/${kohdeId}`),
  /** Näkymän asettelu (110). Esitystä, ei valtuutusta: ei hae dataa. */
  nakyma: (nakyma: string) => pyynto<Nakyma>(`/api/nakyma/${nakyma}`),
  tallennaNakyma: (nakyma: string, a: Asettelu) =>
    pyynto<{ ok: true; taso: string }>(`/api/nakyma/${nakyma}`, { method: "PUT", body: JSON.stringify(a) }),
  palautaNakyma: (nakyma: string) =>
    pyynto<{ ok: true; taso: string }>(`/api/nakyma/${nakyma}`, { method: "DELETE" }),
  tallennaRoolinNakyma: (nakyma: string, rooli: string, a: Asettelu) =>
    pyynto<{ ok: true; taso: string }>(`/api/nakyma/${nakyma}/rooli/${encodeURIComponent(rooli)}`, { method: "PUT", body: JSON.stringify(a) }),
  palautaRoolinNakyma: (nakyma: string, rooli: string) =>
    pyynto<{ ok: true; taso: string }>(`/api/nakyma/${nakyma}/rooli/${encodeURIComponent(rooli)}`, { method: "DELETE" }),

  /* ── puutteet: mikä on pielessä ──
     Oma moduulinsa, ei hitsauslokin sisällä. Punchit ja lähdeaukot
     ovat hitsauslokissa; tämä on yleinen havainto vastuineen. */
  puutteet: (suodattimet: {
    kohde_id?: number; tila?: PuutteenTila | "avoimet" | "kaikki";
    tyyppi?: PuutteenTyyppi; vaikutus?: PuutteenVaikutus; linja_id?: number; vastuu?: VastuuSuodatin;
    hoitaja?: HoitajaSuodatin; saanto?: string;
  } = {}) => pyynto<Puute[]>(`/api/puutteet?${haku(suodattimet as Record<string, string | number>)}`),

  /** Havaintosääntöjen kattavuus kohdejoukostaan. 100 %:n sääntö on
   *  kattavuusluku eikä havainto, ja se jätetään jonosta pois. */
  puutteidenKattavuus: () => pyynto<{
    tyyppi: string; saanto: string; kohde_taulu: string;
    puutteita: number; kohteita: number;
    populaatio: number | null; kattavuus: number | null;
  }[]>("/api/puutteet/kattavuus"),
  luoPuute: (syote: {
    tyyppi: PuutteenTyyppi; kohde_taulu: string; kohde_id: number; kuvaus: string;
    vastuu?: string | null; vastuu_ulkoinen?: boolean;
    vaikutus?: PuutteenVaikutus; havaittu?: string; kirjaustunniste?: string;
  }) => pyynto<{ id: number; toisto?: boolean }>("/api/puutteet", { method: "POST", body: JSON.stringify(syote) }),
  /** 261 (T655): korvaava kuittaus — laji, perustelu ja liite (data-URL) samassa kutsussa. */
  /** T653: «missä / mikä vika» -luettelot kohteelle käytössä olevista moduuleista (koodit; näyttönimi avaimella `luok.<koodi>`). */
  luokitus: (kohde: string) =>
    pyynto<{ missa: string[]; vika: string[]; ensin?: Record<string, string[]>; muu: string; muu_lisatieto_min: number }>(`/api/luokitus?kohde=${encodeURIComponent(kohde)}`),
  korvaaPuute: (id: number, syote: { aineistolaji: Aineistolaji; peruste: string; liite: { nimi: string; sisalto: string } }) =>
    pyynto<{ ok: true; tila: "korvattu" }>(`/api/puutteet/${id}/korvaus`, { method: "POST", body: JSON.stringify(syote) }),
  paivitaPuute: (id: number, muutokset: {
    tila?: PuutteenTila; ratkaisu?: string | null; vastuu?: string | null;
    vastuu_ulkoinen?: boolean; vaikutus?: PuutteenVaikutus;
  }) => pyynto<{ ok: true }>(`/api/puutteet/${id}`, { method: "PATCH", body: JSON.stringify(muutokset) }),
  /** 236 (T534): «Otan hoitaakseni» (ilman hoitaja_id:tä) tai osoitus toiselle (työnjohtaja, pääkäyttäjä). */
  otaHoitoon: (taulu: "puute" | "punch", id: number, hoitaja_id?: number) =>
    pyynto<{ ok: true; jo?: boolean }>(`/api/${taulu === "punch" ? "punchit" : "puutteet"}/${id}/hoito`, {
      method: "POST", body: JSON.stringify(hoitaja_id ? { hoitaja_id } : {}) }),
  /** Vapautus takaisin yhteiseen jonoon; toisen hoitaman vapautus vaatii perusteen. */
  vapautaHoidosta: (taulu: "puute" | "punch", id: number, peruste?: string) =>
    pyynto<{ ok: true }>(`/api/${taulu === "punch" ? "punchit" : "puutteet"}/${id}/hoito/vapautus`, {
      method: "POST", body: JSON.stringify(peruste ? { peruste } : {}) }),
  hoidonEhdokkaat: (taulu: "puute" | "punch", id: number) =>
    pyynto<{ id: number; nimi: string }[]>(`/api/${taulu === "punch" ? "punchit" : "puutteet"}/${id}/hoito/ehdokkaat`),
  /** 239 (T566): hitsin tarrahistoria. */
  hitsinTarrat: (id: number) => pyynto<{ id: number; tarra: string; tila: "voimassa" | "korvattu"; syy: string | null; lahde: string | null;
    kirjaaja: string | null; hetki: string; korvattu_hetki: string | null }[]>(`/api/hitsit/${id}/tarrat`),
  /** T332: nimiehdotus rekisteriin (113). Ei kirjoita nimeä lähteen riville; puute sulkeutuu samassa kutsussa. */
  vahvistaNimiehdotus: (asu: string, henkilo_lahde_id: number) =>
    pyynto<{ kirjoitusasu_id: number; ratkesi: number; puutteita_suljettu: number }>("/api/henkilorekisteri/vahvista", {
      method: "POST", body: JSON.stringify({ asu, henkilo_lahde_id, lahde: "kayttoliittyma:puute" }) }),

  /** Hitsaajalista Excelinä (koodi + nimi + osaamiset + pätevyys). Palauttaa osoitteen. */
  hitsaajalistaVienti: () => "/api/hitsaajat/vienti",

  minun: () => pyynto<OmatTiedot>("/api/minun"),
  /** T534 jatko: omat avoimet tehtävät (puutteet ja punchit joita hoidan); lohko puuttuu kun moduuli ei ole käytössä. */
  omatTehtavat: () => pyynto<OmatTehtavat>("/api/minun/tehtavat"),
  omiaTehtavia: () => pyynto<{ n: number }>("/api/minun/tehtavat/maara"),
  linjaMateriaalit: (id: number) => pyynto<LinjaMateriaalit>(`/api/linjat/${id}/materiaalit`),
  linjatarkastus: (linjaId: number) =>
    pyynto<Linjatarkastus | null>(`/api/linjat/${linjaId}/linjatarkastus`),
  tallennaLinjatarkastus: (linjaId: number, data: {
      pvm?: string | null; tarkastaja?: string | null; huomiot?: string | null;
      kohdat?: Record<string, { tulos: string; huomio?: string }>; tila?: "kesken" | "hyvaksytty";
      // 177: hyväksyntä = luovutus asiakkaalle
      asiakkaan_edustaja?: string | null; luovutus_pvm?: string | null; a_poikkeama_peruste?: string | null }) =>
    pyynto(`/api/linjat/${linjaId}/linjatarkastus`, { method: "PUT", body: JSON.stringify(data) }),
  /** 177: hyväksyjälle ennen hyväksyntää — A-punchit (estää), luokittelemattomat (näytetään), oikeus poiketa,
   *  uusin luovutus ja allekirjoitetun isometrin liitteet. */
  luovutustila: (linjaId: number) => pyynto<Luovutustila>(`/api/linjat/${linjaId}/luovutustila`),
  /** 251 (T621): asiakkaan edustajan allekirjoitus PNG-kuvana (data:image/png;base64,…) ja nimi. */
  tallennaAllekirjoitus: (linjaId: number, data: { nimi: string; kuva: string }) =>
    pyynto<{ linjatarkastus_id: number; liite_id: number }>(`/api/linjat/${linjaId}/linjatarkastus/allekirjoitus`,
      { method: "POST", body: JSON.stringify(data) }),

  /* ── seuranta ja raportointi (T646 (1), T514) ─────────────── */
  linjatarkastusSeuranta: (q: { kohde: number; tila?: "hyvaksytty" | "kesken" | "aloittamatta" }) =>
    pyynto<{ rivit: { linja_id: number; tunnus: string; tila: "hyvaksytty" | "kesken" | "aloittamatta"; pvm: string | null;
      tarkastaja: string | null; hyvaksyja: string | null; tuotu: boolean; avoimia_puncheja: number; avoimia_a_puncheja: number }[];
      maarat: { hyvaksytty: number; kesken: number; aloittamatta: number } }>(
      `/api/linjatarkastus/seuranta?${haku({ kohde: q.kohde, ...(q.tila && { tila: q.tila }) })}`),
  koeponnistusvalmius: (q: { kohde: number; vain_valmiit?: "0" | "1" }) =>
    pyynto<{ rivit: { linja_id: number; tunnus: string; hyvaksytty_pvm: string | null; avoimia_a_puncheja: number; koeponnistus: string | null;
      ped_arvo: string | null; ped_tila: string; ped_lahteet: { lahde: string; arvo: string | null; asiakirja?: string | null; pvm?: string | null }[];
      ndt_lahde: string; ndt_tila: string; ndt_erittely: string | null; valmis: boolean; syy: string | null }[];
      valmiita: number; hyvaksyttyja: number }>(
      `/api/linjat/koeponnistusvalmius?${haku({ kohde: q.kohde, vain_valmiit: q.vain_valmiit ?? "1" })}`),

  /* ── linjatarkastusnippu (198, päätös 90) ─────────────── */
  valmiitTarkastukseen: (q: { kohde: number; alue?: string; haku?: string; vain_valmiit?: "0" | "1"; isometri?: "kaikki" | "on" | "ei" }, signal?: AbortSignal) =>
    pyynto<{ rivit: { linja_id: number; tunnus: string; alue: string | null; valmis: boolean; syyt: string[]; isometri: boolean;
      nipussa: boolean; tarkastuksen_tila: string | null }[]; valmiita: number; valmiita_isometrilla: number; alueet: string[] }>(
      `/api/linjatarkastus/valmiit?${haku({ kohde: q.kohde, ...(q.alue && { alue: q.alue }), ...(q.haku && { haku: q.haku }),
        vain_valmiit: q.vain_valmiit ?? "1", isometri: q.isometri ?? "kaikki" })}`, { signal }),
  tarkastusnippu: () => pyynto<{ rivit: { linja_id: number; jarjestys: number; kasitelty: string | null; ohitettu: string | null;
    tunnus: string; alue: string | null; valmis: boolean; syyt: string[]; isometri: boolean; tarkastuksen_tila: string | null;
    /** T642: hyväksytty · ohitettu · kesken (tarkastuksesta johdettu). */
    nipputila?: "hyvaksytty" | "ohitettu" | "kesken" }[];
    /** T642: tehty = hyväksyttyjä. */
    tehty: number; ohitettuja?: number; yhteensa: number }>("/api/linjatarkastus/nippu"),
  lisaaNippuun: (linja_idt: number[]) =>
    pyynto<{ lisatty: number }>("/api/linjatarkastus/nippu", { method: "POST", body: JSON.stringify({ linja_idt }) }),
  merkitseNippu: (linjaId: number, b: { kasitelty?: boolean; ohitettu?: boolean }) =>
    pyynto(`/api/linjatarkastus/nippu/${linjaId}`, { method: "PATCH", body: JSON.stringify(b) }),
  poistaNipusta: (linjaId: number) => pyynto(`/api/linjatarkastus/nippu/${linjaId}`, { method: "DELETE" }),

  /* ── isometrit ja nastat (197, päätös 85) ─────────────── */
  tuoIsometri: (kohde_id: number, nimi: string, sisalto: string) =>
    pyynto<Isometri & { toisto: boolean }>("/api/isometrit/tuonti", { method: "POST", body: JSON.stringify({ kohde_id, nimi, sisalto }) }),
  // Päätös 102: esikatselu ei kirjoita; «Tuo valitut» lukee tiedoston uudelleen palvelimella.
  esikatseleIsometri: (kohde_id: number, nimi: string, sisalto: string) =>
    pyynto<IsometriEsikatselu>("/api/isometrit/esikatselu", { method: "POST", body: JSON.stringify({ kohde_id, nimi, sisalto }) }),
  tuoValitutIsometrit: (kohde_id: number, nimi: string, sisalto: string, valinnat: { avain: string; luo_linja?: boolean; lehti?: string }[]) =>
    pyynto<{ muoto: string; tulokset: { avain: string; tulos: string; isometri_id?: number; linja_luotu?: boolean }[] }>(
      "/api/isometrit/tuo-valitut", { method: "POST", body: JSON.stringify({ kohde_id, nimi, sisalto, valinnat }) }),
  isometrit: (kohde: number, tila: "odottaa" | "kohdistettu" | "kaikki" | "poistetut" = "kaikki") =>
    pyynto<{ rivit: Isometri[]; yhteenveto: { tunnistus: string; kohdistettu: boolean; n: number }[]; poistettuja?: number }>(`/api/isometrit?${haku({ kohde, tila })}`),
  // 201: pehmeä poisto (tuojat) ja palautus (pääkäyttäjä).
  poistaIsometri: (id: number, b: { syy?: string; vahvistettu?: boolean }) =>
    pyynto<Isometri>(`/api/isometrit/${id}/poisto`, { method: "POST", body: JSON.stringify(b) }),
  palautaIsometri: (id: number) => pyynto<Isometri>(`/api/isometrit/${id}/palautus`, { method: "POST", body: "{}" }),
  kohdistaIsometri: (id: number, b: { linja_id: number; lehti_nro?: number | null; lehti_kpl?: number | null; revisio?: string | null }) =>
    pyynto<Isometri>(`/api/isometrit/${id}/kohdistus`, { method: "PATCH", body: JSON.stringify(b) }),
  linjanIsometrit: (linjaId: number) =>
    pyynto<{ isometrit: Isometri[]; kuvat: { id: number; alkuperainen: string; lisatty: string; kierto?: number; nimi?: string | null;
      kuvalaji?: string | null }[] }>(`/api/linjat/${linjaId}/isometrit`),
  /** T635: linjatarkastusraportin koko sisältö yhdellä kutsulla (LinjatarkastusRaportti.tsx tyypittää). */
  linjatarkastusraportti: (linjaId: number) => pyynto<unknown>(`/api/linjat/${linjaId}/linjatarkastusraportti`),
  /** 253 (T639): kuvan näyttökierto 0/90/180/270 myötäpäivään; tiedosto ennallaan. */
  kierraLiite: (id: number, kierto: number) =>
    pyynto<{ id: number; kierto: number; muuttui: boolean }>(`/api/liitteet/${id}/kierto`, { method: "PATCH", body: JSON.stringify({ kierto }) }),
  /** 274 (T547): kuvan nimi ja laji; tyhjä nimi = ei nimeä (näytetään laji ja päivä). */
  nimeaKuva: (id: number, b: { nimi?: string; kuvalaji?: string | null }) =>
    pyynto<{ id: number; nimi: string | null; kuvalaji: string | null; muuttui: boolean }>(`/api/liitteet/${id}/kuva`,
      { method: "PATCH", body: JSON.stringify(b) }),
  isometrinSivu: (id: number, leveys: 1200 | 2400 | 3600 = 2400, sivu?: number) => `/api/isometrit/${id}/sivu.png?leveys=${leveys}${sivu ? `&sivu=${sivu}` : ""}`,
  nastat: (linjaId: number) => pyynto<Nasta[]>(`/api/linjat/${linjaId}/nastat`),
  lisaaNasta: (linjaId: number, b: { pohja: "isometri" | "kuva"; isometri_id?: number; kuva_liite_id?: number; x: number; y: number;
      vika: string; luokka: "A" | "B" | "C"; vastuu?: string; huomiot?: string; kirjaustunniste?: string } & PunchLuokat) =>
    pyynto<{ id: number; numero: number; punch_id: number; toisto?: boolean }>(`/api/linjat/${linjaId}/nastat`, { method: "POST", body: JSON.stringify(b) }),

  /* ── tuntikirjaus ─────────────────────────────────────── */
  tuntikirjaukset: (alku: string, loppu: string, o: { henkilo_id?: number; kohde_id?: number } = {}) =>
    pyynto<TuntiRivi[]>(`/api/tuntikirjaus?${haku({ alku, loppu,
      ...(o.henkilo_id !== undefined && { henkilo_id: o.henkilo_id }),
      ...(o.kohde_id !== undefined && { kohde_id: o.kohde_id }) })}`),
  tuntiYhteenveto: (alku: string, loppu: string, kohde_id?: number) =>
    pyynto<TuntiYhteenveto[]>(`/api/tuntikirjaus/yhteenveto?${haku({ alku, loppu,
      ...(kohde_id !== undefined && { kohde_id }) })}`),
  johdetutTunnit: (alku: string, loppu: string, henkilo_id?: number) =>
    pyynto<JohdettuTunti[]>(`/api/tunnit/johdetut?${haku({ alku, loppu,
      ...(henkilo_id !== undefined && { henkilo_id }) })}`),
  palkkaAineistoOsoite: (alku: string, loppu: string, henkilo_id?: number) =>
    `/api/tunnit/palkka-aineisto?${haku({ alku, loppu,
      ...(henkilo_id !== undefined && { henkilo_id }) })}`,
  luoTunti: (t: { henkilo_id?: number | null; kohde_id?: number | null; tyopaketti_id?: number | null;
      pvm: string; tunnit: number; km?: number; paivaraha?: "ei" | "osa" | "koko"; huomiot?: string | null; tehtava?: string | null;
      kirjaustunniste?: string }) =>
    pyynto<{ id: number; toisto?: boolean }>("/api/tuntikirjaus", { method: "POST", body: JSON.stringify(t) }),
  muokkaaTunti: (id: number, muutos: Partial<{ tunnit: number; km: number;
      paivaraha: "ei" | "osa" | "koko"; huomiot: string | null; tila: string }>) =>
    pyynto(`/api/tuntikirjaus/${id}`, { method: "PATCH", body: JSON.stringify(muutos) }),
  poistaTunti: (id: number) =>
    pyynto(`/api/tuntikirjaus/${id}`, { method: "DELETE" }),
  tuntiAsetukset: () => pyynto<TuntiAsetukset>("/api/tuntikirjaus/asetukset"),
  paivitaTuntiAsetukset: (a: Partial<TuntiAsetukset>) =>
    pyynto("/api/tuntikirjaus/asetukset", { method: "PATCH", body: JSON.stringify(a) }),

  resurssit: () =>
    pyynto<Resurssi[]>("/api/resurssit"),

  /**
   * Henkilölista ilman resursointia — Academyn ja osaamispuolen tapa
   * tietää ketä voi nimittää.
   *
   * ÄLÄ vaihda tätä `resurssit()`:iin. Suuntasääntö on yksisuuntainen:
   * Academy ei lue resursoinnista, resursointi lukee Academysta. Ilman
   * tätä eroa Academya ei voi myydä ilman projektinhallintaa.
   */
  henkilot: () =>
    pyynto<Henkilo[]>("/api/henkilot"),

  /* ── työvuoro / rotaatio ──────────────────────────────── */
  rotaatio: () => pyynto<RotaatioData>("/api/rotaatio"),
  luoRotaatioryhma: (nimi: string, oletus_vuorotyyppi_id: number | null) =>
    pyynto<{ id: number }>("/api/rotaatio",
      { method: "POST", body: JSON.stringify({ nimi, oletus_vuorotyyppi_id }) }),
  muokkaaRotaatioryhma: (id: number, muutos: { nimi?: string; oletus_vuorotyyppi_id?: number | null }) =>
    pyynto(`/api/rotaatio/${id}`, { method: "PATCH", body: JSON.stringify(muutos) }),
  poistaRotaatioryhma: (id: number) =>
    pyynto(`/api/rotaatio/${id}`, { method: "DELETE" }),
  lisaaRotaatioJasen: (id: number, henkilo_id: number) =>
    pyynto(`/api/rotaatio/${id}/jasen`, { method: "POST", body: JSON.stringify({ henkilo_id }) }),
  poistaRotaatioJasen: (id: number, henkilo_id: number) =>
    pyynto(`/api/rotaatio/${id}/jasen/${henkilo_id}`, { method: "DELETE" }),
  lisaaRotaatioJakso: (j: { rotaatioryhma_id?: number | null; henkilo_id?: number | null;
      alkaa: string; paattyy: string; tila: string; vuorotyyppi_id?: number | null }) =>
    pyynto<{ id: number }>("/api/rotaatiojakso", { method: "POST", body: JSON.stringify(j) }),
  poistaRotaatioJakso: (id: number) =>
    pyynto(`/api/rotaatiojakso/${id}`, { method: "DELETE" }),

  /** T373: kolmen viikon suunnitelma kohteelle (viikot, paketit vain hitsauslokilla, aikatauluttamattomat). */
  suunnitelma: (kohde_id: number) => pyynto<Suunnitelma>(`/api/suunnitelma?kohde_id=${kohde_id}`),
  /* ── työpaketit ───────────────────────────────────────── */
  tyopaketit: (kohde_id?: number, arkistoidut = false) =>
    pyynto<Tyopaketti[]>(`/api/tyopaketit?${haku({ ...(kohde_id ? { kohde_id } : {}), ...(arkistoidut ? { arkistoidut: "1" } : {}) })}`),
  luoTyopaketti: (t: { kohde_id: number; nimi: string; media?: string | null;
      dn?: string | null; linja_ids?: number[]; pyytaja?: string | null }) =>
    pyynto<{ id: number; nro: number }>("/api/tyopaketit",
      { method: "POST", body: JSON.stringify(t) }),
  muokkaaTyopaketti: (id: number, muutos: Partial<{ nimi: string; media: string | null;
      dn: string | null; tila: string; jarjestys: number | null;
      aloitettu: string | null; valmis: string | null;
      suunniteltu_alku: string | null; suunniteltu_loppu: string | null; tila_peruste: string }>) =>
    pyynto(`/api/tyopaketit/${id}`, { method: "PATCH", body: JSON.stringify(muutos) }),
  /** 235 (T520): «valmis linjatarkastukseen» -ilmoitus. */
  valmiusilmoitus: (linja_id: number) =>
    pyynto<{ avoin: { ilmoitettu: string; huomio: string | null; ilmoittaja: string | null } | null }>(`/api/linjat/${linja_id}/valmiusilmoitus`),
  ilmoitaValmiiksi: (linja_id: number, huomio?: string) =>
    pyynto<{ id: number }>(`/api/linjat/${linja_id}/valmiusilmoitus`, { method: "POST", body: JSON.stringify(huomio ? { huomio } : {}) }),
  peruValmiusilmoitus: (linja_id: number) =>
    pyynto<{ peruttu: number }>(`/api/linjat/${linja_id}/valmiusilmoitus`, { method: "DELETE" }),
  /** 238: työpakettia ei poisteta, se arkistoidaan perusteella; palautus tyhjentää merkinnän. */
  arkistoiTyopaketti: (id: number, peruste: string) =>
    pyynto(`/api/tyopaketit/${id}/arkistointi`, { method: "POST", body: JSON.stringify({ peruste }) }),
  palautaTyopaketti: (id: number) =>
    pyynto(`/api/tyopaketit/${id}/palautus`, { method: "POST", body: "{}" }),
  lisaaTyopakettiLinja: (id: number, linja_id: number) =>
    pyynto(`/api/tyopaketit/${id}/linja`, { method: "POST", body: JSON.stringify({ linja_id }) }),
  poistaTyopakettiLinja: (id: number, linja_id: number) =>
    pyynto(`/api/tyopaketit/${id}/linja/${linja_id}`, { method: "DELETE" }),
  lisaaTyopakettiTekija: (id: number, resurssi_id: number) =>
    pyynto(`/api/tyopaketit/${id}/tekija`, { method: "POST", body: JSON.stringify({ resurssi_id }) }),
  poistaTyopakettiTekija: (id: number, resurssi_id: number) =>
    pyynto(`/api/tyopaketit/${id}/tekija/${resurssi_id}`, { method: "DELETE" }),
  tyopakettiMateriaalit: (id: number) =>
    pyynto<TyopakettiMateriaalit>(`/api/tyopaketit/${id}/materiaalit`),
  tyopakettiTarve: (id: number) =>
    pyynto<TyopakettiTarve[]>(`/api/tyopaketit/${id}/tarve`),
  kirjaaTyopakettiOtot: (id: number, rivit: { varasto_nimike_id: number; maara: number }[]) =>
    pyynto<{ kirjattu: number }>(`/api/tyopaketit/${id}/otot`,
      { method: "POST", body: JSON.stringify({ rivit }) }),

  /* ── varasto ──────────────────────────────────────────── */
  varasto: (kohde_id?: number) =>
    pyynto<VarastoNimike[]>(`/api/varasto${kohde_id ? `?kohde_id=${kohde_id}` : ""}`),
  luoVarastoNimike: (n: { kohde_id: number; tunnus: string; nimi: string;
      ryhma?: string | null; dn?: number | null; mitat?: string | null;
      materiaali?: string | null; yksikko?: string }) =>
    pyynto<{ id: number }>("/api/varasto/nimike", { method: "POST", body: JSON.stringify(n) }),
  muokkaaVarastoNimike: (id: number, muutos: Partial<{ nimi: string; ryhma: string | null;
      dn: number | null; mitat: string | null; materiaali: string | null; yksikko: string }>) =>
    pyynto(`/api/varasto/nimike/${id}`, { method: "PATCH", body: JSON.stringify(muutos) }),
  poistaVarastoNimike: (id: number) =>
    pyynto(`/api/varasto/nimike/${id}`, { method: "DELETE" }),
  varastoTapahtumat: (nimike_id: number) =>
    pyynto<VarastoTapahtuma[]>(`/api/varasto/nimike/${nimike_id}/tapahtumat`),
  kirjaaVarastoTapahtuma: (t: { nimike_id: number; laji: "saapuminen" | "otto" | "inventaario" | "korjaus";
      maara: number; pvm?: string; tyopaketti_id?: number | null; viite?: string | null;
      sulatus?: string | null; kirjaaja?: string | null; huomiot?: string | null }) =>
    pyynto<{ id: number | null; ei_muutosta?: boolean }>("/api/varasto/tapahtuma",
      { method: "POST", body: JSON.stringify(t) }),
  poistaVarastoTapahtuma: (id: number) =>
    pyynto(`/api/varasto/tapahtuma/${id}`, { method: "DELETE" }),
  synkkaaVarasto: (kohde_id: number) =>
    pyynto<{ luettu: number; paivitetty: number }>(`/api/varasto/synkka?kohde_id=${kohde_id}`,
      { method: "POST" }),
  varastoSynkkaTila: () => pyynto<{ konffattu: boolean }>("/api/varasto/synkka-tila"),

  /* ── kapasiteetti ─────────────────────────────────────── */

  kapasiteetti: (alku: string, loppu: string) =>
    pyynto<KapasiteettiViikko[]>(`/api/kapasiteetti/viikko?${haku({ alku, loppu })}`),

  kapasiteettiPaiva: (alku: string, loppu: string) =>
    pyynto<KapasiteettiPaiva[]>(`/api/kapasiteetti/paiva?${haku({ alku, loppu })}`),

  /** Rekisterin tila: montako laskentaan menee, montako merkitty
   *  päättyneeksi, montako työaikamallilla. Ks. reitin perustelu. */
  kapasiteettiRekisteri: (alku: string, loppu: string) =>
    pyynto<KapasiteettiRekisteri>(
      `/api/kapasiteetti/rekisteri?${haku({ alku, loppu })}`),

  vaje: (alku: string, loppu: string) =>
    pyynto<Vaje[]>(`/api/vaje?${haku({ alku, loppu })}`),

  varamiehet: (alku: string, loppu: string) =>
    pyynto<Varamiehet[]>(`/api/varamiehet?${haku({ alku, loppu })}`),

  kuukausikuorma: (alku: string, loppu: string) =>
    pyynto<Kuukausikuorma[]>(`/api/kuukausikuorma?${haku({ alku, loppu })}`),

  /** Yhden henkilön aikataulu kohteittain ja viikoittain. */
  henkilonAikataulu: (resurssi_id: number, alku: string, loppu: string) =>
    pyynto<HenkilonRivi[]>(`/api/allokaatio/henkilo?${haku({ resurssi_id, alku, loppu })}`),

  allokaatiot: (resurssi_id: number, alku: string, loppu: string) =>
    pyynto<AllokaatioRivi[]>(`/api/allokaatio/viikko?${haku({ resurssi_id, alku, loppu })}`),

  /* ── allokaation kirjoitus ────────────────────────────── */

  /** Yksi henkilö (resurssi_id) tai joukko (resurssi_idt, T281) samalle kohteelle ja jaksolle. */
  allokoi: (syote: {
    resurssi_id?: number; resurssi_idt?: number[]; kohde_id: number; alkaa: string; paattyy: string;
    tunnit_per_paiva: number; tila?: "ehdotus" | "vahvistettu";
    /** tyopaivat = henkilön oma kuvio, kaikki = myös viikonloput ja pyhät */
    paivat?: "tyopaivat" | "kaikki";
    /** 145: pakollinen jos henkilöltä puuttuu tarverivin pätevyys.
     *  Puuttuva peruste on 400 `peruste_puuttuu`, ja syy luettelee mikä puuttuu. */
    peruste?: string;
    /** T401 rivi 7: mihin tehtävään (tarveriviin). Pakollinen kun kohteella on useampi tarve jaksolle (400 tarve_puuttuu). */
    tarve_id?: number;
  }) =>
    pyynto<{
      paivia: number;
      varoitukset: { henkilo_id: number; viikko: number; ylitys: boolean; virhe_poissa: boolean }[];
      henkilot: { resurssi_id: number; paivia: number }[];
      tarve_id: number | null;
    }>(
      "/api/allokaatio/jakso",
      { method: "POST", body: JSON.stringify(syote) }
    ),

  /** T401 rivi 6: keneltä valituista puuttuu jakson tarverivien pakollinen pätevyys — sama sääntö kuin tallennuksen portilla. */
  allokaatioEsteet: (syote: { kohde_id: number; alkaa: string; paattyy: string; resurssi_idt: number[]; tarve_id?: number }) =>
    pyynto<{
      puuttuvat: { resurssi_id: number; nimi: string; puuttuu: string }[];
      /** T401 rivi 7: kohteen tarpeet jaksolle — yksi valitaan itse, useasta lomake kysyy. */
      tarpeet: { id: number; osaamisluokka: string; alkaa: string; paattyy: string; maara: number; vaatimuksia: number }[];
      tarve_id: number | null;
      tarve_valinta: "ei" | "yksi" | "monta";
      virhe?: string; viesti?: string;
    }>(
      `/api/allokaatio/esteet?${haku({ kohde_id: syote.kohde_id, alkaa: syote.alkaa, paattyy: syote.paattyy,
        resurssi_idt: syote.resurssi_idt.join(","), ...(syote.tarve_id ? { tarve_id: syote.tarve_id } : {}) })}`),

  poistaAllokaatio: (syote: {
    resurssi_id: number; kohde_id: number; alkaa: string; paattyy: string;
  }) =>
    pyynto<{ poistettu: number }>("/api/allokaatio/jakso", {
      method: "DELETE",
      body: JSON.stringify(syote),
    }),

  /* ── työkohteet ───────────────────────────────────────── */

  osaamisluokat: () =>
    pyynto<Osaamisluokka[]>("/api/osaamisluokat"),

  luoKohde: (syote: {
    tunnus: string; nimi: string; laji: "projekti" | "jatkuva";
    asiakas?: string; sijainti?: string; alkaa?: string; paattyy?: string;
    /** 196 (päätös 80): uusi kohde vanhan jatkona — asiakas, sijainti ja asiakasliitokset kopioidaan. */
    jatkaa_kohde_id?: number;
  }) => pyynto<Kohde>("/api/kohteet", { method: "POST", body: JSON.stringify(syote) }),
  /** Päätös 80: tunnuksen muokkaus perusteella; 409 kohteella_kirjauksia / tunnus_varattu nimeltä. */
  muokkaaKohteenTunnusta: (id: number, syote: { tunnus: string; peruste: string; korjaus?: boolean }) =>
    pyynto<{ id: number; tunnus: string; entinen_tunnus: string }>(`/api/kohteet/${id}/tunnus`, { method: "PATCH", body: JSON.stringify(syote) }),

  muokkaaKohdetta: (id: number, muutokset: Record<string, unknown>) =>
    pyynto<Kohde>(`/api/kohteet/${id}`, { method: "PATCH", body: JSON.stringify(muutokset) }),

  kohteidenAikajana: (alku: string, loppu: string) =>
    pyynto<KohdePaallekkaisyys[]>(`/api/kohteet/aikajana?${haku({ alku, loppu })}`),

  tarpeet: (kohdeId: number) =>
    pyynto<Resurssitarve[]>(`/api/kohteet/${kohdeId}/tarpeet`),

  lisaaTarve: (kohdeId: number, syote: {
    osaamisluokka_id: number; alkaa: string;
    /** Puuttuva = toistaiseksi. Jatkuvalla sopimuksella ei ole loppua. */
    paattyy?: string;
    maara: number; kriittisyys?: number;
  }) => pyynto<{ id: number }>(`/api/kohteet/${kohdeId}/tarpeet`, {
    method: "POST", body: JSON.stringify(syote),
  }),

  poistaTarve: (id: number) =>
    pyynto<{ poistettu: number }>(`/api/tarpeet/${id}`, { method: "DELETE" }),

  /**
   * Sidonta tarpeeseen (159) ja työpäivät samalla (sidontalomake 23.9.).
   * Tyhjä alkaa = tarpeen alku, tyhjä paattyy = toistaiseksi. Loppupäivä
   * on mukaan lukien. `toistaiseksi` = päiviä ei kirjoitettu, koska loppua ei ole.
   */
  sidoTarpeeseen: (tarveId: number, syote: {
    resurssi_id: number; alkaa?: string; paattyy?: string;
    tunnit_per_paiva?: number; peruste?: string;
  }) => pyynto<{ id: number; paivia: number; varoitukset: number; toistaiseksi: boolean }>(
    `/api/tarpeet/${tarveId}/sidonta`, { method: "POST", body: JSON.stringify(syote) }),

  /** Päätös 13: vanhat allokaatiot tähän tehtävään — vain tarve_id vaihtuu. 400 peruste_puuttuu kuten allokoinnissa. */
  liitaAllokaatiot: (tarveId: number, syote: { resurssi_id: number; peruste?: string }) =>
    pyynto<{ liitetty: number; peruste_kirjattu: boolean; tarve_id: number; resurssi_id: number }>(
      `/api/tarpeet/${tarveId}/liita-allokaatiot`, { method: "POST", body: JSON.stringify(syote) }),

  /** Purku. `tyopaivat` poistaa myös saman kohteen työpäivät sidotulta väliltä. */
  puraSidonta: (id: number, tyopaivat: boolean) =>
    pyynto<{ poistettu: number; tyopaivia_poistettu: number }>(
      `/api/sidonta/${id}?tyopaivat=${tyopaivat}`, { method: "DELETE" }),

  /** Kohteen pätevyysvaatimukset: osaajat ja saatavuus jaksolla. */
  patevyysvaatimukset: (kohdeId: number) =>
    pyynto<KohdePatevyysvaatimus[]>(`/api/kohteet/${kohdeId}/patevyysvaatimukset`),

  /** Vaihe 1: vanhat kohdetason rivit joita ei ole kohdistettu tarpeeseen. */
  kohdistamattomatVaatimukset: (kohdeId: number) =>
    pyynto<{ id: number; patevyystyyppi_id: number; patevyys: string;
             lukumaara: number; huomiot: string | null;
             /** Kohteen tarverivit: mihin luku mahtuu ja mihin ei. */
             tarpeet: { tarve_id: number; osaamisluokka: string; maara: number; mahtuu: boolean }[];
             /** Koko kohteen tarve. null = kohteella ei ole tarpeita lainkaan
              *  (eri tila kuin nolla henkilöä). Jos lukumaara > tämä,
              *  kohdistus on mahdoton ennen kuin LUKU muuttuu. */
             tarve_yhteensa: number | null }[]>(
      `/api/kohteet/${kohdeId}/patevyysvaatimukset/kohdistamattomat`),

  /* ── työmaaperehdytys ─────────────────────────────────────
     Lista lähtee miehityksestä, ei perehdytyksistä: kysymys on
     kuka on tulossa työmaalle ILMAN perehdytystä. */
  perehdytystilanne: (kohdeId: number) =>
    pyynto<Perehdytystilanne[]>(`/api/kohteet/${kohdeId}/perehdytykset`),

  kirjaaPerehdytys: (kohdeId: number, syote: {
    henkilo_id: number; vaatimus_id?: number | null;
    paiva?: string; voimassa_asti?: string | null;
    perehdyttaja_nimi?: string | null; sisalto?: string | null; huomiot?: string | null;
  }) => pyynto<{ id: number; paiva: string }>(`/api/kohteet/${kohdeId}/perehdytykset`, {
    method: "POST", body: JSON.stringify(syote),
  }),

  poistaPerehdytys: (id: number) =>
    pyynto<{ poistettu: true }>(`/api/perehdytykset/${id}`, { method: "DELETE" }),

  lisaaPatevyysvaatimus: (kohdeId: number, syote: {
    patevyystyyppi_id: number; lukumaara?: number; huomiot?: string;
    /** Vaihe 1: mihin tarveriviin vaatimus kohdistuu. Puuttuva = kohdistamaton. */
    resurssitarve_id?: number;
  }) => pyynto<{ id: number }>(`/api/kohteet/${kohdeId}/patevyysvaatimukset`, {
    method: "POST", body: JSON.stringify(syote),
  }),

  /** Kohdistamaton (migraatiojäämä): tyyppi riittää tunnisteeksi siinä joukossa. */
  poistaPatevyysvaatimus: (kohdeId: number, patevyystyyppiId: number) =>
    pyynto<{ poistettu: number }>(
      `/api/kohteet/${kohdeId}/patevyysvaatimukset/${patevyystyyppiId}`,
      { method: "DELETE" }),

  /** Kohdistettu: tunniste on RIVI, koska sama pätevyys voi olla usealla tarpeella. */
  poistaVaatimusrivi: (kohdeId: number, vaatimusId: number) =>
    pyynto<{ poistettu: number }>(
      `/api/kohteet/${kohdeId}/patevyysvaatimukset/rivi/${vaatimusId}`,
      { method: "DELETE" }),

  /* ── kalusto ── */
  kalusto: (poistetut = false) => pyynto<Kalusto[]>(poistetut ? "/api/kalusto?poistetut=1" : "/api/kalusto"),
  luoKalusto: (syote: KalustoSyote & { nimi: string; laji: KalustoLaji }) =>
    pyynto<{ id: number }>("/api/kalusto", { method: "POST", body: JSON.stringify(syote) }),
  paivitaKalusto: (id: number, syote: KalustoSyote & { nimi?: string }) =>
    pyynto<{ id: number }>(`/api/kalusto/${id}`, { method: "PATCH", body: JSON.stringify(syote) }),
  /** 164: ylläpidettävä tyyppilista. */
  kalustoTyypit: () => pyynto<KalustoTyyppi[]>("/api/kalusto/tyypit"),
  lisaaKalustoTyyppi: (laji: "tyokalu" | "nostoapuvaline", nimi: string) =>
    pyynto<{ laji: string; nimi: string }>("/api/kalusto/tyypit", { method: "POST", body: JSON.stringify({ laji, nimi }) }),
  paivitaKalustoTyyppi: (id: number, syote: { nimi?: string; aktiivinen?: boolean }) =>
    pyynto<{ id: number }>(`/api/kalusto/tyypit/${id}`, { method: "PATCH", body: JSON.stringify(syote) }),
  /** 164: koko rekisteri Pro-Totin inventaariopohjan muodossa. */
  kalustoVientiUrl: () => "/api/kalusto/vienti",
  /* 172: QR-tarra. Numero on tarran oma, ei laitteen. */
  kalustoQr: (n: number) => pyynto<KalustoQr>(`/api/kalusto/qr/${n}`),
  kalustoQrEhdokkaat: (n: number, haku: string) =>
    pyynto<{ haku: string; ehdokkaat: KalustoQrEhdokas[]; raja?: string }>(`/api/kalusto/qr/${n}/ehdokkaat?${new URLSearchParams({ haku }).toString()}`),
  kalustoQrSido: (n: number, kalusto_id: number) =>
    pyynto<{ numero: number; kalusto_id: number }>(`/api/kalusto/qr/${n}/sidonta`, { method: "POST", body: JSON.stringify({ kalusto_id }) }),
  kalustoQrLisaa: (n: number, syote: KalustoSyote & { nimi: string; laji: KalustoLaji }) =>
    pyynto<{ numero: number; kalusto_id: number; hyvaksynta: KalustoHyvaksynta }>(`/api/kalusto/qr/${n}/lisays`, { method: "POST", body: JSON.stringify(syote) }),
  /** 55: kalustovastaavan päätös kenttätekijän ehdotuksesta. Hylkäys vaatii perusteen. */
  kalustoHyvaksynta: (id: number, paatos: "hyvaksytty" | "hylatty", peruste?: string) =>
    pyynto<{ id: number; hyvaksynta: KalustoHyvaksynta }>(`/api/kalusto/${id}/hyvaksynta`, { method: "POST", body: JSON.stringify({ paatos, peruste: peruste ?? null }) }),
  /** 74: joukkohyväksyntä — kaikki tai ei mitään; jokainen laite omana muutoslokirivinään. */
  kalustoHyvaksyntaJoukko: (ids: number[], paatos: "hyvaksytty" | "hylatty", peruste?: string) =>
    pyynto<{ ids: number[]; tunnukset: string[]; hyvaksynta: KalustoHyvaksynta; maara: number }>(`/api/kalusto/hyvaksynta-joukko`,
      { method: "POST", body: JSON.stringify({ ids, paatos, peruste: peruste ?? null }) }),
  kalustoQrOta: (n: number, peruste?: string) =>
    pyynto<{ numero: number; kalusto_id: number; muuttui: boolean }>(`/api/kalusto/qr/${n}/haltuunotto`,
      { method: "POST", body: JSON.stringify(peruste ? { peruste } : {}) }),
  kalustoQrPalauta: (n: number, syote: { sijainti_laji: KalustoSijaintilaji; sijainti_kohde_id: number | null; sijainti_teksti: string | null; peruste?: string }) =>
    pyynto<{ numero: number; kalusto_id: number }>(`/api/kalusto/qr/${n}/palautus`, { method: "POST", body: JSON.stringify(syote) }),
  /** 76: «Kirjaa sijaintiin» haltijattomalle laitteelle — sama runko kuin palautuksella. */
  kalustoQrSijainti: (n: number, syote: { sijainti_laji: KalustoSijaintilaji; sijainti_kohde_id: number | null; sijainti_teksti: string | null; peruste?: string }) =>
    pyynto<{ numero: number; kalusto_id: number; sijainti_laji: KalustoSijaintilaji }>(`/api/kalusto/qr/${n}/sijainti`, { method: "POST", body: JSON.stringify(syote) }),
  kalustoQrKorvaa: (n: number, syote: { syy: KalustoQrKorvausSyy; peruste: string; uusi_numero: number | null }) =>
    pyynto<{ numero: number; uusi_numero: number | null; kalusto_id: number }>(`/api/kalusto/qr/${n}/korvaus`, { method: "POST", body: JSON.stringify(syote) }),
  /** 116: poisto inventaariosta — kuka, milloin, miksi. Rivi säilyy. */
  poistaKalusto: (id: number, syote: { syy: string; peruste: string }) =>
    pyynto<{ id: number; poistettu: boolean }>(`/api/kalusto/${id}/poisto`, { method: "POST", body: JSON.stringify(syote) }),
  palautaKalusto: (id: number, syote: { peruste: string }) =>
    pyynto<{ id: number; palautettu: boolean }>(`/api/kalusto/${id}/palautus`, { method: "POST", body: JSON.stringify(syote) }),
  kalustonTarkastukset: (id: number) =>
    pyynto<KalustoTarkastus[]>(`/api/kalusto/${id}/tarkastukset`),
  lisaaKatsastus: (id: number, syote: {
    voimassa_asti?: string; tarkastuspaiva?: string;
    tarkastaja?: string; tulos: "hyvaksytty" | "ehdollinen" | "hylatty"; tulos_syy?: string; huomiot?: string;
  }) => pyynto<{ id: number }>(`/api/kalusto/${id}/tarkastukset`, {
    method: "POST", body: JSON.stringify(syote),
  }),
  poistaKatsastus: (id: number) =>
    pyynto<{ poistettu: number }>(`/api/kalusto/tarkastukset/${id}`, { method: "DELETE" }),

  /* ── tarjouslaskenta ── */
  tarjoukset: () => pyynto<Tarjous[]>("/api/tarjoukset"),
  luoTarjous: (syote: { tunnus: string; nimi: string; asiakas_id?: number | null; asiakas_nimi?: string }) =>
    pyynto<{ id: number }>("/api/tarjoukset", {
      method: "POST", body: JSON.stringify(syote),
    }),
  tarjous: (id: number) => pyynto<TarjousDetalji>(`/api/tarjoukset/${id}`),
  paivitaTarjous: (id: number, syote: Partial<{
    nimi: string; tilaaja: string; tila: string; alkaa: string; paattyy: string;
    sakkoehto: string; kate_pros: number; riski_pros: number; huomiot: string;
    tes_id: number | null;
    osuus_ilta: number; osuus_yo: number; osuus_lauantai: number;
    osuus_sunnuntai: number; osuus_ylityo50: number; osuus_ylityo100: number;
    asiakas_id: number | null; myyja: string;
  }>) => pyynto<{ id: number }>(`/api/tarjoukset/${id}`, {
    method: "PATCH", body: JSON.stringify(syote),
  }),
  lisaaEra: (id: number, syote: {
    kuvaus: string; maara: number; yksikko?: string; dn?: number;
    materiaali?: string; hitsausprosessi?: string; arvio?: boolean; normi_h?: number;
    osaamisluokka_id?: number | null;
  }) => pyynto<{ id: number }>(`/api/tarjoukset/${id}/erat`, {
    method: "POST", body: JSON.stringify(syote),
  }),
  poistaEra: (id: number) =>
    pyynto<{ poistettu: number }>(`/api/erat/${id}`, { method: "DELETE" }),
  lisaaKustannus: (id: number, syote: { laji: string; kuvaus: string; summa: number }) =>
    pyynto<{ id: number }>(`/api/tarjoukset/${id}/kustannukset`, {
      method: "POST", body: JSON.stringify(syote),
    }),
  poistaKustannus: (id: number) =>
    pyynto<{ poistettu: number }>(`/api/kustannukset/${id}`, { method: "DELETE" }),

  /* ── materiaalikatalogi + tarjouksen BOM ── */
  materiaalit: () => pyynto<Materiaali[]>("/api/materiaalit"),
  /** Tuontipohja: samat otsikot joihin tuonti osuu, ohjearkki mukana. */
  materiaalipohjaUrl: () => "/api/materiaalit/pohja",
  tuoMateriaalit: (sisalto: string, vahvista: boolean) =>
    pyynto<MateriaaliTuonti>("/api/materiaalit/tuonti",
      { method: "POST", body: JSON.stringify({ sisalto, vahvista }) }),
  lisaaMateriaali: (syote: {
    tunnus?: string | null; nimi: string; ryhma?: string | null; yksikko?: string;
    dn?: number | null; paineluokka?: string | null; laatu?: string | null;
    sisaostohinta?: number | null;
  }) => pyynto<{ id: number }>("/api/materiaalit", { method: "POST", body: JSON.stringify(syote) }),
  paivitaMateriaali: (id: number, muutokset: Partial<{
    nimi: string; ryhma: string | null; yksikko: string; dn: number | null;
    paineluokka: string | null; laatu: string | null; sisaostohinta: number | null;
    aktiivinen: boolean;
  }>) => pyynto<{ id: number }>(`/api/materiaalit/${id}`, { method: "PATCH", body: JSON.stringify(muutokset) }),
  poistaMateriaali: (id: number) =>
    pyynto<{ passivoitu: boolean }>(`/api/materiaalit/${id}`, { method: "DELETE" }),
  lisaaTarjousMateriaali: (id: number, syote: {
    materiaali_id?: number | null; kuvaus?: string; maara: number;
    yksikko?: string; yksikkohinta?: number | null;
  }) => pyynto<{ id: number }>(`/api/tarjoukset/${id}/materiaalit`, {
    method: "POST", body: JSON.stringify(syote),
  }),
  poistaTarjousMateriaali: (id: number) =>
    pyynto<{ poistettu: number }>(`/api/tarjous-materiaali/${id}`, { method: "DELETE" }),

  /* ── asiakasrekisteri ── */
  /* ── kohteen asiakkaat (149) ─────────────────────────────
     ⛔ Rooli ei ole pääsy: nämä kutsut eivät kosketa `kayttaja_kohde`en.
     Tilaajaksi lisääminen ei anna kenellekään pääsyä. */
  asiakasroolit: () => pyynto<Asiakasrooli[]>("/api/asiakasroolit"),
  kohteenAsiakkaat: (kohdeId: number) =>
    pyynto<KohteenAsiakas[]>(`/api/kohteet/${kohdeId}/asiakkaat`),
  liitaAsiakasKohteeseen: (kohdeId: number,
      syote: { asiakas_id: number; rooli?: AsiakasRooli; huomiot?: string }) =>
    pyynto<{ id: number }>(`/api/kohteet/${kohdeId}/asiakkaat`,
      { method: "POST", body: JSON.stringify(syote) }),
  vaihdaKohteenAsiakasRooli: (kohdeId: number, riviId: number, rooli: AsiakasRooli) =>
    pyynto<{ id: number }>(`/api/kohteet/${kohdeId}/asiakkaat/${riviId}`,
      { method: "PATCH", body: JSON.stringify({ rooli }) }),
  irrotaAsiakasKohteesta: (kohdeId: number, riviId: number) =>
    pyynto<{ poistettu: number }>(`/api/kohteet/${kohdeId}/asiakkaat/${riviId}`,
      { method: "DELETE" }),

  asiakkaat: (poistetut = false) => pyynto<Asiakas[]>(poistetut ? "/api/asiakkaat?poistetut=1" : "/api/asiakkaat"),
  /** 118: poisto rekisteristä — kuka, milloin, miksi. Rivi ja tarjoukset säilyvät. */
  /* ── kohteen perehdytysvaatimukset (146/147) ─────────────
     `oletettu` = kohteelle ei ole kirjattu yhtään vaatimusta, ja tämä on
     se sama lause jota havainnointi on aina nostanut. Se ei ole tilaajan
     vaatimus, ja näkymä sanoo sen. */
  /** 148: vientiloki. `alkoi` kertoo mistä hetkestä loki kattaa —
     tyhjä lista ei ole väite siitä ettei mitään ole viety. */
  vientiloki: (raja = 100, ehdot?: { kohde_id?: number; vienti?: string }) =>
    pyynto<{ rivit: VientilokiRivi[]; alkoi: string | null }>(
      "/api/vientiloki?" + new URLSearchParams({
        raja: String(raja),
        ...(ehdot?.kohde_id ? { kohde_id: String(ehdot.kohde_id) } : {}),
        ...(ehdot?.vienti ? { vienti: ehdot.vienti } : {}),
      }).toString()),

  perehdytysvaatimukset: (kohdeId: number) =>
    pyynto<PerehdytysVaatimus[]>(
      `/api/kohteet/${kohdeId}/perehdytysvaatimukset`),
  lisaaPerehdytysvaatimus: (kohdeId: number,
      syote: { laji: string; alue?: string; pakollinen?: boolean; huomiot?: string }) =>
    pyynto<{ id: number }>(`/api/kohteet/${kohdeId}/perehdytysvaatimukset`,
      { method: "POST", body: JSON.stringify(syote) }),
  poistaPerehdytysvaatimus: (kohdeId: number, vaatimusId: number) =>
    pyynto<{ poistettu: number }>(
      `/api/kohteet/${kohdeId}/perehdytysvaatimukset/${vaatimusId}`, { method: "DELETE" }),

  /* ── punakynä (152) ──────────────────────────────────────
     Hyväksyntä vaatii oikeuden `punakyna_hyvaksynta` (ei roolia), ja
     perustelu on pakollinen. Peruutus on oma rivinsä eikä poisto. */
  /** 153: seuraava vapaa alinumero kantanumerolle. Palvelin ehdottaa,
   *  kanta on tuomari (uniikki linja+lehti+numero). */
  seuraavaAlinumero: (linjaId: number, kanta: string, lehti?: string) =>
    pyynto<{ kanta: string; ehdotus: string }>(
      "/api/hitsit/seuraava-alinumero?" + new URLSearchParams({
        linja_id: String(linjaId), kanta,
        ...(lehti ? { lehti } : {}),
      }).toString()),
  /** 153: työnjohdon jono — alinumerot joilla ei ole voimassa olevaa hyväksyntää. */
  punakynaOdottavat: (kohdeId?: number) =>
    pyynto<PunakynaOdottava[]>(
      "/api/punakyna/odottavat" + (kohdeId ? `?kohde_id=${kohdeId}` : "")),
  punakynaHistoria: (hitsiId: number) =>
    pyynto<Punakyna[]>(`/api/hitsit/${hitsiId}/punakyna`),
  /** 160: `asema` valitaan hyväksymishetkellä. Puuttuva = kenttätyönjohto, jos rooli riittää. */
  hyvaksyPunakyna: (hitsiId: number, laji: "lisays" | "ei_toteudu", peruste: string, asema?: HyvaksyjanAsema, ulk: UlkopuolinenHyvaksyja = {}) =>
    pyynto<{ id: number }>(`/api/hitsit/${hitsiId}/punakyna`,
      { method: "POST", body: JSON.stringify({ laji, peruste, ...(asema ? { asema } : {}), ...ulk }) }),
  peruPunakyna: (id: number, peruste: string, asema?: HyvaksyjanAsema, ulk: UlkopuolinenHyvaksyja = {}) =>
    pyynto<{ id: number }>(`/api/punakyna/${id}/peruutus`,
      { method: "POST", body: JSON.stringify({ peruste, ...(asema ? { asema } : {}), ...ulk }) }),

  /* «Hitsiä ei tehdä» (158). Ehdotus kantaa mukanaan sen mihin se
     perustuu: pelkkä numero näyttäisi tiedolta, ja se on arvaus. */
  vapaaNumero: (linjaId: number) =>
    pyynto<{ ehdotus: string; suurin: string | null; lehdet: string[];
             numeroituja: number; numeroimattomia: number }>(
      `/api/linjat/${linjaId}/vapaa-numero`),
  hitsiaEiTehda: (linjaId: number, syote: { hitsinumero: string; peruste: string; piirustuslehti?: string }) =>
    pyynto<{ id: number; tila: string; taso: string | null }>(
      `/api/linjat/${linjaId}/ei-toteudu`,
      { method: "POST", body: JSON.stringify(syote) }),

  poistaAsiakas: (id: number, syote: { syy: string; peruste: string }) =>
    pyynto<{ id: number; poistettu: boolean }>(`/api/asiakkaat/${id}/poisto`, { method: "POST", body: JSON.stringify(syote) }),
  palautaAsiakas: (id: number, syote: { peruste: string }) =>
    pyynto<{ id: number; palautettu: boolean }>(`/api/asiakkaat/${id}/palautus`, { method: "POST", body: JSON.stringify(syote) }),
  haeYtj: (ytunnus: string) => pyynto<YtjTulos>(`/api/ytj?${haku({ ytunnus })}`),
  luoAsiakas: (syote: Partial<Asiakas> & { nimi: string }) =>
    pyynto<{ id: number }>("/api/asiakkaat", { method: "POST", body: JSON.stringify(syote) }),
  paivitaAsiakas: (id: number, muutokset: Partial<Asiakas>) =>
    pyynto<{ id: number }>(`/api/asiakkaat/${id}`, { method: "PATCH", body: JSON.stringify(muutokset) }),

  /* ── talon sertifikaatit + yrityksen tiedot ── */
  sertifikaatit: () => pyynto<Sertifikaatti[]>("/api/sertifikaatit"),
  luoSertifikaatti: (syote: {
    nimi: string; tunnus?: string | null; myontaja?: string | null; voimassa_asti?: string | null;
  }) => pyynto<{ id: number }>("/api/sertifikaatit", { method: "POST", body: JSON.stringify(syote) }),
  poistaSertifikaatti: (id: number) =>
    pyynto<{ passivoitu: boolean }>(`/api/sertifikaatit/${id}`, { method: "DELETE" }),
  paivitaYrityksenTiedot: (muutokset: Partial<{
    nimi: string; ytunnus: string; osoite: string; puhelin: string; verkkosivu: string; yhteysosoite: string;
  }>) => pyynto<{ paivitetty: number }>("/api/brandi", { method: "PATCH", body: JSON.stringify(muutokset) }),

  /* ── tarjouslaskennan asetukset (talon luvut) ── */
  tarjousAsetukset: () => pyynto<TarjousAsetukset>("/api/tarjous/asetukset"),
  paivitaSivukulu: (id: number, prosentti: number) =>
    pyynto<{ id: number }>(`/api/sivukulut/${id}`, {
      method: "PATCH", body: JSON.stringify({ prosentti }),
    }),
  paivitaSivukuluLahde: (id: number, muutos: { lahde_url?: string | null; vuosi?: number | null }) =>
    pyynto<{ id: number }>(`/api/sivukulut/${id}`, {
      method: "PATCH", body: JSON.stringify(muutos),
    }),
  paivitaOsaamisluokkaHinta: (id: number, tuntihinta: number | null) =>
    pyynto<{ id: number }>(`/api/osaamisluokat/${id}/tuntihinta`, {
      method: "PATCH", body: JSON.stringify({ tuntihinta }),
    }),
  luoTes: (tunnus: string, nimi: string) =>
    pyynto<{ id: number }>("/api/tesit", {
      method: "POST", body: JSON.stringify({ tunnus, nimi }),
    }),
  paivitaTes: (id: number, muutokset: { nimi?: string; aktiivinen?: boolean }) =>
    pyynto<{ id: number }>(`/api/tesit/${id}`, {
      method: "PATCH", body: JSON.stringify(muutokset),
    }),
  // Voimassaolojakso: yhden voimaantulopäivän lisät kerralla. Sama päivä
  // korjaa arvot, uusi päivä luo uuden jakson (vanha jää historiaan).
  tallennaTesJakso: (id: number, alkaa: string,
    arvot: { laji: string; arvo: number; yksikko?: string }[]) =>
    pyynto<{ tallennettu: number }>(`/api/tesit/${id}/jakso`, {
      method: "POST", body: JSON.stringify({ alkaa, arvot }),
    }),
  poistaTesJakso: (id: number, alkaa: string) =>
    pyynto<{ poistettu: number }>(`/api/tesit/${id}/jakso/${alkaa}`, { method: "DELETE" }),

  /** TES-PDF:n poiminta: palauttaa ehdokkaat vahvistettavaksi, ei tallenna. */
  parsiTes: (sisalto: string) =>
    pyynto<{ ehdokkaat: TesEhdokas[] }>("/api/tes/parsi", {
      method: "POST", body: JSON.stringify({ sisalto }),
    }),

  /* ── normitaulukko (DN → h/hitsi) ── */
  hitsausnormit: () => pyynto<Hitsausnormi[]>("/api/hitsausnormit"),
  hitsausnormiEhdotus: (dn: number, prosessi?: string) =>
    pyynto<{ normi_h: number | null }>(
      `/api/hitsausnormi?${haku({ dn, ...(prosessi ? { prosessi } : {}) })}`),
  lisaaHitsausnormi: (syote: {
    dn: number; hitsausprosessi?: string | null; normi_h: number; huomiot?: string | null;
  }) => pyynto<{ id: number }>("/api/hitsausnormit", {
    method: "POST", body: JSON.stringify(syote),
  }),
  paivitaHitsausnormi: (id: number, normi_h: number) =>
    pyynto<{ id: number }>(`/api/hitsausnormit/${id}`, {
      method: "PATCH", body: JSON.stringify({ normi_h }),
    }),
  poistaHitsausnormi: (id: number) =>
    pyynto<{ poistettu: number }>(`/api/hitsausnormit/${id}`, { method: "DELETE" }),
  paivitaTarjousAsetukset: (muutokset: Partial<{
    vuokra_kerroin: number; tyopaivia_per_vko: number; tunnit_per_paiva: number;
    matka_kustannus: number; matka_paivaraha: number;
  }>) => pyynto<{ paivitetty: number }>("/api/tarjous/asetukset", {
    method: "PATCH", body: JSON.stringify(muutokset),
  }),
  paivitaOsaamisluokkaMatkat: (id: number, matkat_per_kk: number) =>
    pyynto<{ id: number }>(`/api/osaamisluokat/${id}/matkat`, {
      method: "PATCH", body: JSON.stringify({ matkat_per_kk }),
    }),

  muokkaaHenkiloa: (id: number, muutokset: {
    tunnit_per_paiva?: number; maksimi_per_paiva?: number;
    maksimi_per_viikko?: number | null;
    maksimi_per_kuukausi?: number | null;
    tyopaivat?: number[];
    lyhenne?: string; tyotunnus?: string | null; nimike?: string;
    tuntipalkka?: number | null; tes_id?: number | null;
    matkat_per_kk?: number | null;
    aloitti?: string | null; paatti?: string | null;
  }) => pyynto<Resurssi>(`/api/resurssit/${id}`, {
    method: "PATCH", body: JSON.stringify(muutokset),
  }),

  /** Kevyt TES-lista henkilön työsuhdetietojen pudotusvalikkoon. */
  tesitLista: () => pyynto<TesListaus[]>("/api/tesit"),

  /** Tehtäväkohtainen tuntipalkka (per osaamisluokka). null = poista. */
  asetaTehtavapalkka: (id: number, osaamisluokka_id: number, tuntipalkka: number | null) =>
    pyynto<{ ok: boolean }>(`/api/resurssit/${id}/palkka`, {
      method: "PUT", body: JSON.stringify({ osaamisluokka_id, tuntipalkka }),
    }),

  asetaOsaamiset: (id: number, osaamisluokka_idt: number[]) =>
    pyynto<{ osaamisia: number }>(`/api/resurssit/${id}/osaamiset`, {
      method: "PUT", body: JSON.stringify({ osaamisluokka_idt }),
    }),

  asetaHitsaustyo: (id: number, hitsaustyo: boolean, peruste?: { peruste_laji: string; peruste?: string }) =>
    pyynto<{ id: number; hitsaustyo: boolean; muuttui: boolean }>(`/api/osaamisluokat/${id}/hitsaustyo`, {
      method: "PATCH", body: JSON.stringify({ hitsaustyo, ...peruste }),
    }),
  luoOsaamisluokka: (nimi: string, jarjestys?: number) =>
    pyynto<Osaamisluokka>("/api/osaamisluokat", {
      method: "POST", body: JSON.stringify({ nimi, ...(jarjestys !== undefined && { jarjestys }) }),
    }),
  asetaLaippatyo: (id: number, laippatyo: boolean, peruste?: { peruste_laji: string; peruste?: string }) =>
    pyynto<{ id: number; laippatyo: boolean; muuttui: boolean }>(`/api/osaamisluokat/${id}/laippatyo`,
      { method: "PATCH", body: JSON.stringify({ laippatyo, ...(peruste ?? {}) }) }),
  asetaIvAsennustyo: (id: number, iv_asennustyo: boolean, peruste?: { peruste_laji: string; peruste?: string }) =>
    pyynto<{ id: number; iv_asennustyo: boolean; muuttui: boolean }>(`/api/osaamisluokat/${id}/iv_asennustyo`,
      { method: "PATCH", body: JSON.stringify({ iv_asennustyo, ...(peruste ?? {}) }) }),
  asetaLaiteasennustyo: (id: number, laiteasennustyo: boolean, peruste?: { peruste_laji: string; peruste?: string }) =>
    pyynto<{ id: number; laiteasennustyo: boolean; muuttui: boolean }>(`/api/osaamisluokat/${id}/laiteasennustyo`,
      { method: "PATCH", body: JSON.stringify({ laiteasennustyo, ...(peruste ?? {}) }) }),

  passivoiOsaamisluokka: (id: number) =>
    pyynto<{ passivoitu: true }>(`/api/osaamisluokat/${id}`, { method: "DELETE" }),

  /** Mitoituskysely — ei tallenna mitään. */
  mitoitus: (osaamisluokka_id: number, alku: string, loppu: string, henkiloa: number) =>
    pyynto<MitoitusRivi[]>(
      `/api/mitoitus?${haku({ osaamisluokka_id, alku, loppu, henkiloa })}`
    ),
  mitoitusMonta: (alku: string, loppu: string, osaamiset: {
    osaamisluokka_id: number; henkiloa: number; patevyystyyppi_idt?: number[];
  }[]) => pyynto<MitoitusMonta>("/api/mitoitus/monta", {
    method: "POST", body: JSON.stringify({ alku, loppu, osaamiset }),
  }),

  /* ── hitsausloki ──────────────────────────────────────── */

  linjat: (hakusana?: string, kohde_id?: number, raja?: number) =>
    pyynto<Linja[]>(`/api/linjat?${haku({
      ...(hakusana && { haku: hakusana }),
      ...(kohde_id !== undefined && { kohde_id }),
      ...(raja !== undefined && { raja }),
    })}`),

  /** 124: linja tunnuksella, myös korvattu (passiivinen) — kertoo mihin tunnus ohjaa. 404 → ApiError. */
  linjaTunnuksella: (tunnus: string) =>
    pyynto<LinjaTunnuksella[]>(`/api/linjat/tunnus/${encodeURIComponent(tunnus)}`),

  // Ehdota linjat medialla (+ DN) — työpaketin luonnin apuna.
  ehdotaLinjat: (kohde_id: number, media?: string, dn?: string) =>
    pyynto<Linja[]>(`/api/linjat?${haku({
      kohde_id,
      ...(media && { media }),
      ...(dn && { dn }),
      raja: 400,
    })}`),

  luoLinja: (syote: {
    kohde_id: number; tunnus: string; dn?: number;
    putkiluokka?: string; ped_luokka?: string;
    isometri_olemassa?: boolean; suunniteltuja_hitseja?: number | null;
  }) => pyynto<{ id: number; tunnus: string }>("/api/linjat", {
    method: "POST", body: JSON.stringify(syote),
  }),

  wpsLista: () => pyynto<Wps[]>("/api/wps"),

  /* ── T280: Excel-tuonti pohjalla (sama määrittely kirjoittaa pohjan ja lukee tuonnin) ── */
  /** 164: Pro-Totin inventaariopohja (Työkalut · Nostoapuvälineet · Henkilövaljaat). */
  kalustopohjaUrl: () => "/api/kalusto/pohja",
  tuoKalusto: (sisalto: string, tiedosto: string, vahvista: boolean, paivita = false) =>
    pyynto<PohjaTuonti>("/api/kalusto/tuonti",
      { method: "POST", body: JSON.stringify({ sisalto, tiedosto, vahvista, paivita }) }),
  /** T172: henkilöstön luontikaista. Yrityksen yhteinen — ei kohdetta. */
  henkilopohjaUrl: () => "/api/henkilot/pohja",
  tuoHenkilot: (sisalto: string, tiedosto: string, vahvista: boolean, paivita = false) =>
    pyynto<PohjaTuonti>("/api/henkilot/tuonti",
      { method: "POST", body: JSON.stringify({ sisalto, tiedosto, vahvista, paivita }) }),
  // 213 (T445): puuttuvien hitsien kuittaus työnjohdolle. Kuittaus ei luo hitsiä — hitsi tulee masterin kautta.
  hitsikuittausPohjaUrl: () => "/api/hitsikuittaus/pohja",
  hitsikuittausVientiUrl: (kohde_id: number) => `/api/hitsikuittaus/vienti?kohde_id=${kohde_id}`,
  tuoHitsikuittaus: (kohde_id: number, sisalto: string, tiedosto: string, vahvista: boolean, paivita?: boolean) =>
    pyynto<PohjaTuonti>("/api/hitsikuittaus/tuonti",
      { method: "POST", body: JSON.stringify({ kohde_id, sisalto, tiedosto, vahvista, paivita: !!paivita }) }),
  /** «Tunnista linja»: tarra · linjatunnuksen osa · laitetunnus From/To + rajaimet (pääChat, Tonin välittämä 9.10.2026). */
  tunnistaLinja: (kohde_id: number, p: { q?: string; alue?: string; dn?: string; spec?: string; media?: string }) =>
    pyynto<{ rivit: TunnistettuLinja[]; rajattu: boolean }>(`/api/hitsikuittaus/tunnista?${new URLSearchParams(
      Object.entries({ kohde_id: String(kohde_id), ...p }).filter(([, v]) => v !== undefined && v !== "") as [string, string][]).toString()}`),
  hitsikuittaus: (kohde_id: number, alue?: string) =>
    pyynto<Hitsikuittaukset>(`/api/hitsikuittaus?kohde_id=${kohde_id}${alue ? `&alue=${encodeURIComponent(alue)}` : ""}`),
  kuittaaHitsi: (id: number, b: { ratkaisu: KuittausRatkaisu | null; hitsaaja_koodi?: string | null; asentaja_koodi?: string | null;
                                  paiva?: string | null; vaihe?: "esivalmiste" | "asennus" | "korjaus" | null; kuittaus_huomio?: string | null }) =>
    pyynto<{ id: number; ratkaisu: KuittausRatkaisu | null; kuitattu: string | null }>(`/api/hitsikuittaus/${id}`,
      { method: "PATCH", body: JSON.stringify(b) }),
  linjapohjaUrl: () => "/api/linjat/pohja",
  /** vahvista=false = kuivakäynti (ei kirjoita), true = luo uudet.
   *  paivita=true päivittää myös olemassa olevat rivit erojen mukaan (G4b);
   *  tyhjä solu ei muuta mitään, koska tyhjästä ei synny eroa. */
  tuoLinjat: (kohde_id: number, sisalto: string, tiedosto: string, vahvista: boolean, paivita = false) =>
    pyynto<PohjaTuonti>("/api/linjat/tuonti",
      { method: "POST", body: JSON.stringify({ kohde_id, sisalto, tiedosto, vahvista, paivita }) }),
  ndtPohjaUrl: () => "/api/hitsausloki/ndt-vaatimukset/pohja",
  /** T286: kohteen NDT-vaatimukset. vahvista=false = kuivakäynti. */
  tuoNdtVaatimukset: (kohde_id: number, sisalto: string, tiedosto: string, vahvista: boolean) =>
    pyynto<PohjaTuonti>("/api/hitsausloki/ndt-vaatimukset/tuonti",
      { method: "POST", body: JSON.stringify({ kohde_id, sisalto, tiedosto, vahvista }) }),
  /** T652: linjojen PED-luokat asiakirjasta (lähde «asiakirja» koeponnistusvalmiudelle). */
  pedPohjaUrl: () => "/api/hitsausloki/ped-luokat/pohja",
  tuoPedLuokat: (kohde_id: number, sisalto: string, tiedosto: string, vahvista: boolean) =>
    pyynto<PohjaTuonti>("/api/hitsausloki/ped-luokat/tuonti",
      { method: "POST", body: JSON.stringify({ kohde_id, sisalto, tiedosto, vahvista }) }),
  wpsPohjaUrl: () => "/api/wps/pohja",
  tuoWps: (sisalto: string, tiedosto: string, vahvista: boolean) =>
    pyynto<PohjaTuonti>("/api/wps/tuonti",
      { method: "POST", body: JSON.stringify({ sisalto, tiedosto, vahvista }) }),

  hitsauslokiTilanne: (kohde?: number) =>
    pyynto<HitsauslokiTilanne>(`/api/hitsausloki/tilanne${kohde ? `?kohde=${kohde}` : ""}`),
  /** T350 taso 1: henkilö × hitsausprosessi → milloin viimeksi. Pelkkä loki,
   *  ei pätevyysrekisteriä. Päivätön hitsi ei ole vanha hitsi (sääntö 61). */
  hitsauksenJatkuvuus: () => pyynto<{ hetki: string; rivit: JatkuvuusRivi[] }>("/api/hitsausloki/jatkuvuus"),

  /** Etenemä: dia-inch-kertymä viikoittain (S-käyrä). */
  etenema: (kohdeId?: number) =>
    pyynto<Etenema>(`/api/hitsausloki/etenema${kohdeId ? `?kohde_id=${kohdeId}` : ""}`),

  seuraavaHitsinumero: (linja_id: number, lehti?: string) =>
    pyynto<SeuraavaNumero>(`/api/hitsit/seuraava?${haku({
      linja_id, ...(lehti && { lehti }),
    })}`),

  /** 194 (päätös 62): «Ei toteudu» rivin toimintona = esitys, odottaa vahvistusta. */
  esitaEiToteudu: (hitsiId: number, peruste: string) =>
    pyynto<{ id: number; tila: string }>(`/api/hitsit/${hitsiId}/ei-toteudu-esitys`, { method: "POST", body: JSON.stringify({ peruste }) }),
  /** 194: vahvistaja hylkää esityksen perusteella. */
  hylkaaPunakyna: (esitysId: number, peruste: string, asema?: HyvaksyjanAsema, ulk: UlkopuolinenHyvaksyja = {}) =>
    pyynto<{ id: number }>(`/api/punakyna/${esitysId}/hylkays`, { method: "POST", body: JSON.stringify({ peruste, ...(asema ? { asema } : {}), ...ulk }) }),
  hitsitVahvistettavat: (kohdeId?: number) =>
    pyynto<Hitsi[]>("/api/hitsit?" + new URLSearchParams({ raja: "500", vain_vahvistettavat: "1", ...(kohdeId ? { kohde_id: String(kohdeId) } : {}) }).toString()),
  /** T471 (10.10.2026): «Kaikki linjat» rajataan valittuun kohteeseen (kohde_id) — ilman sitä lista näytti kaikki kohteet. */
  hitsit: (linja_id?: number, raja = 200, kohde_id?: number, vaihe?: "suunniteltu" | "hitsattu") =>
    pyynto<Hitsi[]>(`/api/hitsit?${haku({
      ...(linja_id !== undefined && { linja_id }), ...(kohde_id !== undefined && { kohde_id }), raja,
      ...(vaihe && { vaihe }),
    })}`),

  kirjaaHitsi: (syote: {
    linja_id: number; piirustuslehti?: string; hitsinumero: string;
    hitsityyppi?: Hitsityyppi;
    hitsausprosessi?: string; wps_id?: number | null;
    // Asentaja on hitsaajan pari (T331): oma kenttänsä, ei täyttöhitsaaja.
    juuri_hitsaaja_koodi?: string; asentaja_koodi?: string;
    lisaaine?: string; paiva?: string; dn?: number; tarra?: string;
    osat: { osalaji?: string; materiaali?: string; sulatus?: string; dn?: number }[];
    /** T323: sama tunniste palauttaa jo kirjatun hitsin (toisto) eikä tee toista. */
    kirjaustunniste?: string;
    /** 153: kentän perustelu alinumerolliselle lisäykselle. Pakollinen kun
     *  numerossa on alinumero — palvelin torjuu 400 `punakyna_peruste_puuttuu`. */
    punakyna_peruste?: string;
    /** 66 (Toni 5.10.2026): lisähitsi luodaan suunniteltuna — ilman päivää, lajia ja osia; kirjataan hitsatuksi myöhemmin. */
    suunniteltu?: boolean;
  }) => pyynto<{ id: number; taydennetty?: boolean; varoitukset?: string[]; toisto?: boolean; vaihe_lisatty?: "esivalmiste" | "asennus" }>("/api/hitsit", {
    method: "POST", body: JSON.stringify(syote),
  }),

  /** Excel-vienti. Palauttaa osoitteen; selain lataa tiedoston. */
  hitsauslokiVienti: (ehdot: {
    kohde_id?: number; linja_id?: number; alkaen?: string; asti?: string; kieli?: string;
  } = {}) => `/api/hitsausloki/vienti?${haku(vainAnnetut(ehdot))}`,

  /** Virallinen kaksirivinen loki. Jäljitettävyysdokumentti. */
  hitsauslokiVirallinen: (ehdot: { kohde_id?: number; linja_id?: number; kieli?: string } = {}) =>
    `/api/hitsausloki/virallinen?${haku(vainAnnetut(ehdot))}`,

  /** Muokkauspohja (hitsit + osat) Excelinä korjattavaksi. */
  hitsauslokiMuokkauspohja: (ehdot: { kohde_id?: number; linja_id?: number } = {}) =>
    `/api/hitsausloki/muokkauspohja?${haku(vainAnnetut(ehdot))}`,

  /** Muokkauspohjan tuonti. vahvista=false = kuivakäynti (erot), true = tallenna. */
  tuoHitsauslokiKorjaukset: (sisalto: string, vahvista: boolean) =>
    pyynto<{
      hitsit?: { rivit: number; tuntematon: number };
      osat?: { rivit: number; tuntematon: number };
      rivimuutoksia?: number; kenttamuutoksia?: number;
      esikatselu?: { taulu: string; id: number; kentta: string; vanha: string; uusi: string }[];
      paivitetty?: number;
    }>("/api/hitsausloki/tuonti", { method: "POST", body: JSON.stringify({ sisalto, vahvista }) }),

  /** 218 (Toni 10.10.2026): poisto on aina esitys + vahvistus. Suora DELETE vastaa 409 «poisto_esityksena». */
  esitaPoisto: (id: number, peruste: string) =>
    pyynto<{ id: number }>(`/api/hitsit/${id}/poistoesitys`, { method: "POST", body: JSON.stringify({ peruste }) }),
  vahvistaPoisto: (esitysId: number) =>
    pyynto<{ ok: true; poistettu: number }>(`/api/hitsipoistot/${esitysId}/vahvistus`, { method: "POST", body: "{}" }),
  hylkaaPoisto: (esitysId: number, peruste: string) =>
    pyynto<{ ok: true }>(`/api/hitsipoistot/${esitysId}/hylkays`, { method: "POST", body: JSON.stringify({ peruste }) }),

  /**
   * Hitsin korjaus (T309): vain annetut kentät muuttuvat. Syy pakollinen, kun virallisen lokin kenttä muuttuu
   * (palvelin kertoo: 400 korjaus_syy_puuttuu). Kenttätekijä korjaa omat ja parinsa hitsit 24 h kirjauksesta.
   */
  muokkaaHitsi: (id: number, syote: Partial<{
    piirustuslehti: string; hitsinumero: string; hitsityyppi: Hitsityyppi | null;
    hitsausprosessi: string; wps_id: number | null; lisaaine: string;
    paiva: string; dn: number;
    juuri_hitsaaja_koodi: string; asentaja_koodi: string;
    osat: { jarjestys: number; osalaji?: string; materiaali?: string; sulatus?: string }[];
    korjaus_syy: { laji: KorjauksenSyyLaji; teksti?: string };
  }>) => pyynto<{ id: number; korjaus_id: number | null; muuttui: string[] }>(`/api/hitsit/${id}`, {
    method: "PATCH", body: JSON.stringify(syote),
  }),

  /* ── NDT ja painekoe ── */
  /** VT-joukkokirjaus ja pikakirjaus (11.10.2026): jokaiselle hitsille oma rivi, yksi transaktio, ohitetut nimeltä. */
  kirjaaVt: (syote: {
    paiva?: string; tarkastaja?: string; raportti?: string; kirjaustunniste?: string;
    rivit: { hitsi_id: number; tulos: "hyvaksytty" | "hylatty"; vika?: string; syy?: string }[];
  }) => pyynto<{ kirjattu: number[]; hyvaksytty: number; hylatty: number; toisto: boolean;
                 ohitetut: { hitsi_id: number; hitsinumero: string | null; syy: "jo_vt" | "hitsaamaton" | "ei_loydy" }[] }>(
    "/api/hitsit/vt", { method: "POST", body: JSON.stringify(syote) }),
  lisaaTarkastus: (hitsiId: number, syote: {
    menetelma: string; paiva?: string; tulos?: string;
    tarkastaja?: string; raportti?: string; huomiot?: string; kirjaustunniste?: string;
    /** T525: hylätyn vikatyyppi ja kuvaus. */ vika?: string; syy?: string;
  }) => pyynto<{ id: number; toisto?: boolean }>(`/api/hitsit/${hitsiId}/tarkastukset`, {
    method: "POST", body: JSON.stringify(syote),
  }),
  paivitaTarkastus: (id: number, syote: {
    tulos?: string; paiva?: string; tarkastaja?: string; raportti?: string; huomiot?: string;
  }) => pyynto<{ id: number }>(`/api/tarkastukset/${id}`, {
    method: "PATCH", body: JSON.stringify(syote),
  }),
  poistaTarkastus: (id: number) =>
    pyynto<{ poistettu: number }>(`/api/tarkastukset/${id}`, { method: "DELETE" }),
  ndtKattavuus: (linjaId: number) =>
    pyynto<NdtKattavuus[]>(`/api/linjat/${linjaId}/ndt-kattavuus`),

  /* Painekoe on tarkastusrivi (136). Vapaateksti `linja.painekoe` siirrettiin
     rakenteiseen kokeeseen 30.9.2026 (T396, painekoesiirto) ja kenttä on
     piilotettu Selailusta; `paivitaLinjaPainekoe` poistettiin samalla. */
  painekoe: (linjaId: number) =>
    pyynto<{ painekoe: Painekoe | null }>(`/api/linjat/${linjaId}/painekoe`),
  tallennaPainekoe: (linjaId: number, muutos: { paiva?: string | null; tulos?: string;
      tarkastaja?: string; raportti?: string; huomiot?: string;
      /** 161: pakollinen jos NDT on kesken ja tulos on hyväksytty/hylätty (409 `ndt_kesken`). */
      ndt_ohitus_peruste?: string;
      /** T396: pöytäkirja samassa kutsussa (base64, data:-etuliite sallitaan). */
      liite?: { nimi: string; sisalto: string } }) =>
    pyynto<{ id: number; ndt_tila_kirjattaessa?: string | null; ndt_ohitus_peruste?: string | null }>(`/api/linjat/${linjaId}/painekoe`,
      { method: "POST", body: JSON.stringify(muutos) }),

  muokkaaLinja: (id: number, muutos: Partial<{ media: string; dn_nimellinen: number | null;
      putkiluokka: string; ped_luokka: string; hitsiluokka: string; revisio: string;
      eristys: string; isometri_olemassa: boolean; suunniteltuja_hitseja: number | null; painekoe: string }>) =>
    pyynto(`/api/linjat/${id}`, { method: "PATCH", body: JSON.stringify(muutos) }),

  /* ── punch (linjan tarkastuslöydökset) ── */
  punchit: (linjaId: number) => pyynto<Punch[]>(`/api/linjat/${linjaId}/punchit`),
  /** Laippaliitokset linjalta tai laitteelta (133). Rajaus tulee palvelimelta. */
  laippaliitokset: (q: { linja_id?: number; laite_id?: number }) =>
    pyynto<{ rivit: Laippaliitos[] }>(
      `/api/laippaliitokset?${new URLSearchParams(
        Object.entries(q).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)]),
      )}`).then((r) => r.rivit),
  kuittaaLaippaliitos: (id: number, b: { toteutunut_nm?: number; paiva?: string; huomiot?: string }) =>
    pyynto<{ kuitattu: { id: number; tunnus: string }; jo_kuitattu?: boolean }>(
      `/api/laippaliitokset/${id}/kuittaus`, { method: "POST", body: JSON.stringify(b) }),
  /** 264 (T636): liitoksen kiristykset, uusin ensin. */
  laippakiristykset: (liitosId: number) =>
    pyynto<{ rivit: Laippakiristys[] }>(`/api/laippaliitokset/${liitosId}/kiristykset`).then((r) => r.rivit),
  /** Kiristäjäehdokkaat työnjohdon lomakkeelle: laippatyö + voimassa oleva laippapätevyys, tunnukselle näkyvät. */
  laippaKiristajat: () =>
    pyynto<{ rivit: { id: number; nimi: string; lyhenne: string | null; voimassa_asti: string | null }[] }>(
      `/api/laippaliitokset/kiristajat`).then((r) => r.rivit),
  kirjaaLaippakiristys: (liitosId: number, b: LaippakiristysSyote) =>
    pyynto<{ kiristys: { id: number }; varoitukset: string[]; tila: LaippakiristysTila }>(
      `/api/laippaliitokset/${liitosId}/kiristykset`, { method: "POST", body: JSON.stringify(b) }),
  kasitteleLaippakiristys: (kiristysId: number, teko: "hyvaksy" | "palauta", peruste?: string) =>
    pyynto<{ kiristys: { id: number; tila: LaippakiristysTila }; varoitukset: string[] }>(
      `/api/laippaliitokset/kiristykset/${kiristysId}/${teko}`, { method: "POST", body: JSON.stringify(peruste ? { peruste } : {}) }),
  kaikkiPunchit: (o: { kohde?: number; vain_avoimet?: boolean; hoitaja?: HoitajaSuodatin } = {}) =>
    pyynto<PunchRivi[]>(`/api/punchit?${haku({
      ...(o.kohde !== undefined && { kohde: o.kohde }),
      ...(o.hoitaja && { hoitaja: o.hoitaja }),
      ...(o.vain_avoimet && { vain_avoimet: "1" }) })}`),
  lisaaPunch: (linjaId: number, syote: {
    // Estoluokka pakollinen (Toni 26.9.2026): palvelin hylkää puuttuvan 400:lla.
    vika: string; hitsi_id?: number | null; huomioinut?: string; huomio_pvm?: string; luokka: "A" | "B" | "C";
    kirjaustunniste?: string; vastuu?: string;
    /** 249: linjatarkastuksen kohta 7.1–7.9. */
    linjatarkastus_kohta?: string;
  } & PunchLuokat) => pyynto<{ id: number; toisto?: boolean }>(`/api/linjat/${linjaId}/punchit`, {
    method: "POST", body: JSON.stringify(syote),
  }),
  /** 262 (T653): vanhan punchin luokat tai ehdotuksen hyväksyntä. */
  punchinLuokat: (id: number, b: PunchLuokat) =>
    pyynto<{ ok: true }>(`/api/punchit/${id}/luokat`, { method: "POST", body: JSON.stringify(b) }),
  /** 262: isometrin positiosta «missä»-ehdotus osaluettelon nimikkeestä. */
  positioluokka: (linjaId: number, positio: string) =>
    pyynto<{ nimike: string | null; missa: string | null }>(`/api/linjat/${linjaId}/positioluokka?positio=${encodeURIComponent(positio)}`),
  /** 262: «Muu»-valintojen lisätiedot raportiksi. */
  punchMuut: (kohde?: number) =>
    pyynto<{ luokka_missa: string; luokka_vika: string; lisatieto: string; n: number; viimeisin: string | null }[]>(
      `/api/punchit/luokitus/muut${kohde ? `?kohde=${kohde}` : ""}`),
  kuittaaPunch: (id: number) =>
    pyynto<{ id: number }>(`/api/punchit/${id}/kuittaa`, { method: "POST" }),
  muokkaaPunch: (id: number, muutokset: Partial<{
    vika: string; huomioinut: string | null; huomio_pvm: string | null;
    kuitannut: string | null; kuittaus_pvm: string | null; huomiot: string | null; luokka: "A" | "B" | "C"; vastuu: string | null;
  }>) => pyynto<{ id: number }>(`/api/punchit/${id}`, {
    method: "PATCH", body: JSON.stringify(muutokset),
  }),
  poistaPunch: (id: number) =>
    pyynto<{ poistettu: number }>(`/api/punchit/${id}`, { method: "DELETE" }),

  /* ── pätevyydet ───────────────────────────────────────── */

  patevyystyypit: () => pyynto<Patevyystyyppi[]>("/api/patevyystyypit"),

  luoPatevyystyyppi: (syote: { nimi: string; myontaja?: string; voimassa_kk?: number }) =>
    pyynto<Patevyystyyppi>("/api/patevyystyypit", {
      method: "POST", body: JSON.stringify(syote),
    }),

  patevyydet: (henkilo_id?: number) =>
    pyynto<PatevyysRivi[]>(
      `/api/patevyydet${henkilo_id !== undefined ? `?${haku({ henkilo_id })}` : ""}`
    ),

  kirjaaPatevyys: (syote: {
    henkilo_id: number; patevyystyyppi_id: number; taso?: string;
    myonnetty?: string; voimassa_asti?: string; varmennettu?: boolean;
  }) => pyynto<{ id: number; voimassa_asti: string | null }>("/api/patevyydet", {
    method: "POST", body: JSON.stringify(syote),
  }),

  poistaPatevyys: (id: number) =>
    pyynto<{ poistettu: number }>(`/api/patevyydet/${id}`, { method: "DELETE" }),

  patevyysriski: (alku: string, loppu: string) =>
    pyynto<PatevyysRiskiRivi[]>(`/api/patevyysriski?${haku({ alku, loppu })}`),

  /* ── poissaolot ───────────────────────────────────────── */

  poissaololajit: () =>
    pyynto<Poissaololaji[]>("/api/poissaololajit"),

  poissaolot: (alku: string, loppu: string, henkilo_id?: number) =>
    pyynto<Poissaolo[]>(`/api/poissaolot?${haku({
      alku, loppu, ...(henkilo_id !== undefined && { henkilo_id }),
    })}`),

  merkitsePoissaolo: (syote: {
    henkilo_id: number; poissaololaji_id: number;
    alkaa: string; paattyy: string; huomiot?: string;
  }) => pyynto<{ id: number }>("/api/poissaolot", {
    method: "POST", body: JSON.stringify(syote),
  }),

  poistaPoissaolo: (id: number) =>
    pyynto<{ poistettu: number }>(`/api/poissaolot/${id}`, { method: "DELETE" }),

  /* ── henkilön lisäys ──────────────────────────────────── */

  luoHenkilo: (syote: {
    etunimi: string; sukunimi: string; nimike?: string;
    tyosuhde?: "oma" | "vuokra" | "alihankinta"; nimeamaton?: boolean;
    tunnit_per_paiva?: number; maksimi_per_paiva?: number;
    tyopaivat?: number[]; osaamisluokka_idt?: number[];
    /** Valinnainen: annetaan itse, jos johdettu lyhenne on varattu. */
    lyhenne?: string;
  }) => pyynto<{ id: number; lyhenne: string }>("/api/resurssit", {
    method: "POST", body: JSON.stringify(syote),
  }),

  passivoiHenkilo: (id: number) =>
    pyynto<{ passivoitu: true }>(`/api/resurssit/${id}`, { method: "DELETE" }),

  /* ── Laiteasennus (V1) ──────────────────────────────────── */

  /* ── IV-asennus (189) ── */
  // 254 (T625 vaihe 1): kohteen portit (TG).
  tgPortit: (kohde_id: number) => pyynto<TgPortit>(`/api/suunnittelu/portit?kohde_id=${kohde_id}`),
  tgLuoPortti: (syote: { kohde_id: number; tunnus: string; nimi: string; maaraaika: string }) =>
    pyynto<TgPortti>("/api/suunnittelu/portit", { method: "POST", body: JSON.stringify(syote) }),
  tgHyvaksy: (id: number, peruste: string | null) =>
    pyynto<TgPortti>(`/api/suunnittelu/portit/${id}/hyvaksynta`, { method: "POST", body: JSON.stringify({ peruste }) }),
  tgLisaaTehtava: (porttiId: number, teksti: string) =>
    pyynto<TgTehtava>(`/api/suunnittelu/portit/${porttiId}/tehtavat`, { method: "POST", body: JSON.stringify({ teksti }) }),
  tgKuittaa: (tehtavaId: number, tehty: boolean) =>
    pyynto<TgTehtava>(`/api/suunnittelu/tehtavat/${tehtavaId}/kuittaus`, { method: "POST", body: JSON.stringify({ tehty }) }),
  tgSiirra: (id: number, maaraaika: string, peruste: string | null) =>
    pyynto<TgPortti>(`/api/suunnittelu/portit/${id}/maaraaika`, { method: "POST", body: JSON.stringify({ maaraaika, peruste }) }),
  ivRakenne: (kohde_id: number) => pyynto<IvRakenne>(`/api/iv/rakenne?kohde_id=${kohde_id}`),
  ivMittalaitteet: () => pyynto<IvMittalaite[]>("/api/iv/mittalaitteet"),
  ivTiiviysluokat: () => pyynto<IvTiiviysluokka[]>("/api/iv/tiiviysluokat"),
  ivLaskutus: (kohde_id: number) => pyynto<IvLaskutusRivi[]>(`/api/iv/laskutus?kohde_id=${kohde_id}`),
  luoIvJarjestelma: (syote: { kohde_id: number; tunnus: string; nimi?: string | null; palvelualue?: string | null }) =>
    pyynto<{ id: number }>("/api/iv/jarjestelmat", { method: "POST", body: JSON.stringify(syote) }),
  luoKanavaosuus: (syote: { jarjestelma_id: number; tunnus: string; kuvaus?: string | null; pinta_ala_m2?: number | null; pituus_m?: number | null; vaadittu_luokka?: string | null }) =>
    pyynto<{ id: number }>("/api/iv/kanavaosuudet", { method: "POST", body: JSON.stringify(syote) }),
  luoPaate: (syote: { kanavaosuus_id: number; tunnus: string; tyyppi?: string | null; suunniteltu_ls?: number | null; toleranssi_pros?: number | null }) =>
    pyynto<{ id: number }>("/api/iv/paatteet", { method: "POST", body: JSON.stringify(syote) }),
  kuittaaKanavaosuus: (id: number, laji: "asennettu" | "p1", peru = false) =>
    pyynto<{ ok: true }>(`/api/iv/kanavaosuudet/${id}/kuittaus`, { method: "POST", body: JSON.stringify({ laji, peru }) }),
  kirjaaTiiviysmittaus: (syote: { kanavaosuus_id: number; paiva?: string; koepaine_pa: number; vuotoilmavirta_ls: number; mittalaite_id: number; tarkastaja?: string | null; raportti?: string | null }) =>
    pyynto<IvMittausTulos>("/api/iv/tiiviysmittaukset", { method: "POST", body: JSON.stringify(syote) }),
  kirjaaIlmavirtamittaus: (syote: { paate_id: number; paiva?: string; mitattu_ls: number; saatoasento?: string | null; mittalaite_id: number; tarkastaja?: string | null }) =>
    pyynto<IvMittausTulos>("/api/iv/ilmavirtamittaukset", { method: "POST", body: JSON.stringify(syote) }),
  laitteet: (kohde_id?: number) =>
    pyynto<LaiteRivi[]>(`/api/laitteet${kohde_id ? `?kohde_id=${kohde_id}` : ""}`),

  laite: (id: number) => pyynto<LaiteTila>(`/api/laitteet/${id}`),

  /**
   * Laitteen luonti.
   *
   * Reitti oli olemassa 26.8.2026 asti ilman että selain osasi kutsua
   * sitä: moduulin sai päälle mutta ensimmäisen laitteen sai kantaan
   * vain SQL:llä. Ominaisuus jonka voi ottaa käyttöön vain
   * tietokantayhteydellä ei ole otettavissa käyttöön.
   */
  luoLaite: (syote: {
    kohde_id: number; valmistaja: string; malli: string;
    sarjanumero?: string | null; tunnus?: string | null;
    sijainti?: string | null; tyonjohtaja_id?: number | null;
    huomiot?: string | null;
  }) => pyynto<{ id: number }>("/api/laitteet", {
    method: "POST", body: JSON.stringify(syote),
  }),

  /** Ketjun kiinnitys laitteeseen. Kutakin lajia täsmälleen yksi. */
  kiinnitaVaiheketju: (laiteId: number, laji: "asennus" | "kayttoonotto", ketju_id: number) =>
    pyynto<{ ok: true }>(`/api/laitteet/${laiteId}/vaiheketju`, {
      method: "PUT", body: JSON.stringify({ laji, ketju_id }),
    }),

  vaiheketjut: () => pyynto<Vaiheketju[]>("/api/vaiheketjut"),

  /** Vaiheen kuittaus laitteelle. */
  kuittaaVaihe: (laiteId: number, vaiheId: number, huomiot?: string) =>
    pyynto<{ id: number }>(`/api/laitteet/${laiteId}/vaiheet/${vaiheId}/kuittaus`, {
      method: "POST", body: JSON.stringify(huomiot ? { huomiot } : {}),
    }),

  peruVaihekuittaus: (laiteId: number, vaiheId: number) =>
    pyynto<{ ok: true }>(`/api/laitteet/${laiteId}/vaiheet/${vaiheId}/kuittaus`,
      { method: "DELETE" }),

  /** Asentajan polku: seuraava kuittaamaton liitos + edistymä. */
  laiteSeuraava: (id: number) => pyynto<SeuraavaLiitos>(`/api/laitteet/${id}/seuraava`),

  /** Yksi painallus: kuittaa ja saa seuraavan samassa vastauksessa. */
  kuittaaLiitos: (id: number, syote?: { paiva?: string; huomiot?: string }) =>
    pyynto<SeuraavaLiitos & { kuitattu: { id: number; tunnus: string }; jo_kuitattu?: boolean }>(
      `/api/liitokset/${id}/kuittaus`,
      { method: "POST", body: JSON.stringify(syote ?? {}) }),

  /**
   * Kuittauksen peruminen.
   *
   * Tämä ei ole lisäominaisuus vaan ehto sille että kirjausnäkymässä
   * EI ole vahvistusikkunaa: perustelu "väärä kuittaus perutaan"
   * pätee vain jos peruminen on olemassa ja yhtä näkyvä kuin
   * kuittaus. Ilman tätä turvaverkko korvattiin lupauksella.
   */
  peruLiitosKuittaus: (id: number) =>
    pyynto<{ id: number }>(`/api/liitokset/${id}`, {
      method: "PATCH", body: JSON.stringify({ kuitattu: false }),
    }),

  /** Liitoslista Excelinä. Työnjohdon polku; asentaja ei avaa Exceliä. */
  liitospohjaUrl: (laiteId: number) => `/api/laitteet/${laiteId}/muokkauspohja`,

  /** Liitoslistan tuonti. vahvista=false = kuivakäynti, true = tallenna. */
  tuoLiitokset: (laiteId: number, sisalto: string, vahvista: boolean) =>
    pyynto<{
      rivit?: number; tuntematon?: number;
      rivimuutoksia?: number; kenttamuutoksia?: number; uusia?: number;
      esikatselu?: { taulu: string; id: number; kentta: string; vanha: string; uusi: string }[];
      uudet_esikatselu?: { tunnus: string; kuvaus: string | null; piiri: string;
                           vaadittu_nm: number | null; jarjestys: number }[];
      paivitetty?: number; luotu?: number;
    }>(`/api/laitteet/${laiteId}/tuonti`, {
      method: "POST", body: JSON.stringify({ sisalto, vahvista }),
    }),

  luovutusaineisto: (id: number) => pyynto<Luovutus>(`/api/laitteet/${id}/luovutus`),
};

export interface LaiteRivi {
  id: number; kohde_id: number; kohde: string;
  /** 59: kohteen tunnus otsikkoon ja linkkiin («Kohde N-2404 › CDU-1»). */
  kohde_tunnus: string;
  valmistaja: string; malli: string;
  sarjanumero: string | null; tunnus: string | null; sijainti: string | null;
  tyonjohtaja: string | null;
  liitoksia: number; liitoksia_kuitattu: number;
  vaiheita: number; vaiheita_kuitattu: number;
}

export interface Liitos {
  id: number; tunnus: string; kuvaus: string | null;
  piiri: "sekundaari" | "primaari";
  vaadittu_nm: string | number | null;
  jarjestys: number;
  kuitattu: string | null; paiva: string | null;
  huomiot: string | null; kuittaaja: string | null;
}

export interface LaiteVaihe {
  id: number; ketju_id: number; tunnus: string; nimi: string;
  jarjestys: number; laji: "asennus" | "kayttoonotto";
  kuitattu: string | null; paiva: string | null; kuittaaja: string | null;
}

export interface LaiteTila {
  laite: LaiteRivi & { huomiot: string | null };
  ketjut: { laji: string; ketju_id: number; tunnus: string; nimi: string; lahde: string }[];
  vaiheet: LaiteVaihe[];
  liitokset: Liitos[];
  avoimia_liitoksia: number;
  /** Vaihe kuitattu valmiiksi vaikka liitoksia on auki. Merkintä, ei este. */
  ristiriidat: { vaihe: string; syy: string }[];
}

export interface Vaiheketju {
  id: number;
  tunnus: string;
  nimi: string;
  laji: "asennus" | "kayttoonotto";
  /** ehdotus = Sampon siemen, ei kenenkään vahvistama. */
  lahde: "ehdotus" | "asiakas" | "manuaali";
  kuvaus: string | null;
  aktiivinen: boolean;
  vaiheet: { id: number; tunnus: string; nimi: string; jarjestys: number }[];
}

export interface SeuraavaLiitos {
  laite?: { id: number; valmistaja: string; malli: string; tunnus: string | null; kohde: string };
  seuraava: Liitos | null;
  yhteensa: number;
  /** Kuitattujen LUKUMÄÄRÄ. `kuitattu` on kuitattu liitos — eri asia. */
  kuitattuja: number;
  jaljella: number;
}

export interface Luovutus {
  laite: { id: number; valmistaja: string; malli: string; sarjanumero: string | null;
           tunnus: string | null; kohde: string };
  piiri: "sekundaari";
  kuittaamattomat_liitokset: { tunnus: string; kuvaus: string | null; vaadittu_nm: string | number | null }[];
  kuittaamattomat_vaiheet: { laji: string; tunnus: string; nimi: string }[];
  ketju_kiinnittamatta: string[];
  ristiriidat: { laji: string; nimi: string; paiva: string | null }[];
  valmis: boolean;
  hetki: string;
}

export type MitoitusTila = "riittää" | "täpärästi" | "vuokraamalla" | "ei riitä";

export interface MitoitusRivi {
  viikko_alku: string;
  viikko: number;
  tyopaivia: number;
  tarve: number;
  vapaa_oma: number;
  vapaa_vuokra: number;
  alihankintatarve: number;
  vaje: number;
  /** Vapaita omia henkilöitä urakan jälkeen. 0 = ei kestä yhtään poissaoloa. */
  puskuria: number;
  /** Montako vuokrapaikkaa tarve söisi. Samat paikat ovat myös muiden käytössä. */
  vuokrapaikkoja: number;
  tila: MitoitusTila;
}

export interface MitoitusOsaaminenTulos {
  osaamisluokka_id: number;
  nimi: string;
  henkiloa: number;
  /** Vaatimukset pyynnön järjestyksessä; `puuttuu` = osaajia joilta juuri tämä puuttuu. */
  vaatimukset: { id: number; nimi: string; puuttuu: number }[];
  /** Osaajia yhteensä (nimetyt), ja niistä ne jotka täyttävät JOKAISEN vaatimuksen koko jakson. */
  osaajia: number;
  tayttaa_kaikki: number;
  /** Osaajia joilta puuttuu vähintään yksi vaatimus (suoritettavissa/kirjaamatta). */
  ilman_patevyytta: number;
  rivit: MitoitusRivi[];
}
export interface MitoitusMonta {
  alku: string;
  loppu: string;
  osaamiset: MitoitusOsaaminenTulos[];
}

export interface LahdeAukkoRivi {
  id: number; tunnus: string; media: string | null; kohde: string; kohde_id: number;
  isometri_puuttuu: boolean; mto_puuttuu: boolean; ei_lahtotietoa: boolean;
  suunnitelma_puuttuu: boolean; dn_epaselva: boolean; dn_ristiriita: boolean; pid_dn: number | null;
  nimi_vs_kanta: boolean; nimen_koko: number | null;
  tehty_ilman_suunnitelmaa: boolean;
  /** T655: isometripuute kuitattu korvaavalla aineistolla (laji); aukko pysyy, rivi saa merkinnän. null = ei korvattu. */
  isometri_korvattu: Aineistolaji | null;
}
export interface PuuttuvaPidLinja { tunnus: string; media: string | null; dn: number | null; kohde: string }
export interface LahdeAukot {
  /** Palvelimen hetki ISO-muodossa — tilanneleiman lähde. */
  hetki: string;
  onPid: boolean;
  summary: { linjoja_aukoilla: number; ei_lahtotietoa: number; isometri_puuttuu: number; mto_puuttuu: number;
    dn_ristiriita: number; dn_epaselva: number; nimi_vs_kanta: number;
    tehty_ilman_suunnitelmaa: number; puuttuu_pid: number };
  linjat: LahdeAukkoRivi[];
  puuttuvat: PuuttuvaPidLinja[];
}
export interface DokVelkaRivi {
  id: number; tunnus: string; media: string | null; kohde: string; kohde_id: number;
  hitsattu: number; hitseja: number;
  /** G4: seitsemän arvoa, ei totuusarvo. «ei_asetettu» on tieto jota ei ole,
   *  «ei_vaadita» on päätös — ne eivät saa näyttää samalta. */
  ndt_tila: "valmis" | "kesken" | "aloittamatta"
    | "ei_vaadita" | "luokalle_ei_kirjattu" | "ei_asetettu" | "luokka_puuttuu";
  /** Linjan PED-luokka, jotta näkymä voi sanoa MIKÄ luokka on kirjaamatta. */
  ped_luokka: string | null;
  /** T514 ehto b: linjan K (masterin Rtg %), kun NDT-tila luettiin siitä; null = PED-luokan vaatimus. Puuttuu vanhalta palvelimelta. */
  ndt_k?: number | null;
  /** 137: kuka kirjasi luokan vaatimuksen ja milloin («Nimi · 19.09.2026»).
   *  null = riviä ei ole; «ei tiedossa» = rivi on, tekijä ei. */
  ndt_paatos: string | null;
  painekoe_on: boolean; painekoe_kesken: boolean; walkdown_tila: string | null;
  avoimia_puncheja: number; loki: boolean;
  /** Tilaajan kannakointiprosentit (111), uusin tilannekuva; null = ei tiedossa. */
  primaari_pros: number | null; sekundaari_pros: number | null; asennus_pros: number | null;
  kannakointi_pvm: string | null; kannakointi_lahde: string | null;
  /** null = ei mitattavissa: linjalla ei ole yhtään hitsattua hitsiä. */
  valmius: number | null;
  aloittamatta: boolean;
  /** Joukkojäsenyys palvelimelta (`dokumentointivelka.ts
   *  ulottuvuudet()`): sama lista josta Hub laskee lukunsa. Näkymä
   *  suodattaa tällä eikä omalla säännöllään. */
  ulottuvuudet: Ulottuvuus[];
  /** T397: ensimmäinen järjestyksessä täyttämätön vaihe. Jokainen linja on täsmälleen yhdessä. */
  seuraava_vaihe: Vaihe;
  /** T683: hitsit ilman pätevyyttä hitsauspäivänä (pysäyttää) ja pätevyys ei tiedossa (hidastaa). Puuttuu = ei mitattu. */
  patevyys_estaa?: number; patevyys_ei_tiedossa?: number;
}
/** T683: dokumentointivelka hitsaajittain (vain hitsaajat, joilla on hitsejä ilman voimassa olevaa pätevyyttä). */
export interface PatevyysvelkaRivi {
  henkilo_id: number | null; koodi: string | null; estaa: number; ei_tiedossa: number;
  ei: number; vanhentunut: number; myonnetty_myohemmin: number; varmentamaton: number; tuntematon: number;
  linjoja: number; linjoja_estaa: number;
}
/** Punch on suljettu kun sillä on kuittaaja TAI kuittauspäivä — sama ehto
 *  kuin API:n PUNCH_AVOIN (routes.ts). Pelkkä nimi ei ole tila: tuodulla
 *  sulkemisella voi olla päivä ilman leimaa. */
export const punchSuljettu = (p: { kuitannut: string | null; kuittaus_pvm: string | null }): boolean => !!(p.kuitannut || p.kuittaus_pvm);

export const ULOTTUVUUDET = [
  "aloittamatta", "aloitettu", "loki", "ndt", "painekoe", "walkdown", "ei_punchia",
  // D2 (18.9.2026): kesken on oma joukkonsa, ja «ei vaatimusta» on mittaamaton.
  "ndt_kesken", "ndt_ei_vaadita", "ndt_luokalle_ei_kirjattu", "ndt_ei_asetettu",
  "ndt_luokka_puuttuu", "painekoe_kesken",
  // Ennusteen jäljellä-joukot kaikista linjoista (api: dokumentointivelka.ts).
  "hitsaus_jaljella", "ndt_jaljella", "painekoe_jaljella", "walkdown_jaljella", "punchit_jaljella",
  "primaari_jaljella", "sekundaari_jaljella", "kannakointi_ei_tiedossa", "kannakointi_ristiriita",
  // 177 (Toni 26.9.2026): poikkeamalla hyväksytty walkdown ja luovutus ilman allekirjoitettua isometriä.
  "walkdown_poikkeama", "luovutusliite_puuttuu",
  // VT-joukkokirjaus (11.10.2026): hylätty VT on avoin.
  "ndt_hylatty",
  // T655 (25.10.2026): isometri korvattu korvaavalla aineistolla — oma luku, ei estä luovutusta.
  "isometri_korvattu",
  // T677 B (27.10.2026): painekoe kirjattu ennen kuin NDT on valmis — järjestysrike, laatuhavainto.
  "painekoe_ennen_ndt",
  // T683 (28.10.2026): hitsaajan pätevyys hitsauspäivänä — EI/vanhentunut pysäyttää, ei tiedossa hidastaa.
  "patevyys_estaa", "patevyys_ei_tiedossa",
] as const;
export type Ulottuvuus = (typeof ULOTTUVUUDET)[number];

/** T397 (Toni 30.9.2026): seuraava vaihe, kaksoiskappale api/src/dokumentointivelka.ts:stä (web ei voi importata rootDirin yli). */
export const VAIHEET = ["hitsaus", "punchit", "linjatarkastus", "ndt", "painekoe", "luovutus", "valmis"] as const;
export type Vaihe = (typeof VAIHEET)[number];
export const VAIHEEN_NIMI: Record<Vaihe, string> = {
  hitsaus: "hitsaus kesken", punchit: "avoimia puncheja", linjatarkastus: "linjatarkastus tekemättä (tai poikkeamalla)",
  ndt: "NDT tekemättä", painekoe: "painekoe tekemättä", luovutus: "luovutus tekemättä", valmis: "luovutettu",
};

/** Näkymän asettelu (`/api/nakyma/:nakyma`, api/src/nakyma.ts). */
export interface Asettelu { piilotetut: string[]; jarjestys: string[] }
export interface NakymaKortti {
  avain: string; nimi: string; moduuli: string | null; leveys: "sarake" | "taysi";
  oletusjarjestys: number; piilotettu: boolean;
}
export interface Nakyma {
  nakyma: string;
  /** Ratkaistu järjestys, moduulisuodatettu; piilotetut mukana lipulla. */
  kortit: NakymaKortti[];
  taso: "oma" | "rooli" | "oletus";
  /** Tallennettu asettelu piilotti kaiken → palvelin palautti oletuksen ja sanoo sen. */
  tyhja: boolean;
  rooli: string | null;
  oma_tallennettu: boolean;
  rooli_tallennettu: boolean;
  /** Vain pääkäyttäjälle. */
  roolit?: { tunnus: string; nimi: string; asetettu: boolean }[];
}

/** Valmistumisennuste (`GET /api/ennuste/:kohde_id`, api/src/ennuste.ts). */
export interface EnnusteTahti {
  /** Ikkuna leikkauksen jälkeen: jos tuonti ei kata ikkunan loppua, ikkuna päättyy viimeisimpään tuontiin. */
  ikkuna_alku: string; ikkuna_loppu: string; arkipaivia: number;
  tuonti_kattaa: string | null; leikattu: boolean;
  tayttyneita: number; paivaton: number; viimeisin: string | null;
  tahti: number | null; hitain5: number | null; nopein5: number | null;
  /** Suurimman yksittäisen lähdepäivän osuus ikkunan täyttymisistä (0–1) ja se päivä. */
  suurin_osuus: number | null; suurin_paiva: string | null;
}
export interface EnnusteVaihe {
  vaihe: "hitsaus" | "ndt" | "painekoe" | "walkdown" | "punchit";
  nimi: string; joukko: string; jaljella: number; tahti: EnnusteTahti;
  mitattavissa: boolean; syy: string | null;
  /** Sama syy lyhyenä otsikkoriville; null kun mitattavissa. */
  syy_lyhyt: string | null;
  /** 10.10.2026: syy koneluettavana — Hub kokoaa otsikkorivin käyttäjän kielellä (puuttuu vanhalta palvelimelta). */
  syy_koodi?: "tuonti_paattyy" | "ei_ikkunaa" | "aineisto_paattyy" | "ei_tayttymia" | "paiva_puuttuu" | "yksi_paiva" | null;
  syy_arvot?: Record<string, string | number> | null;
  arkipaivia: number | null; aikaisin: string | null; myohaisin: string | null;
  saasto_plus1: number | null;
  muuttaisi: string;
  ketju_aikaisin: string | null; ketju_myohaisin: string | null;
  /** Vaiheen täyttäneitä linjoja joilta täyttymispäivä puuttuu (eivät ole tahdissa). */
  paivaton: number;
}
export interface Ennuste {
  hetki: string;
  tanaan: string;
  kalenteri: { ikkuna_alku: string; ikkuna_loppu: string; arkipaivia: number; tulevia_arkipaivia: number };
  nimittaja: {
    linjoja: number; aloitettu: number; aloittamatta: number; loki_taysi: number;
    /** Suunniteltuja (hitsaamattomia) hitsirivejä koko kohteessa ja niiden
     *  mediaani aloittamatonta linjaa kohti — jos mediaani on 1, isometrin
     *  hitsimäärää ei ole tuotu ja hitsauksen jäljellä-luku on alaraja. */
    suunniteltuja_hitseja: number;
    suunniteltu_mediaani_aloittamaton: number | null;
    /** `linja.tila`-jakauma: rajaus (kesken/valmis/ei tehdä) luetaan tästä, jos se on kannassa. */
    tila: { tila: string; n: number }[];
    /** Vaiheen täyttäneitä linjoja joita Hubin «aloitetuista» ei laske. */
    aloittamattomilla: { ndt: number; painekoe: number; walkdown: number };
    avoimet_punchit: { luokka: string | null; n: number }[];
  };
  vaiheet: EnnusteVaihe[];
  yhteensa: { aikaisin: string | null; myohaisin: string | null } | null;
  katkeaa: string[];
  /** Kannakointi tilaajan seurannasta (taso 1): tilannekuva, ei tahti. */
  kannakointi: {
    lahde: string | null; tilannekuva: string | null; tilannekuvia: number;
    tahti_mitattavissa: boolean; linjoja_kahdella_tilannekuvalla: number;
    primaari:   { tiedossa: number; jaljella: number; keskiarvo: number | null };
    sekundaari: { tiedossa: number; jaljella: number; keskiarvo: number | null };
    ei_tiedossa: number; ristiriita: number;
  };
}
export interface Dokumentointivelka {
  hetki: string;
  summary: {
    linjoja: number; aloittamatta: number; aloitettuja: number; valmiita: number;
    /** Keskiarvo VAIN aloitetuista. null = yhtään ei ole aloitettu. */
    keskivalmius_aloitetut: number | null;
    loki_valmis: number; ndt: number; painekoe: number; walkdown: number;
  };
  linjat: DokVelkaRivi[];
  /** T683: puuttuu kun pätevyyttä ei mitata (moduuli pois tai ei hitsauspätevyystyyppiä). */
  patevyys?: PatevyysvelkaRivi[];
}

export interface ProjektiHub {
  kohde: {
    id: number; tunnus: string; nimi: string; asiakas: string | null;
    tila: string; laji: string; alkaa: string | null; paattyy: string | null;
    tyonumero: string | null;
  } | null;
  /** 188 + päätös 51: kohteen työlaji ohjaa mitkä moduulilohkot koskevat kohdetta. */
  tyolaji?: KohdeTyolaji;
  /** Päätös 51: laiteasennuksen rivi — vain kun moduuli on päällä ja koskee kohdetta (työlaji tai laitteita > 0). */
  laite?: { laitteita: number; vaiheita: number; vaiheita_kuitattu: number; liitoksia: number; liitoksia_kuitattu: number };
  /** Päätös 51: IV-asennuksen rivi (189) — järjestelmät · kanavaosuudet tiiviysmitattu · päätteet säädetty · f_iv_valmius. */
  iv?: { jarjestelmia: number; osuuksia: number; osuuksia_mitattu: number; osuuksia_ok: number; paatteita: number;
         paatteita_mitattu: number; paatteita_ok: number; valmius: number | null };
  loki?: {
    linjoja: number; hitseja: number; tehty: number; suunniteltu: number; hylatty: number;
    /** Vaiheakseli: hitsattu + suunniteltu = hitseja. `tehty` on
     *  paivaakseli (paiva IS NOT NULL) eika summaudu naiden kanssa. */
    hitsattu: number;
    /** Hitsattu mutta paivaton — se joukko joka putoaa akselien valiin. */
    hitsattu_paivaton: number;
    dia_inch_tehty: number; dia_inch_tavoite: number;
    avoimia_puncheja: number; ndt_hitseja: number;
    /** 194 (62 kohta 5): vahvistamattomia muutoksia — sama lauseke kuin listan suodattimessa. */
    vahvistusta_odottaa: number;
  };
  miehitys?: { resurssi_id: number; nimi: string; lyhenne: string | null; tunnit: number }[];
  tarjoukset?: { id: number; hinta: number }[];
  tekijat?: { resurssi_id: number; nimi: string; lyhenne: string | null; paketteja: number;
               /** Tyosuhteen paattymispaiva. Paattynyt tekija NAKYY
                *  kortilla merkittyna eika suodatu pois. */
               paatti: string | null }[];
  /** Alarivi. Jokainen lohko puuttuu kokonaan jos sen moduulia ei ole. */
  puutteet?: { tyyppi: string; vaikutus: string; n: number }[];
  /** T567: jono — pysäyttävät riveinä (enintään 200, `yhteensa` kertoo koko määrän), muut ryhminä; ulkoinen vastuu `odottaa`-nimellä. */
  puutejono?: {
    pysayttavat: { id: number; tyyppi: string; saanto: string; kuvaus: string; vastuu: string | null; vastuu_ulkoinen: boolean;
      ika_vrk: number; linja_tunnus: string | null; kohde_taulu: string; yhteensa: number }[];
    /** Toni 25.10.2026: pysäyttävät ryhminä (n = koko ryhmä). Puuttuu vanhalta palvelimelta — selain näyttää silloin rivit kuten ennen. */
    pysayttavat_ryhmat?: { tyyppi: string; saanto: string; vastuu_ulkoinen: boolean; odottaa: string | null; n: number; vanhin_vrk: number }[];
    ryhmat: { tyyppi: string; saanto: string; vaikutus: "hidastaa" | "ei_esta"; vastuu_ulkoinen: boolean; odottaa: string | null; n: number; vanhin_vrk: number }[];
  };
  valmius?: {
    linjoja: number; aloittamatta: number; aloitettuja: number;
    valmiita: number; keskivalmius_aloitetut: number | null;
    dimensiot: { loki: number; ndt: number; painekoe: number;
                 walkdown: number; ei_punchia: number };
    /** T677 B: järjestysrike — painekoe kirjattu ennen kuin NDT on valmis. Puuttuu vanhalta palvelimelta. */
    painekoe_ennen_ndt?: number;
    /** T397: linjat seuraavan vaiheen mukaan; summa = linjoja. */
    vaiheet: Record<Vaihe, number>;
  };
  kirjaukset?: { laji: string; pvm: string; linja: string; teksti: string; kuka: string }[];
}

export interface JohdettuTunti {
  henkilo_id: number;
  nimi: string;
  lyhenne: string | null;
  hitseja: number;
  johdetut_h: number;
  dia_inch: number;
  ilman_normia: number;
  kirjatut_h: number;
}

export interface TuntiRivi {
  id: number;
  henkilo_id: number;
  nimi: string;
  lyhenne: string | null;
  kohde_id: number | null;
  kohde: string | null;
  tyopaketti_id: number | null;
  tp_nro: number | null;
  tp_nimi: string | null;
  tehtava: string | null;
  pvm: string;
  tunnit: number;
  km: number;
  paivaraha: "ei" | "osa" | "koko";
  huomiot: string | null;
  tila: string;
  /** Toni 16.10.2026 klo 04.25: null ilman palkkaoikeutta. */
  km_korvaus: number | null;
  paivaraha_eur: number | null;
}

export interface TuntiYhteenveto {
  henkilo_id: number;
  nimi: string;
  lyhenne: string | null;
  tunnit: number;
  km: number;
  /** Toni 16.10.2026 klo 04.25: null ilman palkkaoikeutta. */
  km_korvaus: number | null;
  paivaraha_eur: number | null;
  rivit: number;
}

export interface TuntiAsetukset {
  km_korvaus: number;
  paivaraha_koko: number;
  paivaraha_osa: number;
}

export interface OmatTiedot {
  henkilo: {
    id: number; nimi: string; lyhenne: string | null; nimike: string | null;
    puhelin: string | null; sahkoposti: string | null; tyosuhde: string | null;
    osaamiset: string[];
  } | null;
  patevyydet: {
    id: number; patevyys: string; taso: string | null; voimassa_asti: string | null;
    todistuksia: number; tila: string;
  }[];
}

/** 177 (Toni 26.9.2026): linjatarkastuksen hyväksyntä = luovutus asiakkaalle. */
export interface Luovutustila {
  kohde_id: number;
  /** 222 (T466): asiakkaan edustaja pakollinen tämän kohteen linjatarkastuksessa (kohteen asetus, oletus true). */
  edustaja_pakollinen?: boolean;
  /** 203 (Toni 7.10.2026 «2. B»): hitsattuja hitsejä ilman hyväksyttyä VT:tä; merkki, ei este. */
  vt_puuttuu?: number | null; vt_ilman_tulosta?: number | null;
  avoimia_a: number;
  luokittelemattomia: number;
  saa_poiketa: boolean;
  edustajatieto_puuttuu: string | null;
  luovutus: {
    asiakkaan_edustaja: string | null; luovutus_pvm: string | null; luovuttaja: string; hetki: string;
    a_poikkeama_peruste: string | null; avoimia_a_hetkella: number; poikkeama_kirjaaja: string | null;
    luokittelemattomia_hetkella: number | null;
  } | null;
  luovutusliitteita: number;
  /** 251 (T621, Toni 19.10.2026): asiakkaan edustajan piirtämä allekirjoitus; null = ei piirretty. */
  allekirjoitus?: { liite_id: number; nimi: string | null; hetki: string; tallentaja: string | null } | null;
  /** 251: liitteenä olevia allekirjoitettuja paperipöytäkirjoja (liitteen laji 250). */
  paperipoytakirjoja?: number;
}

export interface Linjatarkastus {
  id: number;
  linja_id: number;
  pvm: string | null;
  tarkastaja: string | null;
  tila: string;
  huomiot: string | null;
  kohdat: Record<string, { tulos: string; huomio?: string }>;
  muokattu: string;
  hyvaksyja: string | null;
  /** sampo | sharepoint | null (ennen 109:ää kirjattu). */
  isantajarjestelma?: string | null;
  /** Päätös 90: avoimet SharePoint-ristiriidat Sampossa tehtyyn tarkastukseen. */
  ristiriidat?: { id: number; kuvaus: string; havaittu: string }[];
}

export interface LinjaMateriaalit {
  linja: { id: number; tunnus: string; isometri_olemassa: boolean } | null;
  tarve: { nimike_tunnus: string; nimi: string | null; tarve: number; yksikko: string }[];
  osat: { nimike: string; materiaali: string | null; kpl: number; sulatukset: string | null }[];
  osaluettelo: { osa_nro: string | null; nimike: string; dn: string | null; mitta: string | null;
    standardi: string | null; materiaali: string | null; lkm: number | null; pituus_m: number | null }[];
}

export interface Tyopaketti {
  id: number;
  /** 238: arkistoitu (ei poistettu); näkyy vain ?arkistoidut=1. */
  arkistoitu?: string | null;
  arkistoinnin_peruste?: string | null;
  kohde_id: number;
  nro: number;
  nimi: string;
  media: string | null;
  dn: string | null;
  tila: string;
  jarjestys: number | null;
  aloitettu: string | null;
  valmis: string | null;
  /** 235 (T520): suunnitellut päivät (kolmen viikon suunnitelma). Toteutuneet ovat aloitettu/valmis. */
  suunniteltu_alku?: string | null;
  suunniteltu_loppu?: string | null;
  pyytaja: string | null;
  linjat: { linja_id: number; tunnus: string }[];
  /** Puuttuu tilaajan tunnukselta (26.9.2026): miehitystä. Puuttuva ≠ tyhjä. */
  tekijat?: { resurssi_id: number; nimi: string; lyhenne: string | null }[];
  hitseja: number;
  tehty: number;
}

export interface TyopakettiMateriaalit {
  isometrit: { linja_id: number; tunnus: string; isometri_olemassa: boolean; hitseja: number }[];
  materiaalit: { nimike: string; materiaali: string | null; kpl: number; sulatukset: string | null }[];
}

export interface TyopakettiTarve {
  nimike_tunnus: string;
  nimi: string | null;
  tarve: number;
  yksikko: string;
  varasto_nimike_id: number | null;
  saldo: number;
  jo_otettu: number;
}

export interface VarastoNimike {
  id: number;
  kohde_id: number;
  tunnus: string;
  nimi: string;
  ryhma: string | null;
  dn: number | null;
  mitat: string | null;
  materiaali: string | null;
  yksikko: string;
  saldo: number;
  viim_tapahtuma: string | null;
  synkka_saldo: number | null;
  synkka_aika: string | null;
}

export interface VarastoTapahtuma {
  id: number;
  laji: "saapuminen" | "otto" | "inventaario" | "korjaus";
  maara: number;
  pvm: string;
  viite: string | null;
  sulatus: string | null;
  kirjaaja: string | null;
  huomiot: string | null;
  tyopaketti_id: number | null;
  tp_nro: number | null;
  tp_nimi: string | null;
}

/** 124: vastaus reitiltä /api/linjat/tunnus/:tunnus — myös korvattu linja, ja mihin se ohjaa. */
export interface LinjaTunnuksella {
  id: number; kohde_id: number; tunnus: string; aktiivinen: boolean;
  korvaaja_id: number | null; korvaaja_tunnus: string | null;
  korvattu: string | null; korvaus_havaittu: string | null; korvaus_mekanismi: string | null;
  korvaus_lahde: string | null; korvaus_peruste: string | null;
}

export interface Linja {
  id: number;
  kohde_id: number;
  tunnus: string;
  alue: string | null;
  media: string | null;
  dn_nimellinen: number | null;
  putkiluokka: string | null;
  ped_luokka: string | null;
  /** Alle DN 40 linjoilta isometrit puuttuvat. */
  isometri_olemassa: boolean;
  /** null = suunnitelmaa ei tiedossa. Ei nolla eikä 100 %. */
  suunniteltuja_hitseja: number | null;
  tila: string;
  painekoe?: string | null;
  hitseja: number;
  /** Viimeksi käytetty piirustuslehti — esitäyttöä varten. */
  viim_lehti: string | null;
  /** Avoimet punchit (kuittaamattomat löydökset). Vain linjalistassa. */
  avoimia_puncheja?: number;
  /** Suodatuksen koko ennen `raja`a — Hubin "N linjaa" verrataan
   *  tähän, ei palautettujen rivien määrään. Vain listauksessa. */
  yhteensa?: number;
  /**
   * DN johdettuna isometrin mitta-sarakkeesta ("101.6x2" → 100).
   * null jos ei yksikäsitteistä osumaa — silloin valitaan listasta.
   * EI isometrin dn-sarakkeesta: se sisältää ulkomitan ("DN101.6").
   */
  dn_johdettu?: number | null;
  /** Mistä mitasta johdettu — näytetään, jotta luku ei ole taikaa. */
  dn_johdettu_mitta?: string | null;
  /** Haun nimittäjä: kohteen linjat ilman hakua (Selailu «42 / 728 linjaa»). */
  kaikkiaan?: number;
}

/** 197 (päätös 85): tuotu isometri. linja_id vain varmalle tai ihmisen kohdistamalle; muu odottaa listalla. */
export interface Isometri {
  id: number; kohde_id: number; linja_id: number | null; linja: string | null;
  ehdotus_linja_id: number | null; ehdotus_linja: string | null; liite_id: number | null; tiedostonimi: string;
  positio: string | null; lehti_nro: number | null; lehti_kpl: number | null; revisio: string | null;
  piirustusnumero: string | null; tekija: string | null; sivu_leveys: number | null; sivu_korkeus: number | null;
  tunnistus: "varma" | "epavarma" | "ei_loydy" | "ei_nimiota" | "ei_tekstia" | "kasin"; tunnistus_syy: string | null;
  korvattu_id: number | null; tuotu: string; kohdistettu: string | null; nastoja: number;
  /** 199: kirjan kokoonpano (Vertex) — sivualue ja osaluettelo; NULL = Swecon yksittäinen kuva. */
  muoto?: "vertex_kirja" | null; sivu_alku?: number | null; sivu_loppu?: number | null; osaluettelo?: IsometriOsa[] | null;
  /** 201: pehmeä poisto. */
  poistettu?: string | null; poisto_syy?: string | null; poistaja?: string | null;
}
/** 199 / päätös 100: osaluettelon rivi sellaisena kuin se luettiin isometristä. */
export interface IsometriOsa {
  osa: number; koodi: string | null; kuvaus: string | null; kuvaus2: string | null; standardi: string | null; materiaali: string | null;
  kpl: number | null; mitat: string | null; menekki: number | null; yksikko: string | null; paino: number | null;
}
/** Päätös 102: esikatselun rivi (Vertex: kokoonpano; Sweco: tiedosto). */
export interface IsometriEsikatseluRivi {
  avain: string; linja: string | null; linja_id: number | null; linja_tila: "olemassa" | "puuttuu" | "korvattu" | "epavarma";
  ehdotus_linja: string | null; piirustusnumero: string | null; revisio: string | null; lehti: string | null;
  /** Mistä lehti luettiin: otsikkotaulu · ocr · tiedostonimi; null = ei löytynyt (Toni 3.11.2026). */
  lehti_lahde?: string | null;
  sivu_alku: number | null; sivu_loppu: number | null; osaluettelo: IsometriOsa[]; syyt: string[]; jo_tuotu_id: number | null; voi_tuoda: boolean;
  /** 7.10.2026: DN nimestä putken ulkomitalla vahvistettuna; kirjoitetaan vain luotavalle linjalle. Sweco: null. */
  dn?: { arvo: number | null; huom: string } | null;
  tunnistus: string;
}
export interface IsometriEsikatselu { muoto: "sweco" | "vertex_kirja"; tiiviste: string; sivuja: number; ohitetut: number[]; rivit: IsometriEsikatseluRivi[] }
/** 197: numeroitu nasta = punch, koordinaatit sivun suhteellisina (0…1). */
export interface Nasta {
  id: number; numero: number; pohja: "isometri" | "kuva"; isometri_id: number | null; kuva_liite_id: number | null;
  x: number; y: number; luotu: string; lehti_nro: number | null; revisio: string | null; edellinen_revisio: boolean;
  punch_id: number; vika: string; luokka: string | null; vastuu: string | null; huomiot: string | null;
  huomioinut: string | null; huomio_pvm: string | null; kuitannut: string | null; kuittaus_pvm: string | null;
  avoin: boolean; liitteita: number;
}

/** 262 (T653): punchin luokat lomakkeelta (pakolliset uudessa punchissa; lisätieto on punchin vika-teksti). */
export interface PunchLuokat { luokka_missa: string; luokka_vika: string; viite_positio?: string; viite_tunnus?: string }

export interface Punch {
  id: number;
  linja_id: number;
  hitsi_id: number | null;
  hitsinumero: string | null;
  vika: string;
  huomioinut: string | null;
  huomio_pvm: string | null;
  /** null = avoin (ei kuitattu). */
  kuitannut: string | null;
  kuittaus_pvm: string | null;
  huomiot: string | null;
  /** Kuvia/liitteitä punchilla (liite.kohde_taulu = 'punch'). Puuttuu vanhasta API:sta. */
  liitteita?: number;
  /** Prioriteetti/luokka lähteestä (A, B, 1, 2 …), tyhjä kun ei tiedossa. Migraatio 108. */
  luokka?: string | null;
  /** Lähteen sarakkeen paikka (Punch 1–12); ei sama kuin tekstin sisäinen numero. */
  jarjestys?: number | null;
  /** sampo | sharepoint — kumpi järjestelmä omistaa rivin. */
  isantajarjestelma?: string | null;
  /** 197: kuka korjaa. */
  vastuu?: string | null;
  /** 197: nastan numero, jos punch on merkitty isometriin tai kuvaan. */
  nasta?: number | null;
  /** 236 (T534): kuka hoitaa. null = ei kukaan (yhteinen jono). */
  hoitaja?: Hoitaja | null;
  /** 249: linjatarkastuksen kohta 7.1–7.9; null = ei tarkastuskohtaan. */
  linjatarkastus_kohta?: string | null;
  /** 249: liitetyn hitsin tarra (hitsi.tarra). */
  tarra?: string | null;
  /** 262 (T653): «missä» ja «mikä vika» (luokkarekisterin koodit); null = vanha punch ilman luokkia. */
  luokka_missa?: string | null;
  luokka_vika?: string | null;
  viite_positio?: string | null;
  viite_tunnus?: string | null;
  /** 262: koneen ehdotus vanhalle tai tuodulle punchille — ei hyväksytty luokka. */
  luokitus_ehdotus?: { missa: string | null; vika: string | null; lahde?: string } | null;
}

/** T373: kolmen viikon suunnitelma. `paketit` puuttuu, kun hitsausloki ei ole käytössä (ei nollata). */
export interface Suunnitelma {
  kohde_id: number;
  /** f_kohde_varaa: vain aktiivinen kohde varaa kapasiteettia; holdattu näytetään, merkittynä. */
  varaa: boolean;
  viikot: { alku: string; loppu: string; viikko: number; tarve: number; allokoitu: number; henkiloita: number }[];
  /** T373: resurssitarverivit näillä viikoilla — tarpeen lähde. */
  tarve_lahde?: { rivit: number; henkiloita: number };
  paketit?: { id: number; nro: number; nimi: string; tila: string; suunniteltu_alku: string | null; suunniteltu_loppu: string | null;
    linjoja: number; tekijoita: number; valmiusilmoituksia: number }[];
  aikatauluttamattomat: { id: number; nro: number; nimi: string; tila: string }[];
}
/** T534 jatko: omat tehtävät. */
export interface OmatTehtavat {
  puutteet?: { id: number; tyyppi: string; kuvaus: string; vaikutus: "pysayttaa" | "hidastaa" | "ei_esta"; tila: string; havaittu: string;
    otettu: string; osoittaja: string | null; liittyva_kohde_id: number | null; kohteen_nimi: string | null; linja_tunnus: string | null }[];
  punchit?: { id: number; vika: string; luokka: string | null; linja: string; kohde: string | null; kohde_id: number; otettu: string; osoittaja: string | null; liitteita?: number }[];
  yhteensa: number;
}
/** 236 (T534): rivin avoin hoitaja. osoittaja null = otti itse. */
export interface Hoitaja { id: number; nimi: string; otettu: string; osoittaja: string | null }
export type HoitajaSuodatin = "minun" | "ei";

/** Punchin estoluokka — tilaajan oma määritelmä (Pekilo, 4.9.2026), ja se
 *  on vastaus kysymykseen «mikä tämän takia jää tekemättä», ei arvio
 *  tärkeydestä. Lähteessä on myös vanha numeromerkintä 1/2/3 (Kriittinen/
 *  Normaali/Pieni, 5.8.–1.9.) — mitattu: sama akseli eri aikakaudelta.
 *  Tonin päätös 4.9.2026: A=1, B=2, C=3. Tuonti tallentaa kirjaimen,
 *  näkymä lukee numeronkin kirjaimena. */
export const PUNCH_LUOKAT: Record<"A" | "B" | "C", { nimi: string; maaritelma: string }> = {
  A: { nimi: "estää vesiajon",     maaritelma: "estopalat hitsaamatta · tiivisteet puuttuvat · kaadot väärin · laipat käymättä" },
  B: { nimi: "estää eristystyön",  maaritelma: "putkisangat kiristämättä · laippakiristys · maadoituskaapelit · lämpösaatot" },
  C: { nimi: "ei estä mitään",     maaritelma: "paikkamaalaukset — linja voi olla jo käytössä" },
};
export function punchinLuokka(luokka: string | null | undefined): { tunnus: string; nimi: string; maaritelma: string; esto: boolean } | null {
  const raaka = (luokka ?? "").trim().toUpperCase();
  if (!raaka) return null;
  // Tonin päätös 4.9.2026: A=1, B=2, C=3 — sama asteikko.
  const l = ({ "1": "A", "2": "B", "3": "C" } as Record<string, string>)[raaka] ?? raaka;
  if (l === "A" || l === "B" || l === "C") return { tunnus: l, ...PUNCH_LUOKAT[l], esto: l !== "C" };
  return { tunnus: raaka, nimi: "luokka " + raaka + " (lähteen merkintä, ei määritelmää)", maaritelma: "", esto: false };
}
/** Näyttöteksti: lähteen häntä «· prio 2 Normaali — … (Nimi, 2026-08-17 00:00:00)»
 *  jätetään pois KUN sen sisältö on jo omissa kentissään (luokka, huomioinut,
 *  huomio_pvm). Kannan vika-teksti ei muutu — tämä on vain esitys. */
export function punchinTeksti(p: { vika: string; luokka?: string | null; huomioinut?: string | null; huomio_pvm?: string | null }): string {
  let t = p.vika;
  if (p.huomioinut && p.huomio_pvm) t = t.replace(/\s*\([^(),]{2,60},\s*\d{4}-\d{2}-\d{2}(?:\s+\d{2}:\d{2}:\d{2})?\)\s*$/, "");
  if (p.luokka) t = t.replace(/\s*·\s*prio\s*\S+[^·]*$/i, "");
  return t.trim();
}

/** Punch selailunäkymässä — linja + kohde mukana. */
export interface PunchRivi extends Punch {
  linja: string;
  kohde: string | null;
  /** Suodatuksen koko ennen LIMITiä (ikkunafunktio). Hubin
   *  "N avointa punchia" verrataan tähän. */
  yhteensa: number;
}

export interface EtenemaViikko {
  viikko: string;
  alku: string;
  hitseja: number;
  dia_inch: number;
  /** Kumulatiivinen dia-inch päivätyistä hitseistä — varma viiva. */
  kum: number;
  /** Kaistan alaraja: varma + päivätön työ jonka linjaikkuna on jo päättynyt. */
  kaista_ala: number;
  /** Kaistan yläraja: varma + päivätön työ jonka linjaikkuna on jo alkanut. */
  kaista_yla: number;
}

/**
 * Kattavuus: käyrän oma rehellisyysrivi. Neljä lukua, kaikki laskettuja.
 * hitsattu_dia = kokonaistyö (vaakaviiva). paivatty + kaista = käyrällä.
 * ajoittamaton = tiedämme että se on tehty, emme milloin.
 */
export interface EtenemaKattavuus {
  hitseja: number;
  hitsattu_dia: number;
  paivatty_dia: number;
  kaista_dia: number;
  ajoittamaton_dia: number;
  paivaton_hitseja: number;
  paivaton_dia: number;
  epakelpo_hitseja: number;
  epakelpo_dia: number;
  suunniteltu_hitseja: number;
  /**
   * Dia-inchin kattavuus. ⛔ Sama sauma joka on `dia_inch IS NULL` laskee
   * nollaksi molemmille puolille murtoviivaa, joten prosentti kuvaa vain sitä
   * osaa joukosta jolla putkikoko on tiedossa. Luku kuuluu prosentin viereen
   * eikä alaviitteeseen (Toni 29.9.2026).
   */
  dia_hitseja: number;
  dia_puuttuu_hitseja: number;
}

export interface Etenema {
  viikot: EtenemaViikko[];
  kattavuus: EtenemaKattavuus;
  /** paivaton = hitsit joilta hitsauspäivä puuttuu (eivät laske viikkoihin). */
  yhteensa: { hitseja: number; dia_inch: number; paivaton: number };
}

export interface HitsauslokiTilanne {
  tanaan: { hitseja: number; dia_inch: number };
  /** Vakiokoot DN-valintaan (dn_tuuma). Kenttä ei ole näppäiltävä. */
  dn_koot?: number[];
  /**
   * Tarran kiinteä alkuosa, esim. "JSG-26-". Näytetään lukittuna eikä
   * sitä näppäillä. Tyhjä = koko tarra kirjoitetaan käsin.
   */
  tarra_etuliite?: string;
  /** 207: false = peruskenttätili — `tanaan` on omat hitsit ja `puutteet` tyhjä (työnjohdon sisältö). */
  laaja?: boolean;
  /** T656: avoimien puutteiden määrä samalla rajauksella kuin Puutteet-lista (yläpalkin kohde); `puutteet` on siitä 8 ensimmäistä. */
  puutteet_yhteensa?: number;
  /** T656: Puutteet-moduuli käytössä — luku avaa listan. */
  puutteet_lista?: boolean;
  kohde?: number | null;
  /** Omat viimeisimmät linjat: kirjaus jatkuu mistä jäätiin. */
  viimeiset: {
    id: number; tunnus: string; dn_nimellinen: number | null;
    isometri_olemassa: boolean; lehti: string; paiva: string | null;
    hitseja: number;
  }[];
  puutteet: {
    id: number; tyyppi: string; kuvaus: string;
    vaikutus: "pysayttaa" | "hidastaa" | "ei_esta";
    vastuu: string | null; vastuu_ulkoinen: boolean; ika_vrk: number;
    /** Mille linjalle puute kuuluu — jotta siihen pääsee käsiksi. */
    linja_id: number | null;
    linja_tunnus: string | null;
  }[];
}

/* ── Puutteet ─────────────────────────────────────────────── */

export type PuutteenTyyppi = "punch" | "lahdeaukko" | "ristiriita" | "dokumenttivelka" | "vika" | "huomio";
export type PuutteenVaikutus = "pysayttaa" | "hidastaa" | "ei_esta";
export type PuutteenTila = "avoin" | "kaynnissa" | "ratkaistu" | "hylatty" | "korvattu";
/** 261 (T655): korvaavan aineiston laji — sama lista kuin api/src/puutekorvaus.ts ja 261:n CHECK. */
export type Aineistolaji = "mallinakyma" | "3d_nakyma" | "valokuva" | "luonnos" | "muu";
export const AINEISTOLAJIT: readonly Aineistolaji[] = ["mallinakyma", "3d_nakyma", "valokuva", "luonnos", "muu"];
export interface PuutteenKorvaus { laji: Aineistolaji; peruste: string; liite_id: number; kuittaaja: string; kuitattu: string }

export interface Puute {
  id: number;
  tyyppi: PuutteenTyyppi;
  /** Polymorfinen kiinnitys: henkilo, linja, hitsi, kohde… ilman FK:ta. */
  kohde_taulu: string;
  kohde_id: number;
  kuvaus: string;
  vaikutus: PuutteenVaikutus;
  /**
   * null myös silloin kun vastuu on asetettu mutta rivi on ulkoisella
   * vastuulla ja katsoja on vain-hitsaaja: kenen laskulle aukko menee
   * on kaupallista tietoa. `vastuu_ulkoinen` kertoo silti että se on
   * jonkun muun.
   */
  vastuu: string | null;
  vastuu_ulkoinen: boolean;
  tila: PuutteenTila;
  havaittu: string;
  ratkaistu: string | null;
  ratkaisu: string | null;
  lahde: string;
  /** Ikä vuorokausina — tämä tekee ulkoisesta aukosta laskutettavan.
   *  ⚠ Aika HAVAINNOSTA, ei ilmiön syntymästä: aina alaraja. */
  ika_vrk: number;
  /**
   * Milloin puutteen kohde erääntyy (144). null = ei erääntymistä TAI
   * havainto on vanhempi kuin 144 — vanhoilla riveillä päiväys on yhä
   * kuvauksen sisällä, eikä sitä kaiveta sieltä ulos arvaamalla.
   */
  eraantyy: string | null;
  /** T346: kuvia ja liitteitä tällä puutteella. 0 = ei yhtään, ei «ei tiedossa». */
  liitteita: number;
  /** C3: kuka sulki. null = sääntö sulki (089), ei «unohtunut kuittaus». */
  ratkaisija: string | null;
  /** T567: sääntö tunnisteen alkuosasta (PUUTE_SAANTO); «kasin» = käsin kirjattu. */
  saanto: string;
  /** 236 (T534): kuka hoitaa. null = ei kukaan. */
  hoitaja: Hoitaja | null;
  /** Montako suodatus löysi — EI montako tämä sivu palautti.
   *  Reitillä on `raja` (oletus 200), ja tunnusluvut laskettiin
   *  aiemmin palautetuista riveistä: ruudulla luki "200 avointa" kun
   *  niitä oli 2 260. Ikkunafunktio lasketaan ennen LIMITiä. */
  yhteensa: number;
  yhteensa_pysayttaa: number;
  yhteensa_ulkoisia: number;
  /** Mihin kohteeseen puute liittyy, jos se on johdettavissa. */
  liittyva_kohde_id: number | null;
  /** Ihmisluettava kohde: linjan tunnus, kohteen tunnus tai henkilön nimi. */
  kohteen_nimi: string | null;
  /** T332: linjan tunnus kun puute kohdistuu linjaan, hitsiin, linjatarkastukseen tai punchiin — kohdesolu on linkki. */
  linja_tunnus: string | null;
  /** T332: vahvistamaton nimiehdotus (henkilörekisteri 113). «Hyväksy ehdotus» vahvistaa asun rekisteriin. */
  nimiehdotus: { asu: string; ehdotus_id: number; ehdotus: string } | null;
  /** Montako kertaa **sama** puute on aiemmin hylätty.
   *
   *  Mahdollinen vasta migraation 091 jälkeen: suljettu rivi ei enää estä
   *  uutta nostoa, joten toistuva tilanne kertyy riveiksi. Se on signaali
   *  eikä roskaa — **sama asia hylättynä kolmesti on todiste siitä että
   *  sääntö on väärä, ei siitä että puute on sitkeä.** */
  hylatty_kertaa: number;
  /** 261 (T655): korvaava kuittaus, kun tila on `korvattu`; muuten null. */
  korvaus: PuutteenKorvaus | null;
  /** 261: avoin puute, jonka sääntö on moduulin rekisteröimä korvattavaksi. */
  korvattavissa: boolean;
}

export interface Wps {
  id: number;
  tunnus: string;
  nimi: string | null;
  prosessi: string | null;
  materiaaliryhmat: string[];
}

/**
 * Virallisen lomakkeen luokitus. Ainoa luokitus — erillistä lajia
 * ei ole, koska kaksi totuutta samasta asiasta erkanee.
 */
/** Laji: virallisen lokin kirjain. null = ei valittu (vain suunnitellulla, 119). */
export type Hitsityyppi = "A" | "E" | "R" | "S";

/*
 * A3 (18.9.2026): S puuttui kenttäkirjauksen valinnasta — unohdus, ei rajaus.
 *
 * Mitattu: tyyppi `Hitsityyppi` tuntee S:n, Selailun laji-valinta tarjoaa sen,
 * API hyväksyy sen (`enum` POSTissa) ja migraatio 021 luettelee sen virallisen
 * lomakkeen kirjaimena («S = lisätesti»). Vain tämä lista jätti sen pois, eikä
 * yhdessäkään kommentissa, päätöksessä tai migraatiossa ole perustetta.
 *
 * ⚠ Kaksi asiaa jotka tämä EI korjaa, ja jotka ovat sama vika isompana:
 * kirjaimet ovat kannassa taulussa `hitsityyppi` («valikoima vaihtelee
 * tilaajittain», 021) jota EI LUE KUKAAN — selain kovakoodaa ne kahdessa
 * paikassa ja API kolmannessa. Ja S osallistuu numeron uniikkiuteen (vain R on
 * rajattu pois `uq_hitsi_numero`sta), joten lisätesti tarvitsee oman numeronsa.
 */
export const HITSITYYPIT: { arvo: Hitsityyppi; nimi: string; selite: string }[] = [
  { arvo: "A", nimi: "Asennus",     selite: "Field joint" },
  { arvo: "E", nimi: "Esivalmiste", selite: "Prefabrication" },
  { arvo: "R", nimi: "Korjaus",     selite: "Repair" },
  /* ⛔ S on VEDETTY POIS kenttäkirjauksesta 18.9.2026 (Toni, erä D) kunnes
     numerointi on päätetty. Mitattu: `uq_hitsi_numero` sulkee pois vain R:n,
     joten lisätesti olemassa olevalle saumalle kaatuu 409:ään — ja juuri se on
     tapaus jota varten S valittaisiin. Avoin kysymys on kentän eikä koodin:
     tehdäänkö lisätesti samaan saumaan (→ uniikkiehto NOT IN ('R','S')) vai
     omaan koekappaleeseen (→ oma numeroavaruus).
     ⚠ Selailun laji-valinnassa S on yhä, ja se on tätä vanhempi: siellä numero
     on käyttäjän kirjoittama eikä ehdotettu, joten 409 on siellä luettava virhe
     eikä katkennut kirjausketju. */
];

export interface SeuraavaNumero {
  seuraava: string;
  /** Jo kirjatut numerot tyyppeineen. suunniteltu = esitäytetty, ei vielä
   *  hitsaajaa → sen voi täydentää (ei este). */
  varatut: { nro: string; tyyppi: Hitsityyppi; suunniteltu: boolean;
             /** 211: hitsin vaiheet (esivalmiste / asennus), kumpaakin enintään yksi. */
             vaiheet?: { vaihe: "esivalmiste" | "asennus"; paiva: string | null; koodi: string | null }[];
             /** T675: hitsin huomio (masterin Notes tai oma); «Liitos: …» erotetaan näytössä (hitsihuomio.ts). */
             huomiot?: string | null }[];
  /** Vapaat numerot varattujen välissä — helppo unohtaa. */
  aukot: string[];
}

export interface HitsinOsa {
  jarjestys: number;
  osalaji: string | null;
  materiaali: string | null;
  sulatus: string | null;
  dn: number | null;
}

export type NdtMenetelma = "VT" | "RT" | "UT" | "MT" | "PT" | "painekoe";
export type NdtTulos = "kesken" | "hyvaksytty" | "hylatty";

export interface Tarkastus {
  id: number;
  menetelma: NdtMenetelma;
  paiva: string | null;
  tulos: NdtTulos;
  tarkastaja: string | null;
  /** Pöytäkirjan numero; virallinen loki tulostaa sen tarkastussarakkeeseen. */
  raportti?: string | null;
  /** Hylätyn VT:n syy muodossa «VT hylätty: <vika> — <teksti>» (VT-joukkokirjaus 11.10.2026). */
  huomiot?: string | null;
  /** 233 (T562): pöytäkirjan voimassa olevat liitteet numeron kautta (tilaajalle 0) ja pöytäkirjan id. */
  poytakirja_liitteita?: number;
  poytakirja_id?: number | null;
}

export interface NdtKattavuus {
  menetelma: NdtMenetelma;
  /** Vaadittu osuus 0–1. */
  vaadittu_osuus: number;
  yhteensa: number;
  /** D2: kappalemäärä johon osuus pyöristyy — laskettu kannassa, ei selaimessa. */
  vaadittu_kpl: number;
  tarkastettu: number;
}

/** Linjan painekoe tarkastusrivinä (136). null = koetta ei ole kirjattu. */
export interface Painekoe {
  id: number;
  paiva: string | null;
  tulos: "kesken" | "hyvaksytty" | "hylatty" | "ei_tiedossa";
  tarkastaja: string | null;
  raportti: string | null;
  huomiot: string | null;
  liitteita: number;
  /** 161: NDT:n tila ja kattavuus sillä hetkellä kun koe merkittiin suoritetuksi (jäädytetty). */
  ndt_tila_kirjattaessa?: string | null;
  /** 161: peruste, jos koe kirjattiin suoritetuksi NDT:n ollessa kesken. null = ei tarvittu. */
  ndt_ohitus_peruste?: string | null;
  ndt_ohitus_kuka?: string | null;
  ndt_ohitus_hetki?: string | null;
}

/** Hitsin korjauksen syy (T309, Toni 14.9.2026). Sama lista kuin PATCH /api/hitsit/:id ja migraation 130 CHECK. */
export type KorjauksenSyyLaji = "nappailyvirhe" | "vaara_sauma" | "vaara_sulatus" | "vaara_tekija" | "taydennys" | "muu";
export const KORJAUKSEN_SYYT: { arvo: KorjauksenSyyLaji; nimi: string }[] = [
  { arvo: "nappailyvirhe", nimi: "Näppäilyvirhe" },
  { arvo: "vaara_sauma",   nimi: "Väärä sauma" },
  { arvo: "vaara_sulatus", nimi: "Väärä sulatus" },
  { arvo: "vaara_tekija",  nimi: "Väärä tekijä" },
  { arvo: "taydennys",     nimi: "Puuttuva tieto täydennetty" },   // 223 (T468)
  { arvo: "muu",           nimi: "Muu" },
];

/** 153: rivi työnjohdon jonossa. `esitys_id === null` = alinumero ilman
 *  esitystä, eli ennen 153:a syntynyt — niitä ei muuteta takautuvasti. */
export interface PunakynaOdottava {
  hitsi_id: number;
  hitsinumero: string;
  piirustuslehti: string | null;
  paiva: string | null;
  linja: string;
  kohde_id: number;
  esitys_id: number | null;
  esityksen_peruste: string | null;
  esitetty: string | null;
  esittaja: string | null;
}

/** 160: suljettu sanasto. Ulkopuoliset kolme = taso `nimetty`, kenttätyönjohto = `tyonjohto`. */
export type HyvaksyjanAsema = "kenttatyonjohto" | "valvoja" | "suunnittelutoimisto" | "tilaajan_edustaja";
export const HYVAKSYJAN_ASEMAT: { arvo: HyvaksyjanAsema; nimi: string; ulkopuolinen: boolean }[] = [
  { arvo: "kenttatyonjohto",     nimi: "Kenttätyönjohto",     ulkopuolinen: false },
  { arvo: "valvoja",             nimi: "Valvoja",             ulkopuolinen: true },
  { arvo: "suunnittelutoimisto", nimi: "Suunnittelutoimisto", ulkopuolinen: true },
  { arvo: "tilaajan_edustaja",   nimi: "Tilaajan edustaja",   ulkopuolinen: true },
];

/** 152: yksi punakynärivi — hyväksyntä tai sen peruutus. Append-only. */
export interface Punakyna {
  id: number;
  laji: "esitys" | "ei_toteudu_esitys" | "lisays" | "ei_toteudu" | "peruutus" | "hylkays";
  hyvaksytty: string;
  hyvaksyja: string;
  peruste: string;
  peruttu_id: number | null;
  /** Tämän rivin peruneen rivin id, jos se on peruttu. */
  perunut_id: number | null;
  /** 155: kirjaushetken valtuutus. `nimetty` kelpaa luovutusaineistoon,
   *  `tyonjohto` riittää muutokseen. null = ennen 155:tä kirjattu. */
  taso: "tyonjohto" | "nimetty" | null;
  /** 160: asema hyväksymishetkellä, jäädytetty. null = ennen 160:tä tai esitys. */
  hyvaksyjan_asema?: HyvaksyjanAsema | null;
  /** 267 (T677 K): ulkopuolisen hyväksyjän nimi ja viite; kirjaaja on `hyvaksyja`. */
  ulkopuolinen_nimi?: string | null;
  ulkopuolinen_viite?: string | null;
  /** T677 L: rivin päättänyt teko (hylkays / peruutus), jos rivi on päättynyt. */
  perunut_laji?: Punakyna["laji"] | null;
  /** T679: rivin liitteiden määrä (ulkopuolisen hyväksynnän todiste). */
  liitteita?: number;
}

/** T677 K: ulkopuolisen hyväksyjän tiedot hyväksynnän, hylkäyksen ja peruutuksen kutsuihin. */
export interface UlkopuolinenHyvaksyja { ulkopuolinen_nimi?: string; ulkopuolinen_viite?: string }

/** 194 (päätös 62): hitsin vahvistamaton muutos — punakynä näkyy vain kun tämä ei ole null. */
export interface Vahvistettava {
  /** 218: «poisto» = avoin poistoesitys (esitys_id = hitsi_poisto.id). */
  laji: "lisays" | "ei_toteudu" | "poisto";
  /** null = vanha alinumero ilman esitystä (ennen 153:a syntynyt). */
  esitys_id: number | null;
  kirjaaja: string | null; hetki: string | null; peruste: string | null; hitsinumero: string;
}
export interface Hitsi {
  /** T651: hitsejä samalla ehdolla ilman rajaa (sama luku jokaisella rivillä). Puuttuu vanhasta API:sta. */
  yhteensa?: number;
  /** 239 (T566): korvattujen tarrojen määrä (historia). Puuttuu vanhasta API:sta. */
  tarra_korvattuja?: number;
  /** T675: hitsin huomio ja liitos (120). Puuttuu vanhasta API:sta. */
  huomiot?: string | null;
  liitos?: string | null;
    /** JSG:n paikanpitäjä («00», «0», «?») tai hitsinumero = linjan numero (229): näkymä varoittaa, data ennallaan. */
    numero_tarkastettava?: boolean;
  /** 152: hitsinumerossa on alinumero (6.1) — kentällä tehty lisäys. */
  alinumero?: boolean;
  /** 194: lisähitsin emo (21 → 21.1). */
  emo_hitsi_id?: number | null;
  emo_hitsinumero?: string | null;
  /** T641: uusintahitsin (R) alkuperäinen — lista järjestää R:n sen perään. Puuttuu vanhasta API:sta. */
  korjaa_hitsi_id?: number | null;
  vahvistettava?: Vahvistettava | null;
  /** 194: lisäysesitys hylätty — rivi jää, tila «hylätty». */
  lisays_hylatty?: boolean;
  /** 194 (62 kohta 4): poisto vain riville jota ei ole hyväksytty eikä tuotu. Palvelimen päätös. */
  poistettavissa?: boolean;
  /** 152: onko lisäykselle voimassa oleva hyväksyntä. */
  lisays_hyvaksytty?: boolean;
  /** 152: suunniteltu hitsi jää tekemättä. Rivi säilyy; putoaa
   *  NDT-kattavuuden nimittäjästä. */
  ei_toteudu?: boolean;
  /** 155: onko päätöksellä ASETETUN hyväksyjän vahvistus. Työnjohdon
   *  hyväksyntä riittää muutokseen muttei luovutusaineistoon. */
  ei_toteudu_luovutuskelpoinen?: boolean;
  lisays_luovutuskelpoinen?: boolean;
  /** 267 (T677 K): voimassa olevan hyväksynnän ulkopuolinen hyväksyjä (taho, nimi) ja kirjaaja. */
  punakyna_ulkopuolinen?: { asema: HyvaksyjanAsema | null; nimi: string; kirjaaja: string | null } | null;
  id: number;
  /** Linjan tunnus. Näytetään "Kaikki linjat" -näkymässä. */
  linja: string;
  piirustuslehti: string;
  hitsinumero: string;
  hitsityyppi: Hitsityyppi | null;
  paiva: string | null;
  paiva_epatarkka: boolean;
  dn: number | null;
  dia_inch: number | null;
  tila: string;
  vaihe: string;
  karvi_id: string | null;
  revisio: string | null;
  tarra: string | null;
  sulatus_taydennetty: boolean;
  hitsausprosessi: string | null;
  lisaaine: string | null;
  juuri_hitsaaja_koodi: string | null;
  tayton_hitsaaja_koodi: string | null;
  /** Hitsausparin asentaja (T331). Ennen 130:tä lomake kirjoitti asentajan täyttöhitsaajaksi. */
  asentaja_koodi: string | null;
  /** Kirjaushetki kentältä (130); null = ei tiedetä. */
  kirjattu: string | null;
  /** Saako kysyjä korjata tämän hitsin (T309) — palvelimen päätös, sama ehto kuin PATCH. */
  korjattavissa: boolean;
  /** 7.10.2026: kuvia hitsillä ja saako kysyjä lisätä (oma tai parin hitsi; työnjohto ja ylöspäin). */
  kuvia?: number; kuva_lisattavissa?: boolean;
  wps: string | null;
  osat: HitsinOsa[];
  tarkastukset: Tarkastus[];
}

export interface Patevyystyyppi {
  id: number;
  tunnus: string;
  nimi: string;
  myontaja: string | null;
  /** Voimassaolo kuukausina. null = ei vanhene. */
  voimassa_kk: number | null;
}

/**
 * Rivi kohteen pätevyysvaatimuksesta: vaatimus × osaaja. Täyttämätön
 * vaatimus tulee henkilö-NULLina (ei katoa). saatavuus on NULL kun
 * osaajaa ei ole.
 */
export interface KohdePatevyysvaatimus {
  patevyystyyppi_id: number;
  patevyys: string;
  lukumaara: number;
  henkilo_id: number | null;
  /** Nimi ja lyhenne puuttuvat tilaajan tunnukselta (26.9.2026); henkilo_id jää lukumäärää varten. */
  henkilo?: string | null;
  lyhenne?: string | null;
  voimassa_asti: string | null;
  saatavuus: "vapaa" | "jo_kohteella" | "varattu" | "poissa" | null;
}

/** Kalusto: laite + viimeisimmän katsastuksen tila. */
export type KalustoLaji = "tyokalu" | "nostoapuvaline" | "henkilovaljaat";
/** 173: «henkilo» = hallussa. Lomakkeet eivät tarjoa sitä — haltuun otetaan skannaamalla. */
export type KalustoSijaintilaji = "varasto" | "kohde" | "auto" | "toimipiste" | "henkilo";
export interface KalustoTyyppi { id: number; laji: "tyokalu" | "nostoapuvaline"; nimi: string; aktiivinen: boolean; kaytossa: number }
/** 164: lomakkeen kentät. Annettu null tyhjentää, antamaton säilyttää. */
export interface KalustoSyote {
  laji?: KalustoLaji; tyyppi?: string | null; lisaa_tyyppi?: boolean;
  kunto?: string | null; kantavuus?: string | null;
  sijainti_laji?: KalustoSijaintilaji | null; sijainti_kohde_id?: number | null; sijainti_teksti?: string | null;
  valmistaja?: string | null; malli?: string | null; sarjanumero?: string | null; huomiot?: string | null;
  /** 165: pois käytöstä olevan laitteen siirto — kirjataan riville. */
  siirto_peruste?: string | null;
}
/** 172: skannatun numeron tila. Korvattu ei kanna laitetta (K3 b). */
export type KalustoQrKorvausSyy = "tarra_vaurioitunut" | "tarra_kadonnut" | "vaara_laite" | "muu";
export type KalustoQr =
  /** 181: numero ei osu annettuun tarrasarjaan — syy näytetään, lisäystä ei tarjota. */
  | { numero: number; koodi: string | null; tila: "ei_sarjassa"; alueet: Array<{ sarja: string; alku: number; loppu: number }>; viesti: string }
  | { numero: number; koodi: string | null; tila: "sitomaton"; oikeudet: { lisaa: boolean; sido: boolean }; esto: string | null }
  | { numero: number; koodi: string | null; tila: "korvattu"; korvattu: string; korvaus_syy: KalustoQrKorvausSyy; korvaus_peruste: string }
  | { numero: number; koodi: string | null; tila: "sidottu"; sidottu: string; laite: KalustoQrLaite;
      /** 173: haltija on sijainti (laji henkilo). */
      haltija: { henkilo_id: number; nimi: string | null; mina: boolean } | null;
      /** «Sinne mistä otettu» — null jos ei hallussa, ei lokia tai otettiin henkilöltä (alatapaus auki). */
      palautuksen_oletus: { laji: KalustoSijaintilaji; kohde_id: number | null; kohde: string | null; teksti: string | null } | null;
      oikeudet: { ota: boolean; palauta: boolean; muokkaa: boolean; tarkastus: boolean; korvaa: boolean; sijainti?: boolean };
      /** Puuttuvan toiminnon syy, joka nimeää oikeuden ja keneltä sitä pyydetään. */
      estot: Partial<Record<"ota" | "palauta" | "muokkaa" | "tarkastus", string>>;
      esto: string | null };
/** 55 (193): kenttätekijän lisäys on ehdotus kunnes kalustovastaava päättää. */
export type KalustoHyvaksynta = "ehdotettu" | "hyvaksytty" | "hylatty";
export interface KalustoQrLaite {
  id: number; tunnus: string; nimi: string; laji: KalustoLaji; tyyppi: string | null;
  hyvaksynta: KalustoHyvaksynta; ehdottaja: string | null; ehdotettu: string | null;
  hyvaksyja: string | null; hyvaksynta_hetki: string | null; hyvaksynta_peruste: string | null;
  kunto: string | null; kantavuus: string | null;
  sijainti_laji: KalustoSijaintilaji | null; sijainti_kohde_id: number | null; sijainti_kohde: string | null; sijainti_teksti: string | null;
  valmistaja: string | null; malli: string | null; sarjanumero: string | null; poistettu: string | null; poisto_syy: string | null;
  sijainti_henkilo_id: number | null; sijainti_henkilo: string | null;
  katsastus_asti: string | null; tila: "pois_kaytosta" | "ei_katsastusta" | "vanhentunut" | "vanhenee" | "voimassa";
}
export interface KalustoQrEhdokas {
  id: number; tunnus: string; nimi: string; tyyppi: string | null; sarjanumero: string | null;
  sijainti_laji: KalustoSijaintilaji | null; sijainti_kohde: string | null; sijainti_teksti: string | null;
}
export interface Kalusto {
  /** 55 (193): ehdotettu · hyvaksytty · hylatty, ehdottaja ja päätös. */
  hyvaksynta: KalustoHyvaksynta; ehdottaja: string | null; ehdotettu: string | null;
  hyvaksyja: string | null; hyvaksynta_hetki: string | null; hyvaksynta_peruste: string | null;
  id: number;
  tunnus: string;
  nimi: string;
  /** Voimassa olevan QR-tarran numero (28.9.2026, korjaus b); null = ei tarraa. */
  tarra?: number | null;
  /** 164 */
  laji: KalustoLaji;
  tyyppi: string | null;
  kunto: "hyva" | "tyydyttava" | "huono" | "poistettava" | null;
  kantavuus: string | null;
  sijainti_laji: KalustoSijaintilaji | null;
  sijainti_kohde_id: number | null;
  sijainti_kohde: string | null;
  sijainti_teksti: string | null;
  viimeisin_tarkastus: string | null;
  tarkastaja: string | null;
  valmistaja: string | null;
  malli: string | null;
  sarjanumero: string | null;
  aktiivinen: boolean;
  /** 116 */
  poistettu: string | null; poistaja: string | null; poisto_syy: string | null; poisto_peruste: string | null;
  huomiot: string | null;
  /** Viimeisimmän katsastuksen voimassaolo. null = ei katsastusta. */
  katsastus_asti: string | null;
  tila: "voimassa" | "vanhenee" | "vanhentunut" | "ei_katsastusta" | "pois_kaytosta";
  /** 165: viimeisin tarkastus hylätty. Johdettu, ei lippu. */
  pois_kaytosta: boolean;
}

export interface KalustoTarkastus {
  id: number;
  tarkastuspaiva: string | null;
  /** 165: hylätyllä ei voimassaoloa. */
  voimassa_asti: string | null;
  tarkastaja: string | null;
  tulos: "hyvaksytty" | "ehdollinen" | "hylatty";
  /** 165: pakollinen ehdolliselle. */
  tulos_syy: string | null;
  huomiot: string | null;
  /** 165: jäätyneet — kuka kirjasi ja milloin. */
  kirjaaja: string | null;
  luotu: string;
}

/* ── tarjouslaskenta ── */
export type TarjousTila = "luonnos" | "annettu" | "hyvaksytty" | "havitty" | "peruttu";

export interface Tarjous {
  id: number;
  tunnus: string;
  tilaaja: string | null;
  nimi: string;
  tila: TarjousTila;
  alkaa: string | null;
  paattyy: string | null;
  kate_pros: number;
  riski_pros: number;
  kohde_id: number | null;
  /** Laskettu tarjoushinta (listalla mukana; detaljissa yhteenvedossa). */
  hinta?: number;
  /** Vaihe 2b: TES ja vuorojako-% (TES-lisien laskentaan). */
  tes_id: number | null;
  osuus_ilta: number;
  osuus_yo: number;
  osuus_lauantai: number;
  osuus_sunnuntai: number;
  osuus_ylityo50: number;
  osuus_ylityo100: number;
}

export interface TarjousEra {
  id: number;
  kuvaus: string;
  maara: number;
  yksikko: string;
  dn: number | null;
  materiaali: string | null;
  hitsausprosessi: string | null;
  arvio: boolean;
  /** Normiaika per yksikkö (h). null = ei vielä laskettu (vaihe 2). */
  normi_h: number | null;
  huomiot: string | null;
  /** Erän tekevä osaamisluokka; sen tuntihinta laskentaan. */
  osaamisluokka_id: number | null;
  osaamisluokka_nimi: string | null;
}

export interface TarjousKustannus {
  id: number;
  laji: string;
  kuvaus: string;
  summa: number;
  huomiot: string | null;
}

export interface TarjousYhteenveto {
  eria: number;
  tunnit: number;
  muut_kustannukset: number;
  kate_pros: number;
  riski_pros: number;
  /** Vaihe 2: talon luvuilla lasketut. */
  sivukulukerroin: number;
  henkilosto_kustannus: number;
  /** Vaihe 2b: vuorojako-%:sta ja miehityksestä johdetut. */
  tes_lisat: number;
  matkat: number;
  paivaraha: number;
  /** Materiaalirivien (BOM) summa: Σ määrä × yksikköhinta. */
  materiaali_kustannus: number;
  kokonaiskustannus: number;
  hinta: number;
}

export interface MateriaaliTuontiRivi {
  tunnus: string | null; nimi: string | null; ryhma: string | null;
  yksikko: string | null; dn: number | null; laatu: string | null;
  paineluokka: string | null; sisaostohinta: number | null;
}
export interface MateriaaliTuonti {
  otsikot?: string[];
  mappays?: Record<number, string>;
  rivit_yhteensa?: number;
  tuotavia?: number;
  ohitetut?: number;
  esikatselu?: MateriaaliTuontiRivi[];
  lisatty?: number;
  virhe?: string;
  viesti?: string;
}

export interface Materiaali {
  id: number;
  tunnus: string | null;
  nimi: string;
  ryhma: string | null;
  yksikko: string;
  dn: number | null;
  paineluokka: string | null;
  laatu: string | null;
  sisaostohinta: number | null;
  aktiivinen: boolean;
  huomiot: string | null;
}

export interface TarjousMateriaali {
  id: number;
  materiaali_id: number | null;
  kuvaus: string;
  maara: number;
  yksikko: string;
  yksikkohinta: number;
}

/** 149/150: sopimussuhde, EI pääsy.
 *
 *  ⛔ Joukko on AVOIN (Toni 21.9.2026): uusi rooli on rivi kannassa eikä
 *  migraatio, joten tässä ei voi olla union-tyyppiä eikä vakiolistaa.
 *  Kaksi listaa erkanisi, ja selaimen kopio olisi se joka on väärässä
 *  hiljaa — sama vikamuoto kuin moduulitunnusten kaksoiskappaleessa.
 *  Nimet haetaan `api.asiakasroolit()`:lla. */
export type AsiakasRooli = string;

export interface Asiakasrooli {
  tunnus: string;
  nimi: string;
  jarjestys: number;
  /** false = ei kelpaa uusiin liitoksiin, olemassa olevat säilyvät. */
  aktiivinen: boolean;
  selite: string | null;
}

export interface KohteenAsiakas {
  id: number;
  asiakas_id: number;
  nimi: string;
  y_tunnus: string | null;
  rooli: AsiakasRooli;
  huomiot: string | null;
  luotu: string;
}

export interface Asiakas {
  id: number;
  nimi: string;
  y_tunnus: string | null;
  osoite: string | null;
  yhteyshenkilo: string | null;
  puhelin: string | null;
  sahkoposti: string | null;
  huomiot: string | null;
  /** 118 */
  poistettu: string | null; poistaja: string | null; poisto_syy: string | null; poisto_peruste: string | null;
  tarjouksia: number;
  /** 149: montako työkohdetta ja missä roolissa, esim. `{ tilaaja: 2 }`.
   *  Tyhjä objekti = ei yhtään liitosta. */
  kohteita_rooleittain?: Partial<Record<AsiakasRooli, number>>;
}

/** Ainestodistus (EN 10204 3.1) sulatuslinkkeineen. */
export interface Ainestodistus {
  id: number;
  kohde_id: number | null;
  kohde: string | null;
  toimittaja: string | null;
  materiaali: string | null;
  huomiot: string | null;
  luotu: string;
  ocr_tehty: boolean;
  sulatukset: string[];
  liite_id: number | null;
}

/** YTJ/PRH-haun tulos (Y-tunnuksella). */
export interface YtjTulos {
  nimi: string;
  y_tunnus: string;
  osoite: string;
  postinumero: string;
  postitoimipaikka: string;
  osoite_koko: string;
}

export interface Vuorotyyppi {
  id: number;
  tunnus: string;
  nimi: string;
  tunnit: number | null;
  tes_lisa_laji: string | null;
}
export interface RotaatioJasen {
  henkilo_id: number;
  nimi: string;
  lyhenne: string | null;
}
export interface RotaatioJakso {
  id: number;
  alkaa: string;
  paattyy: string;
  tila: "paalla" | "pois";
  vuorotyyppi_id: number | null;
  henkilo_id: number | null;
  henkilo_nimi: string | null;
}
export interface Rotaatioryhma {
  id: number;
  nimi: string;
  oletus_vuorotyyppi_id: number | null;
  jasenet: RotaatioJasen[];
  jaksot: RotaatioJakso[];
}
export interface RotaatioData {
  vuorotyypit: Vuorotyyppi[];
  ryhmat: Rotaatioryhma[];
}

export interface Sertifikaatti {
  id: number;
  nimi: string;
  tunnus: string | null;
  myontaja: string | null;
  voimassa_asti: string | null;
  huomiot: string | null;
}

/** Tarjouksen osapuolitiedot (asiakas rekisteristä + myyjä meiltä). */
export interface TarjousOsapuolet {
  asiakas_id: number | null;
  myyja: string | null;
  asiakas_nimi: string | null;
  asiakas_ytunnus: string | null;
  asiakas_osoite: string | null;
  asiakas_yhteyshenkilo: string | null;
  asiakas_puhelin: string | null;
  asiakas_sahkoposti: string | null;
}

export interface TarjousDetalji {
  tarjous: Tarjous & { sakkoehto: string | null; huomiot: string | null } & TarjousOsapuolet;
  erat: TarjousEra[];
  kustannukset: TarjousKustannus[];
  materiaalit: TarjousMateriaali[];
  yhteenveto: TarjousYhteenveto;
}

/* ── tarjouslaskennan asetukset (talon luvut) ── */
export type TesLisalaji =
  "ilta" | "yo" | "lauantai" | "sunnuntai" | "ylityo50" | "ylityo100";
export type TesYksikko = "prosentti" | "eur_per_h";

export interface TesLisaVersio {
  laji: TesLisalaji;
  yksikko: TesYksikko;
  arvo: number;
  /** Voimaantulopäivä. Sovellettava arvo = uusin alkaa <= laskentapäivä. */
  alkaa: string;
}

export interface Sivukulu {
  id: number;
  tunnus: string;
  nimi: string;
  prosentti: number;
  jarjestys: number;
  lahde_url: string | null;
  vuosi: number | null;
}

export interface OsaamisluokkaHinta {
  id: number;
  tunnus: string;
  nimi: string;
  tuntihinta: number | null;
  /** Matkakerrat/kk tarjouslaskennan matka-automaatioon. */
  matkat_per_kk: number;
}

export interface Tes {
  id: number;
  tunnus: string;
  nimi: string;
  aktiivinen: boolean;
  /** Lisät päivämäärällisinä versioina; frontend ryhmittelee jaksoiksi. */
  lisat: TesLisaVersio[];
}

export interface Hitsausnormi {
  id: number;
  dn: number;
  hitsausprosessi: string | null;
  normi_h: number;
  huomiot: string | null;
}

export interface TesEhdokas {
  laji: TesLisalaji;
  yksikko: TesYksikko;
  arvo: number;
  /** Voimaantulopäivä ISO; null = "voimaan tullessa" (aikaisin). */
  alkaa: string | null;
  /** Lähderivi PDF:stä — tarkistettavaksi. */
  lahde: string;
  varmuus: "korkea" | "matala";
}

export interface TarjousAsetukset {
  sivukulut: Sivukulu[];
  /** Efektiivinen sivukulukerroin = 1 + Σ(prosentti)/100. Laskettu, ei tallennettu. */
  kerroin: number;
  osaamisluokat: OsaamisluokkaHinta[];
  tesit: Tes[];
  asetukset: {
    vuokra_kerroin: number; tyopaivia_per_vko: number; tunnit_per_paiva: number;
    matka_kustannus: number; matka_paivaraha: number;
  };
  lisalajit: TesLisalaji[];
}

export type PatevyydenTila =
  | "voimassa" | "varoitus" | "kriittinen" | "vanhentunut" | "ei vanhene";

export interface PatevyysRivi {
  id: number;
  henkilo_id: number;
  lyhenne: string | null;
  nimi: string;
  patevyystyyppi_id: number;
  patevyys: string;
  taso: string | null;
  myonnetty: string | null;
  voimassa_asti: string | null;
  paivia_jaljella: number | null;
  varmennettu: boolean;
  /** Liitteinä olevien todistusten määrä. */
  todistuksia: number;
  epavarma: boolean;
  huomiot: string | null;
  tila: PatevyydenTila;
}

/** Pätevyys umpeutuu kesken allokoidun työn — kalenteri ei näytä tätä. */
export interface PatevyysRiskiRivi {
  henkilo_id: number;
  lyhenne: string | null;
  nimi: string;
  patevyys: string;
  voimassa_asti: string;
  kohde: string;
  allokaatio_asti: string;
  tunteja_jalkeen: number;
}

export interface Poissaololaji {
  id: number;
  tunnus: string;
  nimi: string;
  /** Terveystieto. Näkyy vain resursoinnista vastaaville rooleille. */
  arkaluonteinen: boolean;
  palkallinen: boolean;
}

export interface Poissaolo {
  id: number;
  henkilo_id: number;
  nimi: string;
  lyhenne: string | null;
  /** 84: puuttuu, kun laji on arkaluonteinen eikä katsojalla ole oikeutta. */
  poissaololaji_id?: number;
  /** "Poissa" jos rooli ei oikeuta näkemään arkaluonteista lajia. */
  laji: string;
  arkaluonteinen?: boolean;
  alkaa: string;
  paattyy: string;
  tila: "haettu" | "hyvaksytty" | "hylatty";
}

export interface Osaamisluokka {
  id: number;
  tunnus: string;
  nimi: string;
  jarjestys: number;
  /** Aktiivisten henkilöiden määrä — passivoituja ei lasketa. */
  henkiloita: number;
  /** false = poistettu valintalistoilta mutta yhä käytössä vanhoilla riveillä. */
  aktiivinen?: boolean;
  /** Työlaji on hitsaustyötä: kenttätekijä näkee hitsauslokin (127). */
  hitsaustyo?: boolean;
  /** Viimeisin lipun muutos perusteineen (129). null = ei muutoksia. */
  hitsaustyo_muutos?: { peruste_laji: string | null; peruste: string | null; kuka: string | null;
                        milloin: string; hitsaustyo: boolean } | null;
  /** Työlaji avaa laiteasennuksen kenttätekijälle (134). */
  laiteasennustyo?: boolean;
  iv_asennustyo?: boolean;
  laippatyo?: boolean;
  /** Luonnin vastauksessa: true = palautettiin passivoitu rivi. */
  palautettu?: boolean;
}

/** f_kuukausikuorma: viikko voi kulkea kahden kuukauden yli, joten
 *  kuukausiraja lasketaan erikseen koko kuukaudelta. */
export interface Kuukausikuorma {
  henkilo_id: number;
  lyhenne: string | null;
  nimi: string;
  kuukausi: string;
  allokoitu: number;
  maksimi: number;
  ylitys: boolean;
}

export interface Resurssitarve {
  id: number;
  osaamisluokka_id: number;
  osaamisluokka: string;
  alkaa: string;
  /** null = toistaiseksi voimassa. */
  paattyy: string | null;
  maara: number;
  kriittisyys: number;
  huomiot: string | null;
  /** Vaihe 1: vaatimukset jotka koskevat TÄTÄ tarveriviä. */
  vaatimukset?: { id: number; patevyystyyppi_id: number; nimi: string;
                  lukumaara: number; tayttaa: number; kelpaa_tanaan: number }[];
  /**
   * Päiviä jakson alkuun palvelimen kellosta. Negatiivinen = alkanut jo.
   *
   * Eräpäivä itse on `alkaa`; tämä on vain etäisyys siihen, koska selaimen
   * kello ei kelpaa vertailukohdaksi. Kolmatta tilaa («ei tiedetä milloin»)
   * ei ole: `resurssitarve.jakso` on NOT NULL ja luontireitti vaatii
   * `alkaa`-kentän — mitattu 20.9.2026, avoin alaraja 0 riviä kaikissa
   * neljässä instanssissa. Tilaa jota ei voi syntyä ei rakenneta.
   */
  paivia_alkuun?: number;
  /** Osaamisluokan henkilömäärä — se luku joka ennen oli sivun ainoa luku. */
  osaajia?: number;
  /** Leikkaus: osaaminen JA kaikki vaatimukset voimassa koko jakson. */
  tarjokkaita?: number;
  /** Montako henkilöä on jo SIDOTTU tälle jaksolle. Eri kysymys kuin
   *  kelpoisuus: «0 / 3 tarjokasta» kolmen sidotun rinnalla luki kuin virhe. */
  sidottuja?: number;
  /** Tarve miinus sidotut, ei alle nollan: «avoinna X / N». */
  avoinna?: number;
  /** Allokoitu kohteeseen tarpeen jaksolla mutta EI sidottu tähän tarpeeseen. */
  kohteessa_sitomatta?: number;
  /** Samat nimeltä (päätös 13, 30.9.2026): vanhan allokaation voi sitoa tehtävään ilman että tunnit tai huomiot muuttuvat. */
  sitomattomat?: { resurssi_id: number; nimi: string; paivia: number; tunnit: number }[];
  /** Sidonnat tähän tarpeeseen. Loppu mukaan lukien, null = toistaiseksi. */
  sidonnat?: { id: number; resurssi_id: number; nimi: string;
               alkaa: string; paattyy: string | null; peruste: string | null }[];
  /** Sidottuja joilta puuttuu tämän tarpeen vaatimus — ✗-rivit. */
  sidottuja_vajain?: number;
  /** Voimassa tänään, umpeutuu jakson aikana → muistutus, kortti uusittavissa. */
  uusittava?: number;
  /** ⛔ Umpeutunut JO, jakso jatkuu → este joka on voimassa nyt. */
  ei_kelpaa_nyt?: number;
  /** Tarjokkaat nimeltä, jotta sidonta voi tapahtua samalta riviltä. */
  tarjokkaat?: { henkilo_id: number; nimi: string;
                 tila: "tayttaa" | "uusittava" | "ei_kelpaa_nyt" | "ei_tayta";
                 allokoitu_pv: number;
                 /** Henkilön sidonnat jotka leikkaavat tarpeen jakson — tähän (`tama`)
                  *  tai muualle. Loppu mukaan lukien, null = toistaiseksi. */
                 sidottu?: { alkaa: string; paattyy: string | null; tama: boolean; kohde: string }[];
                 /** 145: miksi sidottiin vaikka pätevyys puuttuu. null = ei kirjattu. */
                 peruste?: string | null;
                 /**
                  * Mitkä vaatimukset jäävät täyttymättä jakson loppuun asti.
                  * `on_kortti` erottaa kolme syytä joilla on eri teko:
                  * ei korttia (hankittava) · kortti umpeutunut (uusittava) ·
                  * kortti ilman voimassaoloa (kirjattava). Päivä on NULL
                  * kaikissa kolmessa, joten se ei kerro kumpi on kyseessä.
                  */
                 puuttuu?: { nimi: string; on_kortti: boolean;
                             voimassa_asti: string | null }[];
                 /**
                  * 147: mitkä kohteen pakolliset perehdytykset puuttuvat.
                  * EI este — sidonta saa tapahtua, mutta perehdytys on
                  * tehtävä ennen ensimmäistä työpäivää (`perehdytys_mennessa`).
                  * Tyhjä lista = ei puutu; kenttä on aina olemassa.
                  */
                 perehdytys_puuttuu?: string[];
                 perehdytys_mennessa?: string | null }[];
  /** Kuka ja mikä: luku yksin ei riitä toimenpiteeseen. */
  vanhenevat?: { henkilo_id: number; nimi: string; vaatimus: string;
                 ryhma: "uusittava" | "ei_kelpaa_nyt"; voimassa_asti: string | null }[];
}

/* ── tyypit, joita ei ole vielä shared/types.ts:ssä ─────── */

export interface Moduuli {
  tunnus: string;
  nimi: string;
  kuvaus: string | null;
  /** Ydin: ei kytkettävissä pois. */
  pakollinen: boolean;
  kaytossa: boolean;
  jarjestys: number;
}

export interface Liite {
  id: number;
  alkuperainen: string;
  mime: string;
  koko: number;
  kuvaus: string | null;
  /** Sisältää henkilötietoa: näkyy oikeudella arkaluonteiset_henkilotiedot — ja aina lisääjälleen (Toni 16.10.2026). */
  arkaluonteinen: boolean;
  /** Tunnus lisäsi liitteen itse. */
  oma?: boolean;
  /** Saako tunnus poistaa arkaluonteinen-merkinnän (palkkaoikeus). */
  saa_poistaa_merkinnan?: boolean;
  lisatty: string;
  lisaaja: string | null;
  /** 250 (Toni 19.10.2026): mitä liite todistaa; null = tavallinen. */
  laji?: LiiteLaji | null;
  /** 274 (T547): käyttäjän antama nimi; null = ei nimetty (näytetään laji ja päivä, ei tiedostonimeä). */
  nimi?: string | null;
  /** 274 (T547): kuvan laji kuvaus / sijainti / sijainti_3d; null = ei valittu. */
  kuvalaji?: string | null;
}
export type LiiteLaji = "allekirjoitettu_poytakirja" | "allekirjoitus";

export interface Rooli {
  id: number;
  tunnus: string;
  nimi: string;
  kuvaus: string | null;
}

/** Kutsun luonti: linkki aina; lähetys jos pyydettiin ja onnistui, muuten syy. */
export interface Kutsutulos { kutsu: string; osoite: string; lahetys: { lahetetty: boolean; syy: string | null }; olemassa?: boolean }

/** Kutsun luonti: linkki aina; lähetys jos pyydettiin ja onnistui, muuten syy. */
export interface Kutsutulos { kutsu: string; osoite: string; lahetys: { lahetetty: boolean; syy: string | null }; olemassa?: boolean }

export interface KayttajaRivi {
  id: number;
  sahkoposti: string;
  nimi: string;
  aktiivinen: boolean;
  viimeksi_sisaan: string | null;
  roolit: string[];
  /** Voimassa olevien istuntojen määrä. */
  istuntoja: number;
  /** 106/117: laji tulee reitistä. jarjestelma = vahti, toimittaja = Sampo Pro, ulkopuolinen = tilaaja. */
  laji: "henkilo" | "jarjestelma" | "toimittaja" | "ulkopuolinen" | "kavely";
  /** true = koodi + PIN, false = sähköposti + salasana. */
  kenttatunnus: boolean;
  /** Henkilörekisterin nimi, jos tunnus on liitetty henkilöön. */
  henkilo: string | null;
  /** B2: viimeisimmän kutsun tila — avoin · kaytetty · vanhentunut · null (ei kutsua). */
  kutsu_tila: "avoin" | "kaytetty" | "vanhentunut" | "peruttu" | null;
  kutsu_vanhenee: string | null;
  /** Saman liitoksen ID. Lomake tarvitsee tämän; nimi ei kelpaa. */
  henkilo_id: number | null;
  /** Oikeudet jotka eivät ole rooleja (migraatio 067). Vain koko yrityksen (163). */
  oikeudet: string[];
  /** 163: kohteeseen rajatut oikeudet. */
  kohdeoikeudet?: { oikeus: string; kohde_id: number }[];
  /** 232 (T530): voimassa olevat oikeuspaketit. */
  paketit?: { id: number; paketti: string; kohde_idt: number[] | null }[];
  /** Kohderajaus. Tyhjä = näkee kaiken. */
  kohteet: number[];
  sahkoposti_keksitty?: boolean;
}

/** Kohteen elinkaari (migraatio 088).
 *
 *  **Vain `aktiivinen` varaa kapasiteettia.** Muut neljä vapauttavat
 *  miehityksen laskennasta ilman että allokointeja poistetaan — hold
 *  purkautuu yhdellä tilanvaihdolla ja suunnitelma palaa sellaisenaan.
 *
 *  `keskeytetty` ja `peruuntunut` käyttäytyvät kapasiteetissa
 *  identtisesti; ero on siinä odotetaanko paluuta. Se ero on pakko
 *  kysyä ihmiseltä silloin kun hän tietää sen, koska jälkikäteen sitä
 *  ei voi päätellä. */
export type KohdeTila =
  | "suunnitteilla"
  | "aktiivinen"
  | "keskeytetty"
  | "peruuntunut"
  | "valmis";

/** Varaako tämä tila kapasiteettia. Peili `f_kohde_varaa`ille
 *  (003_kyselyt.sql) — jos toinen muuttuu, tämän on muututtava. */
export const varaaKapasiteettia = (tila: KohdeTila) => tila === "aktiivinen";

/** Näyttönimi ja selite luetaan nayttonimi.ts:n kautta (`nayttonimi("kohde_tila", …)`, `selite(…)`), jotta kieli vaihtuu. */
export const KOHDE_TILA: Record<KohdeTila, { nimi: string; selite: string; en: string; selite_en: string }> = {
  suunnitteilla: { nimi: "Suunnitteilla", selite: "ei vielä päätetty — ei varaa väkeä",
                   en: "Planned", selite_en: "not yet decided — does not reserve people" },
  aktiivinen:    { nimi: "Aktiivinen",    selite: "työ käynnissä — varaa väkeä",
                   en: "Active", selite_en: "work in progress — reserves people" },
  keskeytetty:   { nimi: "Hold",          selite: "jatkuu myöhemmin — väki vapautuu, suunnitelma säilyy",
                   en: "On hold", selite_en: "continues later — people are released, the plan is kept" },
  peruuntunut:   { nimi: "Peruuntunut",   selite: "ei jatku — väki vapautuu",
                   en: "Cancelled", selite_en: "does not continue — people are released" },
  valmis:        { nimi: "Valmis",        selite: "tehty ja luovutettu — väki vapautuu",
                   en: "Completed", selite_en: "done and handed over — people are released" },
};

/** 162: vastaanottajan näkymä viestiin. */
/** 10.10.2026: Puutteet-näkymän vastuu-suodatin (routes.ts GET /api/puutteet). */
export type VastuuSuodatin = "tyonjohto" | "suunnittelu" | "ulkoinen" | "muu" | "ei" | "sisainen";
export const VASTUU_SUODATTIMET: readonly VastuuSuodatin[] = ["tyonjohto", "suunnittelu", "sisainen", "ulkoinen", "muu", "ei"];

/** T487: testauspalautteen rivi pääkäyttäjän koontiin (ei nimiä: tunnuskoodi + nimikirjaimet). */
export interface TestauspalauteRivi {
  id: number; tunnuskoodi: string; nimikirjaimet: string; rooli: string | null; kieli: string; laji: "tehtava" | "loppu" | "sivu";
  tehtava_nro: number | null; tehtava_teksti: string | null; tulos: string | null; kysymys: string | null; arvo: number | null;
  tyyppi: string | null; teksti: string | null; osoite: string | null; laite: string | null; luotu: string; muokattu: string;
}

export interface SaapunutViesti {
  id: number; otsikko: string; teksti: string; lahettaja: string;
  lahetetty: string; voimassa_asti: string | null; luettu: string | null;
  /** 227 (T453): näkymän osoite (#/…) ja sen teksti lukijan kielellä; NULL kun viestissä ei ole linkkiä. */
  linkki?: string | null; linkki_teksti?: string | null;
}
/** 162: lähettäjän näkymä — lukutilanne henkilöittäin. */
export interface LahetettyViesti {
  id: number; otsikko: string; teksti: string; lahettaja: string; lahetetty: string;
  voimassa_asti: string | null; kohdistus: string; kohde: string | null;
  peruttu: string | null; peruja: string | null; peruste: string | null; vanhentunut: boolean;
  vastaanottajat: { nimi: string; luettu: string | null }[];
}

export type KohdeTyolaji = "putkihitsaus" | "iv_asennus" | "laiteasennus" | "muu";
export const KOHDE_TYOLAJI: Record<KohdeTyolaji, string> = {
  putkihitsaus: "Putkihitsaus", iv_asennus: "IV-asennus", laiteasennus: "Laiteasennus", muu: "Muu",
};
export interface Kohde {
  id: number;
  tunnus: string;
  nimi: string;
  /** Kohteen oma asiakasteksti — LÄHTEEN arvo, ei rekisteriviite.
   *  Jää paikalleen myös liitoksen jälkeen: se on ainoa tieto siitä
   *  mihin liitos perustui (vrt. `paiva_raaka`). */
  asiakas: string | null;
  /** 149: rekisterin tilaaja. null = ei liitetty — ja se on oma
   *  tilansa, ei tyhjä arvo. */
  tilaaja: string | null;
  tilaaja_id: number | null;
  /** 196 (päätös 80): viimeisin edellinen tunnus («entinen X») ja jatkettava kohde. */
  entinen_tunnus?: string | null;
  jatkaa_kohde_id?: number | null;
  jatkaa_tunnus?: string | null;
  /** 278 (Toni 3.11.2026): arkistointihetki; null = ei arkistoitu. Arkistoitu ei näy valitsimessa eikä Kaikki kohteet -luvuissa. */
  arkistoitu?: string | null;
  laji: "projekti" | "jatkuva";
  sijainti: string | null;
  alkaa: string | null;
  paattyy: string | null;
  tila: KohdeTila;
  /** 187: luovutuspäivä. Kanta asettaa tilan vaihtopäivän kun tila vaihtuu valmiiksi ilman päivää;
   *  null = ei luovutettu tai päivää ei tiedetä (valmis-tila ennen 187:ää). */
  luovutettu_pvm?: string | null;
  /** 188: työlaji kenttänä (putkihitsaus · iv_asennus · laiteasennus · muu). Ei johdeta datasta. */
  tyolaji?: KohdeTyolaji;
  /** 209 (vaihe c): raporttien ja tulosteiden oletuskieli; tulostaja voi valita toisen. null = instanssin kieli. */
  dokumentaatiokieli?: "fi" | "en" | null;
  /** 221 (T464): näytetäänkö Luovutusvalmiuden lähtötietojen aukot tilaajalle (oletus true). */
  nayta_lahtoaukot_tilaajalle?: boolean;
  /** 222 (T466): asiakkaan edustaja pakollinen linjatarkastuksen hyväksynnässä (oletus true). */
  linjatarkastus_edustaja_pakollinen?: boolean;
  /** Linjojen määrä. Puuttuu kun hitsauslokimoduuli ei ole käytössä --
   *  poissaolo ei ole nolla, ja lajittelu ei saa lukea sitä nollaksi. */
  linjoja?: number;
}

/** Sopimussuhde tähän organisaatioon, ei rooli (migraatio 073).
 *  `ulkopuolinen` = ei työsuhdetta, ei laskeudu kapasiteettiin:
 *  opiskelija, toisen yrityksen asentaja, auditoija. */
export type { Tyosuhde };

/**
 * Henkilö ilman resursointia.
 *
 * `GET /api/henkilot` — Academyn ja osaamispuolen näkymä ihmisiin.
 * Tarkoituksella suppeampi kuin `Resurssi`: ei kapasiteettia eikä
 * palkkoja, koska Academy ei tarvitse niitä eikä saa lukea niitä.
 */
export interface Henkilo {
  id: number;
  lyhenne: string | null;
  tyotunnus: string | null;
  nimi: string;
  etunimi: string;
  sukunimi: string;
  nimike: string | null;
  tyosuhde: Tyosuhde;
  nimeamaton: boolean;
  sahkoposti: string | null;
  puhelin: string | null;
}

/** Olemassa oleva kenttätili: henkilö jolla on koodi, tunnus ja
 *  kenttärooli. `id` on henkilön `resurssi_id` — sama jota
 *  `POST /api/kenttatilit/:id/pin` odottaa. */
/** T280: pohjallisen tuonnin raportti (linjat, WPS). Kuivakäynnissä luotu = 0. */
export interface JatkuvuusRivi {
  henkilo_id: number;
  nimi: string;
  tyotunnus: string | null;
  prosessi: string;
  /** null = kaikki tämän parin saumat ovat päivättömiä → ei mitattavissa. */
  viimeisin: string | null;
  /** «ylaraja» = osa saumoista on päiväämättömiä, joten todellinen tauko on
   *  tätä lyhyempi tai yhtä pitkä. Päiväämätön sauma on tehty työ. */
  tarkkuus: "tasmallinen" | "ylaraja" | "ei_mitattavissa";
  paivia: number | null;
  hitseja: number;
  paivattomia: number;
  epatarkkoja: number;
  /** Päätös 88: yli 6 kk (EN ISO 9606-1, 9.2) · yli 5 kk · «ehkä» kun osa saumoista päiväämättömiä · null = alle 5 kk tai ei mitattavissa. */
  kk_raja?: "yli_6" | "yli_5" | "ehka_yli_6" | "ehka_yli_5" | null;
}

export type KuittausRatkaisu = "hitsattu" | "hitsattu_selvitettava" | "ei_hitsattu" | "ei_talla_linjalla" | "punakyna";
export interface TunnistettuLinja {
  linja_id: number; tunnus: string; alue: string | null; dn: string | null; spec: string | null; media: string | null;
  /** tunnus · tarra · laite · rajaus (pilkuin) */
  tapa: string; reitit: { mista: string | null; mihin: string | null }[] | null;
  kuittausriveja: number; avoimia: number; isometri_liite_id: number | null; isometreja: number;
}
export interface HitsikuittausRivi {
  id: number; linja_id: number | null; linja_tunnus: string; isometri: string | null; lehti: string | null; hitsinumero: string; dn: string | null;
  tila: "puuttuu_masterista" | "masterissa_ilman_lehtea" | "kohdistus_epavarma" | "kunnossa"; isometri_tiedosto: string | null; karvi_id: string | null;
  huomio: string | null; ratkaisu: KuittausRatkaisu | null; hitsaaja_koodi: string | null; asentaja_koodi: string | null;
  paiva: string | null; vaihe: "esivalmiste" | "asennus" | "korjaus" | null; kuittaus_huomio: string | null;
  kuitattu: string | null; kuittaaja: string | null; hitsi_id: number | null; alue: string | null; isometri_liite_id: number | null;
}
export interface Hitsikuittaukset {
  rivit: HitsikuittausRivi[];
  reitit: { tag: string; mista: string | null; mihin: string | null }[];
  alueet: { alue: string | null; linjoja: number; avoimia: number }[];
  hitsaajat: { koodi: string; hitseja: number }[];
}

export interface PohjaTuonti {
  sarakkeet: Record<string, string>;
  /** Otsikot joita ei tunnistettu — niitä ei lueta. */
  tuntemattomat: string[];
  tyhjia: number;
  /** Ei estä tuontia, mutta kertoo mitä rivit eivät tee (T286: PED-luokka ilman linjaa). */
  huomautukset?: string[];
  uusia: number;
  olemassa: number;
  /** G4b: montako olemassa olevaa riviä muuttuisi, jos päivitys sallitaan. */
  paivitettavia?: number;
  ristiriitoja: number;
  hylattyja: number;
  /** Rivi on numero, tai monen välilehden tuonnissa «Välilehti rivi» (164). */
  uudet_esikatselu: { rivi: number | string; tunnus: string; kuvaus: string }[];
  olemassa_esikatselu: { rivi: number | string; tunnus: string; aktiivinen?: boolean;
                         erot: { kentta: string; sampo: string; tiedosto: string }[] }[];
  ristiriidat: { rivi: number; tunnus: string; syy: string }[];
  hylatyt: { rivi: number | string; syyt: string[] }[];
  luotu: number;
  /** G4b: montako olemassa olevaa riviä päivitettiin (0 jos päivitystä ei pyydetty). */
  paivitetty?: number;
  /** T172 kalusto: montako kalibrointi- tai katsastusriviä luotiin. */
  kalibroinnit_luotu?: number;
  kalibrointeja?: number;
}

export interface Kenttatili {
  id: number;
  /** Käyttöliittymän kieli (205); null = organisaation oletus. Työnjohto asettaa (Norjan erä 8.10.2026). */
  kieli?: string | null;
  /** Tilin oma id (`id` on henkilön). Työmaiden muutos kulkee tällä. */
  kayttaja_id: number;
  /** 67 B (5.10.2026): erilliset oikeudet koko yritykseen ja kohteittain — sama muoto kuin KayttajaRivi. */
  oikeudet: string[];
  kohdeoikeudet: { oikeus: string; kohde_id: number }[];
  koodi: string;
  nimi: string;
  /** Käsin tehdyt sidonnat, kaikissa tiloissa — poikkeus (päätös 13.9.2026). Kentällä näkyvät vain aktiiviset. */
  kohde_idt: number[];
  /** Sidonta seuraa allokaatiosta (päätös 13.9.2026): työallokaatio ± 30 pv, kaikissa tiloissa.
   *  Tili näkee näiden ja käsin tehtyjen yhdisteen, aktiiviset. Molemmat tyhjiä = ei näe kohteita. */
  johdetut_kohde_idt: number[];
  /** 125: null = ei sähköpostia (tila); keksitty = vanha paikanpitäjäarvo tallessa, merkitty. */
  sahkoposti?: string | null;
  sahkoposti_keksitty?: boolean;
  /** 51: paatti < tänään → kenttäkirjautuminen kieltäytyy (auth.ts), sama ehto. */
  kirjautuminen_estetty?: boolean;
  /** 168: PIN on pääkäyttäjän asettama (luonti tai nollaus), omistaja ei ole vielä vaihtanut. */
  pin_vaihdettava?: boolean;
  /** 91: työmaa allokaatiosta (tänään, muuten lähin 30 pv) — «mihin». null = ei allokaatiota. */
  tyomaa?: string | null;
  tyomaa_peruste?: string | null;
  viimeksi_sisaan: string | null;
  aktiivinen: boolean;
  /** Työsuhteen päättymispäivä. Päättynyt työsuhde ja elävä
   *  kenttätili ovat eri asia, ja vain jälkimmäinen näkyi. Tiliä ei
   *  suljeta automaattisesti — merkintä, ja pääkäyttäjä päättää. */
  paatti: string | null;
}

export interface Resurssi {
  /** T530: henkilön aktiivisten tunnusten numerot (kenttätili ensin); puuttuu kenttätilin työkaverinäkymästä. */
  tunnusnumerot?: number[] | null;
  id: number;
  tunnus: string;
  lyhenne: string | null;
  tyotunnus: string | null;
  /** `Etunimi Sukunimi`. Ruudukko ja valintalistat käyttävät tätä. */
  nimi: string;
  /** Erikseen, jotta Henkilöt-näkymä voi näyttää `Sukunimi, Etunimi`:
   *  lista on lajiteltu sukunimen mukaan, ja rivin on alettava
   *  lajitteluavaimesta tai järjestys on näkymätön. */
  etunimi: string;
  sukunimi: string;
  nimike: string | null;
  puhelin: string | null;
  sahkoposti: string | null;
  /** Työsuhteen alku ja loppu. `f_tyoaika_paiva` suodattaa näillä, eli
   *  päättynyt ei kuluta kapasiteettia. Kenttä on ohjannut laskentaa
   *  097:stä asti mutta ei näkynyt yhdessäkään näkymässä. */
  aloitti: string | null;
  paatti: string | null;
  tyosuhde: Tyosuhde;
  nimeamaton: boolean;
  tunnit_per_paiva: number;
  maksimi_per_paiva: number;
  /** null = ei viikkokattoa, jolloin päivärajojen summa ratkaisee. */
  maksimi_per_viikko: number | null;
  /** null = ei kuukausikattoa. Ei vaikuta ruudukon väreihin. */
  maksimi_per_kuukausi: number | null;
  /** ISO-viikonpäivät joina henkilö työskentelee, 1 = ma … 7 = su. */
  tyopaivat: number[];
  osaamiset: string[];
  osaamisluokka_idt: number[];
  /** Tehtäväkohtaiset tuntipalkat: osaamisluokka_id → €/h. */
  palkat: Record<string, number>;
  /** Henkilön oma raakatuntipalkka (€/h), yleispalkka. Todelliseen kustannukseen. */
  tuntipalkka: number | null;
  /** Mihin TESiin henkilö kuuluu (lisälaskentaan). */
  tes_id: number | null;
  /** Henkilön oma matkakerrat/kk. null = osaamisluokan oletus. */
  matkat_per_kk: number | null;
  /** 106: onko ihmisellä kenttätunnus (koodi + PIN). */
  kenttatunnus_on: boolean;
  /** 217: kenttätilillä on Academy-oikeus; null = osaaminen-moduuli pois. */
  academy_on?: boolean | null;
  /** 106: onko ihmisellä käyttäjätili (sähköposti + salasana). */
  kayttajatili_on: boolean;
}

export interface TesListaus {
  id: number;
  tunnus: string;
  nimi: string;
  aktiivinen: boolean;
}

export interface HenkilonRivi {
  kohde_id: number;
  tunnus: string;
  nimi: string;
  laji: "projekti" | "jatkuva";
  viikko_alku: string;
  allokaation_laji: "tyo" | "varallaolo" | "matka";
  tunnit: number;
  paivia: number;
}

export interface AllokaatioRivi {
  kohde_id: number;
  tunnus: string;
  nimi: string;
  laji: string;
  /** ALLOKAATION tila: ehdotus vai vahvistettu. */
  tila: string;
  /** KOHTEEN elinkaari. Jos tämä ei varaa kapasiteettia, rivi näkyy
   *  ruudukossa mutta ei kuormita — ja sen on näytettävä siltä. */
  kohde_tila: KohdeTila;
  alkaa: string;
  paattyy: string;
  paivia: number;
  tunnit: number;
  tunnit_per_paiva: number;
}

/* ── päivämääräapurit ───────────────────────────────────── */

/** Viikon maanantai. ISO-viikko alkaa maanantaista. */
export function maanantai(p: Date): Date {
  const d = new Date(Date.UTC(p.getFullYear(), p.getMonth(), p.getDate()));
  const paiva = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() - paiva + 1);
  return d;
}

export const pvmTeksti = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * Tämä päivä paikallisessa kalenterissa.
 *
 * `pvmTeksti(new Date())` on UTC-päivä, ja Suomessa kesällä UTC+3.
 * Yövuorolainen joka kirjaa klo 01:00 sai EDELLISEN päivän — rivi
 * näytti päivätyltä, oli vain väärässä kohdassa. Se on pahempi vika
 * kuin päiväämättömyys, joka ainakin tunnustaa epävarmuutensa.
 *
 * `pvmTeksti`ä EI vaihdettu paikalliseksi, koska sillä on 24 kutsujaa
 * ja niistä 15 on muotoa `pvmTeksti(lisaaPaivia(new Date(pvm), n))` —
 * tallennetun päivän aritmetiikkaa, joka on UTC:nä sisäisesti
 * johdonmukainen. Jaetun funktion vaihtaminen olisi rikkonut ne 15
 * saadakseen korjauksen kahteen. Yksi funktio, monta kutsujaa on oma
 * vikaluokkansa, eikä sitä korjata korjaamalla se väärästä päästä.
 *
 * Sama kuvio kuin `maanantai`ssa: paikalliset kalenterikentät ulos,
 * UTC-keskiyöksi sisään, jolloin `toISOString` lukee saman päivän.
 */
export function tanaan(): string {
  // Yksi Date-instanssi: kolme erillistä `new Date()`-kutsua voisi
  // ylittää keskiyön kesken lausekkeen.
  const n = new Date();
  return pvmTeksti(new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate())));
}

export function lisaaPaivia(d: Date, n: number): Date {
  const uusi = new Date(d);
  uusi.setUTCDate(uusi.getUTCDate() + n);
  return uusi;
}

/** Perjantai samalta viikolta. Allokaatio koskee vain työpäiviä. */
export const perjantai = (maanantaiPvm: string): string =>
  pvmTeksti(lisaaPaivia(new Date(maanantaiPvm), 4));


/* ── osaamisradat (moduuli: osaaminen) ────────────────────── */

export interface OsaamisVaihe {
  id: number; tunnus: string; nimi: string;
  kuvaus: string | null; jarjestys: number; aktiivinen: boolean;
}

export interface OsaamisPorras {
  id: number; tunnus: string; nimi: string; porras: number;
  saanto: "nimitys" | "kattavuus" | "ketju";
  raja: number; eri_yksikkoa: number | null; vastuuraja: number | null;
  vaatii_mitatun: boolean;
}

export interface OsaamisRata {
  id: number; tunnus: string; nimi: string; kuvaus: string | null;
  tuoreus_kk: number; jarjestys: number; aktiivinen: boolean;
  vaiheet: OsaamisVaihe[]; tasot: OsaamisPorras[];
}

/** f_osaamistaso: johdettu taso. Hyvitetyt ja mitatut aina erikseen. */
export interface OsaamisTaso {
  henkilo_id: number; rata_id: number; rata: string;
  taso_id: number | null; taso: string | null; porras: number | null;
  mitatut: number; hyvitetyt: number; eri_yksikkoa: number;
  vastuulla: number; ketjuja: number;
  viimeisin: string | null; lepaava: boolean;
}

export interface OsaamisNimitys {
  id: number; rata_id: number; rata: string; alkaen: string;
  peruste: string | null; myonsi: string | null;
  peruttu: string | null; perui: string | null; perumisen_syy: string | null;
}

export interface OsaamisSuoritus {
  id: number; henkilo_id: number; vaihe_id: number; vaihe: string;
  rata_id: number; rata: string; yksikko: string | null; kohde_id: number | null;
  paiva: string; vastuuhenkilona: boolean; hyvitys: boolean;
  peruste: string | null; myonsi: string | null;
}


/* ── Academy ───────────────────────────────────────────────── */

export interface AcademyKurssi {
  id: number; tunnus: string; nimi: string; kuvaus: string | null;
  rata_id: number | null; rata?: string | null;
  vaatii_id: number | null; vaatii?: string | null;
  lapaisyraja: number; voimassa_kk: number | null;
  jarjestys: number; julkaistu: boolean;
  osioita?: number; kysymyksia?: number;
}

export interface AcademyOmaKurssi extends AcademyKurssi {
  lapaisty: boolean | null;
  pisteet: number | null;
  paiva: string | null;
  voimassa_asti: string | null;
  yritys: number | null;
  lahde: "koe" | "kasin" | null;
  voimassa: boolean | null;
  lukossa: boolean;
}

export interface AcademyOsio {
  id: number; tunnus: string; nimi: string;
  sisalto: string | null; video_url: string | null; jarjestys: number;
}

/** Kokeen kysymys. Oikeat vastaukset EIVÄT tule selaimeen. */
export interface KoeKysymys {
  id: number; kysymys: string; tyyppi: "yksi" | "monta"; jarjestys: number;
  vaihtoehdot: { id: number; teksti: string }[];
}

export interface KoeTulos {
  suoritus_id: number; yritys: number;
  /** Vain läpäisseelle (25.9.2026): hylätyn pisteet ja oikeiden määrä olisivat oraakkeli. */
  pisteet?: number; lapaisyraja: number; lapaisty: boolean;
  kysymyksia: number;
}

export interface TeoriaTila {
  henkilo_id: number; rata_id: number; rata: string;
  kursseja: number; lapaistyja: number; vanhentunut: number; valmis: boolean;
}


/* ── Sertifikaatti ─────────────────────────────────────────── */

export interface SertiEhdot {
  henkilo_id: number; rata_id: number; rata: string;
  taso_id: number | null; taso: string | null;
  teoria: boolean; teoria_kursseja: number; tyo: boolean; arviointi: boolean;
  arviointi_pvm: string | null; lepaava: boolean; valmis: boolean;
}

export interface Arviointi {
  id: number; rata_id: number; rata: string; paiva: string;
  hyvaksytty: boolean; kohde: string | null; huomiot: string | null;
  arvioija: string | null;
}

export interface Sertifikaatti {
  id: number; numero: string; henkilo_id: number; nimi: string;
  rata_nimi: string; taso_nimi: string; myonnetty: string;
  voimassa_asti: string | null; myontaja: string | null;
  peruttu: string | null; perui: string | null; perumisen_syy: string | null;
  voimassa: boolean;
}


/** Yksi sertifikaatti jäädytettyine todisteineen (tuloste lukee tätä). */
export interface SertifikaattiTiedot {
  id: number; numero: string; henkilo_id: number; nimi: string;
  lyhenne: string | null;
  rata_nimi: string; taso_nimi: string;
  myonnetty: string; voimassa_asti: string | null; myontaja: string | null;
  peruttu: string | null; perumisen_syy: string | null;
  ehdot: {
    taso?: {
      mitatut: number; hyvitetyt: number; eri_yksikkoa: number;
      vastuulla: number; ketjuja: number;
    };
    kurssit?: { nimi: string; paiva: string; pisteet: number | null;
                voimassa_asti: string | null; lahde: string }[];
    arviointi?: { paiva: string; kohde: string | null;
                  huomiot: string | null; arvioija: string | null } | null;
  } | null;
}

/**
 * Työmaan miehitys ja perehdytystilanne.
 *
 * `perehdytetty` on johdettu: tyhjä JA vanhentunut ovat molemmat
 * false, mutta eri syystä — siksi `perehdytys_pvm` ja `voimassa_asti`
 * ovat mukana erikseen. Tyhjä kenttä ei ole sama asia kuin
 * vanhentunut, eikä kumpaakaan saa lukea todisteeksi.
 */
/** 148: yksi rivi vientilokissa — kuka vei mitä ulos ja milloin. */
export interface VientilokiRivi {
  id: number;
  hetki: string;
  kayttaja_id: number | null;
  /** Nimi jäädytettynä vientihetkeen: säilyy vaikka tunnus poistetaan. */
  kayttaja_nimi: string;
  vienti: string;
  tiedosto: string | null;
  kohde_id: number | null;
  kohde_tunnus: string | null;
  /** null = ei laskettu. EI sama kuin 0 riviä. */
  rivit: number | null;
  rajaus: unknown;
}

/** 147: mitä perehdytyksiä kohde vaatii. `vaatimus_id === null` = oletettu. */
export interface PerehdytysVaatimus {
  vaatimus_id: number | null;
  laji: string;
  alue: string | null;
  pakollinen: boolean;
  oletettu: boolean;
}

export interface Perehdytystilanne {
  henkilo_id: number;
  nimi: string;
  lyhenne: string | null;
  /** Montako allokoitua päivää tällä työmaalla (30 pv taaksepäin). */
  allokoituja_pv: number;
  ensimmainen_pv: string;
  /** 147: mikä vaatimus tämä rivi on. null = kohteelle ei ole kirjattu
   *  yhtään vaatimusta, ja tämä on oletettu rivi (`oletettu`). */
  vaatimus_id: number | null;
  laji: string;
  alue: string | null;
  oletettu: boolean;
  perehdytetty: boolean;
  perehdytys_pvm: string | null;
  /** null = voimassa koko työmaan ajan. */
  voimassa_asti: string | null;
  perehdyttaja: string | null;
}

/* ── IV-asennus (189, 2.10.2026) ── */
export type IvTulos = "hyvaksytty" | "hylatty" | "ei_kelpaa";
export interface IvJarjestelma { id: number; tunnus: string; nimi: string | null; palvelualue: string | null; luovutettu_pvm: string | null; valmius: number | null; osuuksia: number }
export interface IvOsuus {
  id: number; jarjestelma_id: number; tunnus: string; kuvaus: string | null; pinta_ala_m2: number | null; pituus_m: number | null;
  vaadittu_luokka: string | null; asennettu_pvm: string | null; p1_suojaus_pvm: string | null; valmius: number | null;
  tiiviys_tulos: IvTulos | null; tiiviys_syy: string | null; tiiviys_paiva: string | null; vuotoilmavirta_ls: number | null; raja_ls: number | null;
  koepaine_pa: number | null; tiiviys_kalibrointi: boolean | null; paatteita: number; paatteita_ok: number;
}
export interface IvPaate {
  id: number; kanavaosuus_id: number; tunnus: string; tyyppi: string | null; suunniteltu_ls: number | null; toleranssi_pros: number;
  tulos: IvTulos | null; tulos_syy: string | null; paiva: string | null; mitattu_ls: number | null; poikkeama_pros: number | null;
  saatoasento: string | null; kalibrointi_voimassa: boolean | null;
}
export interface IvPuute { id: number; tunniste: string; kohde_taulu: string; kohde_id: number; kuvaus: string; vaikutus: string; havaittu: string }
export interface IvRakenne {
  kohde: { id: number; tunnus: string; nimi: string; tyolaji: KohdeTyolaji; tila: string };
  valmius: number | null; jarjestelmat: IvJarjestelma[]; osuudet: IvOsuus[]; paatteet: IvPaate[]; puutteet: IvPuute[];
}
export interface IvMittalaite { id: number; tunnus: string; nimi: string; malli: string | null; kalibrointi_voimassa_asti: string | null; kalibrointi_voimassa: boolean }
export interface IvTiiviysluokka { luokka: string; kerroin: number; eksponentti: number; yksikko: string; lahde: string; huomio: string | null }
export interface IvLaskutusRivi { kk: string; paatteita_kirjattu: number; ilmavirtamittauksia: number; ilmavirta_hyvaksytty: number }
export interface IvMittausTulos { id: number; tulos: IvTulos; tulos_syy: string | null; raja_ls?: number | null; luokka?: string | null; poikkeama_pros?: number | null; kalibrointi_voimassa: boolean; kalibrointi_voimassa_asti: string | null }

/* ── T530 oikeuspaketit (232) ── */
export interface OikeuspakettiMaar { tunnus: string; oikeudet: string[]; rooli: string | null; ehdotettuRooli?: string; laji?: "henkilo" | "ulkopuolinen" }
/** 254 (T625 vaihe 1): kohteen portti (TG) ja sen tapahtuma. */
export interface TgPortti {
  id: number; kohde_id: number; jarjestys: number; tunnus: string; nimi: string;
  maaraaika: string; tila: "avoin" | "hyvaksytty"; hyvaksyja: string | null; hyvaksytty: string | null;
}
export interface TgTapahtuma {
  id: number; portti_id: number; laji: "luonti" | "siirto" | "hyvaksynta"; kuka: string; milloin: string;
  vanha_pvm: string | null; uusi_pvm: string | null; peruste: string | null;
}
/** 255 (T625 vaihe 2): portin tehtävä; tekemätön estää portin ja seuraavat. */
export interface TgTehtava { id: number; portti_id: number; jarjestys: number; teksti: string; tehty: string | null; tekija: string | null }
export interface TgPortit {
  kohde: { id: number; tunnus: string; nimi: string };
  portit: TgPortti[]; tapahtumat: TgTapahtuma[]; tehtavat: TgTehtava[];
  oikeudet: { luonti: boolean; hyvaksynta: boolean; maaraaika: boolean; kuittaus: boolean };
}

export interface OikeuspakettiLuettelo { paketit: OikeuspakettiMaar[]; ryhmat: Record<string, string>; ryhmajarjestys: string[]; ei_kopioida: string[] }
export interface Oikeustila {
  id: number; laji: string; roolit: string[];
  paketit: { id: number; paketti: string; kohde_idt: number[] | null; myonnetty: string; myonsi: string | null; peruste: string | null }[];
  oikeusrivit: { oikeus: string; kohde_id: number | null; myonnetty: string; myonsi: string | null; peruste: string | null; paketit: string[]; yksittain: boolean }[];
}
